// Interfaz V2 — pantalla de exploración del nuevo flujo de generación.
//
// Esta pantalla es un prototipo visual con data mockeada (ver `mockData.ts`).
// En el sidebar se muestra para la experiencia V2 (ver lib/userGroups):
// `company_profiles.grupo_testing = 'B'` (etiquetada "Estudio virtual") y dev
// testers (etiquetada "Interfaz V2").
//
// Flujo de usuario:
//   1. Browsing — saludo personalizado + barra de prompt + grilla de
//      colecciones de presets (cada una scrolleable horizontal con
//      "Ver más" para abrir la colección completa).
//   2. Preset detail — al tocar un preset se abre un sheet de pantalla
//      completa con la imagen del preset en grande y un container que
//      pide subir/elegir un producto.
//   3. Configure — después de elegir producto, ese container muta para
//      mostrar opciones de Pose, Formato y un campo libre, más un
//      botón final de Generar.
//
// La pantalla NO genera nada todavía: el botón Generar solo cierra el
// sheet y vuelve a browsing. Cuando se valide la dirección, se cablea
// con el agente / webhook de generación real.
//
// Animaciones:
//   • Prompt bar: focus expande el container a un panel de prompt con
//     textarea + "sorprendeme" + Generar. Salida con fade-out cuando se
//     hace blur sin escribir nada.
//   • Preset detail: sheet con backdrop fade + slide-up de 300ms.
//   • Configure swap: el contenido del config panel (subir producto vs.
//     configurar generación) usa `key` + `animate-mode-swap` para
//     transicionar suave entre estados.

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useHomeBanners } from '../../hooks/useHomeBanners';
import { HomeBannersView } from '../../components/HomeBanners';
import { CreditsTopBar } from '../../components/CreditsTopBar';
import type { MotivoPricing } from '../../components/pricing/pricingContexts';
import { PRODUCTS_CACHE_KEY } from './productsCache';
import { estadoDeCreditos } from '../../lib/creditsStatus';
import {
  HomeMinimal,
  SeccionesHome,
  nombreCortoModo,
  useCuentaShell,
} from '../estilos/EstiloMinimal';
import { useRotacionPlantillas } from '../../hooks/useRotacionPlantillas';

/**
 * Descarte de la barra superior de "sin créditos"
 * (ADR-2026-07-30-barra-superior-creditos-home-ancho), corregido por
 * ADR-2026-08-04-el-cartel-de-creditos-vuelve-al-recargar.
 *
 * Vive en el MÓDULO y no en `sessionStorage`. La diferencia se ve al recargar: con
 * `sessionStorage` el cartel no volvía en toda la pestaña —una X y el aviso se perdía
 * para siempre, aunque el saldo siguiera en cero—, mientras que en desktop el shell lo
 * descarta sólo en memoria y vuelve en cada recarga. Era la misma decisión resuelta de
 * dos maneras según el dispositivo.
 *
 * Tampoco puede ser `useState` a secas: en mobile esta pantalla se desmonta al navegar,
 * así que el cartel volvería en cada vuelta al inicio. En el módulo sobrevive a la
 * navegación —como el estado del shell en desktop— y se va con la recarga.
 */
let topBarDescartadaEnEstaCarga = false;
import {
  Sparkles,
  ArrowUp,
  Wand2,
  Plus,
  Mic,
  Play,
  X,
  AlertCircle,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { getAppScrollY, setAppScrollY } from '../../utils/appScroll';
import { useSidebar } from '../../context/SidebarContext';
import {
  GENERATE_AUTH_TOKEN,
  GENERATE_AD_V3_URL,
  GENERATE_VIDEO_INFLUENCER_URL,
  USE_ATLAS_BACKEND,
} from '../../config';
import {
  postGenerateAd,
  GenerateAdError,
  shouldRouteToAtlas,
  mapErrorTypeToMessage,
  type GenerateAdErrorType,
} from '../../lib/generateAdRequest';
import {
  fetchSuggestedVideoPrompt,
  generateVideoFromImage,
  generateVideoFromTemplate,
} from '../../lib/videoFromImage';
import { atlasBackendFetch } from '../../lib/atlasBackend';
import LoadingAnimation from '../../components/LoadingAnimation';
import {
  preloadGeneratedImages,
  type PreloadedImage,
} from '../../utils/preloadGeneratedImages';
import {
  type CollectionV2,
  type MockProductV2,
  type PresetV2,
} from './mockData';
import { fetchTemplatesV2 } from './templatesApi';
import { fetchUserProducts } from './productsApi';
import { uploadNewProduct } from './productUploadApi';
import { recordPromptHistory } from './promptHistory';
import {
  ProductsContext,
  ProductsLoadedContext,
  ProductEditContext,
} from './productsContext';
import { ModelsContext } from './modelsContext';
import { AgentEnvContext } from './agentEnvContext';
import { fetchAllPoses, type PoseV2 } from './posesApi';
import {
  fetchMyCustomPoses,
  uploadCustomPose,
  deleteCustomPose,
} from './posesCustomApi';
import {
  type VistaCatalogo,
  buildVistasSoloProductoPresets,
  vistasSoloProductoParaConjunto,
  VISTA_PRESET_PREFIX,
} from './vistasCatalogo';
import {
  fetchBibliotecaFondos,
  fetchFondosGuardados,
  type FondoBiblioteca,
  type FondoGuardado,
} from './fondosApi';
import { PosePickerSheet } from './PosePicker';
import {
  FondoPickerSheet,
  ILUMINACION_BLANCO_DEFAULT,
  TEMPERATURA_BLANCO_DEFAULT,
  type FondoSelection,
  type FondoPickerView,
} from './FondoPicker';
import { COLORES_FONDO_LISO } from './components/FondoProductoPicker';
import { useV2Chat } from './useV2Chat';
import { ChatPanelV2 } from './ChatPanelV2';
import SubscriptionLimitModal from '../../components/SubscriptionLimitModal';
import { FeatureLockedModal } from '../../components/FeatureLockedModal';
import { VideoPlanRestrictionModal } from '../../components/VideoPlanRestrictionModal';
import {
  getModePlanLock,
  getPlanRequirementNote,
  isProOrUltra,
  type ModePlanLock,
} from '../../lib/planCapabilities';
import { getModeExamples, type ModeExample } from './modeExamples';
import { ModeExamplesModal } from './components/ModeExamplesModal';
import {
  ModelPickerSheet,
  type ModelSelection,
  type ModelPickerView,
} from './ModelPicker';
import { FormatPicker } from './FormatPicker';
import { fetchUserModels, type ModelV2 } from './modelsApi';
import { AsistenteMarketingSheet } from './modes/AsistenteMode';
import {
  VideoConfigSheet,
  VIDEO_INFLUENCER_DURATION_MIN,
  VIDEO_INFLUENCER_DURATION_MAX,
  VIDEO_INFLUENCER_DURATION_MAX_SIN_GANCHO,
  VIDEO_INFLUENCER_DURATION_DEFAULT,
  VIDEO_DURATION_DEFAULT,
} from './components/VideoConfigSheet';
import { isSinGancho } from './ganchosCatalog';
import { rubroVePlantillas } from '../../lib/rubro';
import { resolveDesignModeloCount } from './designModeloCount';
import {
  RESOLUCION_DEFAULT,
  type ResolucionImagen,
} from '../../lib/resolucionImagen';
import { ajustarPosesPorCantidadDeModelos } from './posesCompatibilidad';
import { fetchInfluencerDialogo } from './dialogoApi';
import {
  MODES,
  VIDEO_INFLUENCER_ENABLED,
  anuncioTercero,
  MODE_KIND_TO_PATH,
  kindDeModoParaRubro,
  kindsHomeGratis,
} from './modesCatalog';
import { useRubroMarcaActiva } from '../../hooks/useRubroMarca';
import { esRubroHogarDeco } from '../../lib/rubro';
import { medidasParaPayload, type Medidas } from '../../lib/medidas';
import { pedirNuevosAngulos } from '../../lib/carruselRequest';
import {
  VISTA_MUEBLE_CONTEXTO,
  VISTA_MUEBLE_INFOGRAFIA,
  VISTAS_MUEBLES,
  backgroundParaVistaMueble,
} from './vistasMuebles';
import {
  pollGeneratedByExecutionId,
  ANUNCIO_POLL_TIMEOUT_MS,
  ANUNCIO_STABLE_ROUNDS,
} from './pollGeneratedByExecutionId';
import {
  awaitImageGeneration,
  markPendingGeneration,
  clearPendingGeneration,
  readPendingGeneration,
} from './imageGenerationRecovery';
import { MultiSlotProductPickerSheet } from './components/MultiSlotProductPickerSheet';
import { AgregarVariosModal } from '../../components/AgregarVariosModal';
import { type CatalogoCard } from './components/CatalogoCardsFullscreen';
import { PresetDetailSheet } from './components/PresetDetailSheet';
import {
  getVoiceGuionDefault,
  getGuionDurationSec,
  sanitizeEstiloVoz,
  saveLastUsedVoice,
  type VoiceGuionValue,
} from './components/VoiceGuionSection';
/* `HomeTemplatesSection` (chips + masonry) salió de la home con
   ADR-2026-07-31-inicio-se-funde-interfaz-v2: el diseño nuevo muestra las plantillas
   como una tarjeta por colección. El componente sigue existiendo y es el que sirve
   `/interfaz-v2/plantillas`, que es a donde lleva el "Ver todo". */
import AddToHomescreenPrompt from '../../components/AddToHomescreenPrompt';
import {
  HomeSkeleton,
  MobileModeHero,
} from './components/HomeCards';
/* La barra inferior (`NavInferiorMobile`) y el sheet de "Generar" ya no se importan
   acá: los monta el shell (ADR-2026-08-04-la-barra-inferior-vive-en-el-shell). */
import { loadLastCrearAnuncioState } from '../../utils/crearAnuncioStorage';
import {
  normalizeBrandHexColors,
  resolveBrandFont,
} from './panels/AnuncioPanel';

/* `AR_TZ` + `getGreeting()` (la franja horaria → "Buenos días" / "Buenas tardes" /
   "Buenas noches") vivían acá y se fueron con ADR-2026-07-31-inicio-se-funde-interfaz-v2:
   el saludo lo arma `HomeMinimal`, que usa `saludo()` de `contenidoAtlas` — la misma
   lógica, con la misma zona horaria, ya compartida con el resto del shell. Eran dos
   copias del mismo cálculo. */

// Cuántos presets se muestran por colección. En desktop entran más
// thumbnails porque hay más espacio horizontal. Si una colección tiene
// menos o igual que el count del device, NO se muestra "Ver más"
// (renderizamos todos los presets y no tiene sentido el link).
const PRESETS_PREVIEW_COUNT_MOBILE = 6;
const PRESETS_PREVIEW_COUNT_DESKTOP = 10;

// Key del sessionStorage donde guardamos scroll position al navegar
// desde la home V2 a una sub-ruta (categoria, plantillas, etc.). Al
// volver, restauramos antes del paint para evitar el "salto" del top
// al punto guardado.
const HOME_SCROLL_KEY = 'atlas-v2-home-scroll';
// Cache de las collections fetcheadas — sobrevive a mounts/unmounts
// dentro de la sesión. Sin esto, al volver desde una categoría el
// home arranca con la grilla vacía (mientras el fetch corre) y el
// scroll restore no puede aplicarse porque el documento es muy corto.
const HOME_COLLECTIONS_CACHE_KEY = 'atlas-v2-home-collections-cache';
// Cache de poses para la fila virtual "E-commerce". Sin esto, al
// volver desde una sub-ruta, las plantillas de DB cargan desde el
// cache instantáneo pero E-commerce aparece con delay (porque
// `allPoses` es otro fetch async). Caché las dos para que toda la
// home se renderice de una.
const HOME_POSES_CACHE_KEY = 'atlas-v2-home-poses-cache';
// Cache de `isDesignRubro` — sino, aunque tengamos `allPoses`
// cacheadas, la condicional de la fila E-commerce evalúa false en el
// primer paint hasta que el fetch de user info resuelve.
const HOME_DESIGN_RUBRO_CACHE_KEY = 'atlas-v2-home-design-rubro';
// Cache del catálogo de productos del user. Sin esto, al abrir un modo el
// CTA "Elegir de mis productos" y sus thumbnails aparecían recién cuando
// resolvía `fetchUserProducts` (cadena de user-info + firmado de URLs). Con
// el cache, el primer paint ya muestra los productos; igual disparamos el
// fetch en cada mount para refrescar las signed URLs (TTL 1h), así si el
// cache quedó viejo las URLs se reemplazan a los pocos segundos.
// La llave vive en `productsCache` desde ADR-2026-08-04-la-marca-nueva-arranca-vacia:
// el alta de marca también la necesita para borrar el catálogo de la marca anterior.

// El cache de productos puede tener dos formatos en sessionStorage: el viejo
// (array plano de productos) o el nuevo envelope `{ brandId, products }`. El
// envelope guarda la marca a la que pertenece el catálogo cacheado: como ahora
// V2 filtra por marca activa, necesitamos saber de qué marca era el cache para
// no pintar productos de OTRA marca en el primer paint. Normaliza ambos.
const readProductsCache = (): {
  products: MockProductV2[];
  brandId: string | null;
} => {
  try {
    const raw = sessionStorage.getItem(PRODUCTS_CACHE_KEY);
    if (!raw) return { products: [], brandId: null };
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return { products: parsed as MockProductV2[], brandId: null };
    }
    if (parsed && Array.isArray(parsed.products)) {
      return {
        products: parsed.products as MockProductV2[],
        brandId: (parsed.brandId as string) ?? null,
      };
    }
  } catch {
    /* noop */
  }
  return { products: [], brandId: null };
};

// Mapeo bidireccional entre el slug de URL y el `kind` del preset sintético.
// Cada uno de estos 4 modos tiene su propia URL (`/interfaz-v2/modo/<slug>`)
// para que el browser back/forward funcione por modo y los deeplinks sean
// compartibles. Otros kinds (`template`, `design` de E-commerce) NO usan
// URL — abren el sheet vía state interno como antes.
const MODE_PATH_TO_KIND: Record<
  string,
  | 'design-auto'
  | 'design-modelo'
  | 'fotos-producto'
  | 'catalogo-ia'
  | 'catalogo-muebles'
  | 'infografia'
  | 'asistente'
  | 'anuncio'
  | 'video-influencer'
> = {
  auto: 'design-auto',
  modelo: 'design-modelo',
  'fotos-producto': 'fotos-producto',
  catalogo: 'catalogo-ia',
  'catalogo-muebles': 'catalogo-muebles',
  infografia: 'infografia',
  asistente: 'asistente',
  anuncio: 'anuncio',
  'video-influencer': 'video-influencer',
};
/**
 * Los dos modos de catálogo (`catalogo-ia` y `catalogo-muebles`) comparten el
 * MISMO flujo: subida masiva de productos, paso intermedio de "elegir vistas" y
 * review de cards en vez del ConfigurePanel. Lo único que cambia es el catálogo
 * de vistas y, en muebles, la pantalla de medidas.
 *
 * Este helper existe para no repetir el `=== 'catalogo-ia' || === 'catalogo-muebles'`
 * en los ~6 puntos del archivo donde se decide flujo. Ver ADR-582.
 */
const esModoCatalogo = (kind?: string | null): boolean =>
  kind === 'catalogo-ia' ||
  kind === 'catalogo-muebles' ||
  kind === 'infografia';

/* `MODE_KIND_TO_PATH` estaba acá, escrito a mano. Pasó a `modesCatalog`, derivado de
   `MODES` (ADR-2026-08-04-nombres-de-modo-iguales-en-mobile): era idéntico, y dos
   listas iguales sólo esperan a que alguien agregue un modo en una y no en la otra. */

/**
 * Nombre del paso para el último nivel del breadcrumb de las pantallas de modo
 * (ADR-2026-08-03-breadcrumbs-pantallas-de-modo).
 *
 * `pick-product` y `choose-product` comparten etiqueta a propósito: para el usuario
 * son el mismo momento —elegir con qué producto genera—, y la diferencia (venir de
 * la bienvenida del modo o de la grilla de productos) es de implementación.
 */
const ETIQUETA_PASO: Record<string, string> = {
  preview: 'Vista previa',
  'choose-product': 'Elegir producto',
  'pick-product': 'Elegir producto',
  'upload-product': 'Subir producto',
  'select-vistas': 'Elegir vistas',
  configure: 'Configurar',
  asistente: 'Asistente',
};

// Modos con pantalla PROPIA fuera de InterfazV2 (declaran `route` en el catálogo,
// ej. Animar imagen y Shorts Studio): la home navega ahí en vez de abrir un sheet.
// Se deriva de MODES para no mantener una tercera lista.
const MODE_KIND_TO_ROUTE: Record<string, string> = Object.fromEntries(
  MODES.filter((m) => m.route).map((m) => [
    m.preset.kind as string,
    m.route as string,
  ]),
);

// Pose default cuando el producto no tiene `modelView` (no se puede
// recomendar por área): preferimos esta pose de cuerpo completo de pie en
// vez de la primera por `created_at` ("Contra-picado dramático"). Match por
// nombre (lowercase) contra la tabla `poses`.
const DEFAULT_FULLBODY_POSE_NAME = 'editorial de pie';

// Modo "Video con influencer" (UGC): tope del poll por `execution_id` y cada
// cuánto se reintenta. Si el video no aparece en la DB dentro de este tope, se
// corta y se muestra error. SOLO aplica a este modo.
// Tope del poll: hubo generaciones reales de MÁS de 15 min con el backend
// todavía trabajando (el corte anterior tiraba error prematuro, ADR-543).
// 30 min da margen holgado; si el backend no escribió la fila para entonces,
// el mensaje aclara que puede seguir generándose y aparecer en el historial.
const VIDEO_INFLUENCER_POLL_TIMEOUT_MS = 30 * 60 * 1000; // 30 min
const VIDEO_INFLUENCER_POLL_INTERVAL_MS = 5000; // 5 s
// Duración VISUAL de la barra de la pantalla de carga: atada al tope del
// poll (un solo knob). Es solo estética: la barra capea en 97% y recién
// completa cuando el poll encuentra el video (`completeNow`).
const VIDEO_INFLUENCER_LOADING_BAR_MS = VIDEO_INFLUENCER_POLL_TIMEOUT_MS;

export const InterfazV2: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode: routeMode } = useParams<{ mode?: string }>();

  // ── Rutas internas — ADR-2026-07-31-inicio-se-funde-interfaz-v2 ──
  //
  // Acá había una traducción de rutas: esta pantalla servía `/interfaz-v2/modo/:mode`
  // bajo `DashboardLayout` y un clon `/modo/:mode` hospedado en el shell nuevo, y
  // cada navegación interna tenía que elegir el par correcto para no expulsar al
  // usuario del shell donde estaba (ADR-2026-07-31-8-herramientas-sidebar-hospedan-shell).
  //
  // Ya no hay dos familias: el shell nuevo se aplicó SOBRE estas mismas rutas
  // (ADR-2026-07-31-shell-nuevo-urls-viejas) y los clones quedaron como alias que
  // redirigen acá. Navegar a un alias sólo agregaría un salto, así que las rutas son
  // siempre las canónicas.
  //
  // `rutaHome` es `/interfaz-v2`: ESTA misma pantalla sin modo abierto, que es contra
  // la que está pensada la animación de cierre del sheet.
  const rutaHome = '/interfaz-v2';
  const rutaModo = (p: string) => `/interfaz-v2/modo/${p}`;
  /**
   * "Ver más" de una fila de plantillas — ADR-2026-08-03-ver-mas-de-una-fila-filtra-plantillas.
   *
   * Antes iba a `/interfaz-v2/coleccion/:id` (`ColeccionDetalle`), una pantalla
   * distinta que muestra UNA colección y de la que sólo se sale por "Volver". Ahora
   * lleva a la misma `/interfaz-v2/plantillas` con su chip ya activo: se ve lo mismo
   * y además el resto de las categorías queda a un toque.
   *
   * La categoría va en la URL —y no en un `state` de navegación— porque es la misma
   * llave que restaura el filtro al volver de un preset: el back del browser
   * reconstruye la URL, no el state.
   */
  const rutaPlantillasCategoria = (id: string) =>
    `/interfaz-v2/plantillas?categoria=${encodeURIComponent(id)}`;
  // Resultados de la generación — destino de ~9 handlers de fin de generación.
  const rutaResultados = '/redes-sociales/imagenes-generadas';

  // ── Chrome que el shell nuevo ya pinta (ADR-2026-07-31-shell-provee-flag-hospedaje) ──
  // En desktop esta pantalla se monta dentro de `EstiloMinimal`, que ya trae la barra
  // de créditos y el panel CONTEXTO DEV. En mobile la monta `DashboardLayout`, que no.
  // Sin esta pregunta, un usuario sin créditos en desktop veía DOS barras iguales.
  const { dentroDelShell } = useCuentaShell();

  // Banners de home (prueba gratis / describir la marca) — mismas condiciones
  // que la pantalla clásica. Se suprime el de marca al volver de describir la
  // marca (`hideBrandBanner` en el state de navegación).
  const homeBanners = useHomeBanners({
    suppressBrand: (location.state as any)?.hideBrandBanner === true,
  });
  // Barra superior de "sin créditos" (ADR-2026-07-30-barra-superior-creditos-home-ancho).
  // El descarte dura lo que la carga de la página y vuelve al recargar, igual que en
  // desktop — ver `topBarDescartadaEnEstaCarga` arriba.
  const [topBarDescartada, setTopBarDescartada] = useState(
    topBarDescartadaEnEstaCarga,
  );
  const descartarTopBar = () => {
    topBarDescartadaEnEstaCarga = true;
    setTopBarDescartada(true);
  };
  const {
    setMobileBackOverride,
    setMobileTitleOverride,
    setMobileHeaderTransparent,
    setMobileHeaderCenter,
    setMobileHideBack,
    setMobileHideBottomNav,
    sidebarWidth,
  } = useSidebar();

  // La barra inferior y el sheet de "Generar" los monta el SHELL
  // (ADR-2026-08-04-la-barra-inferior-vive-en-el-shell), no esta pantalla: son
  // navegación de la app y se ven en todas sus pantallas, no sólo en el inicio. Lo
  // que sí decide esta pantalla es cuándo se aparta — ver el efecto de
  // `mobileHideBottomNav` más abajo, cuando ya existen `selectedPreset` y `panelStep`.
  // Toggle imagen/video del header en preview. Cuando está en 'video' y el
  // preset es 'template', el ConfigurePanel muestra el botón "Configurar
  // video", el costo suma la animación, y `handleGenerateFinal` encadena
  // imagen → prompt → video después de generar.
  //
  // Arranca en Video cuando se entró a Plantillas desde la familia Videos del sheet de
  // Generar (ADR-2026-08-04-plantillas-es-una-opcion-de-generar): el catálogo pasa
  // `mediaInicial` en el state de navegación. Se lee del `history.state` y no de
  // `useLocation` por lo mismo que `initialNavPreset`: tiene que estar resuelto en el
  // PRIMER render, si no el toggle se vería saltar de Imagen a Video.
  const [previewMediaTab, setPreviewMediaTab] = useState<'imagen' | 'video'>(() => {
    try {
      return (window.history.state?.usr as { mediaInicial?: 'imagen' | 'video' })
        ?.mediaInicial === 'video'
        ? 'video'
        : 'imagen';
    } catch {
      return 'imagen';
    }
  });
  // Configuración de video para el flow de plantillas (kind 'template').
  // - `videoDuration`: segundos por escena (default 8 — mismo que animar
  //   imagen en el historial).
  // - `videoMultiScene`: si true, genera multi-shot (Kling vía atlas
  //   /v1/videos/from-image, ADR-582) y duplica el costo en créditos.
  const [videoDuration, setVideoDuration] = useState<number>(8);
  // Gancho elegido en el modo "Video con influencer" (id de `video_hooks`). Se
  // sube desde el `GanchoSection` (vía PresetDetailSheet → ConfigurePanel) para
  // poder mandarlo como `gancho_id` en el payload del webhook. Ver ADR-504.
  const [videoGanchoId, setVideoGanchoId] = useState<string | null>(null);
  // Cambio de gancho: al elegir "Sin gancho" la barra de Duración topa en 14s
  // (ADR-671) porque sin el clip de apertura el total del video ES el
  // principal, así que si venía más arriba la bajamos. Al volver a elegir un
  // gancho NO la subimos sola: sería cobrarle más sin que lo haya pedido.
  const handleGanchoIdChange = (id: string | null) => {
    setVideoGanchoId(id);
    if (isSinGancho(id)) {
      setVideoDuration((d) =>
        Math.min(d, VIDEO_INFLUENCER_DURATION_MAX_SIN_GANCHO),
      );
    }
  };
  // Voz + velocidad + guion del modo "Video con influencer" (sección "Voz y
  // guion" del ConfigurePanel). Agrupado en un solo objeto para no engordar la
  // cadena de props (ADR-525/527). Se mapea al payload como `voz_id`,
  // `velocidad_voz`, `dialogo_input_mode`, `dialogo` / `audio_url`.
  const [videoVoiceGuion, setVideoVoiceGuion] = useState<VoiceGuionValue>(
    // Preselecciona la última voz usada (localStorage) — la voz es
    // obligatoria en este modo (ADR-544).
    () => getVoiceGuionDefault(),
  );
  const [videoMultiScene, setVideoMultiScene] = useState<boolean>(false);
  const [showVideoConfigSheet, setShowVideoConfigSheet] = useState<boolean>(
    false,
  );
  // Costo por video = duración (segundos) × multiplicador. Espeja la
  // fórmula de GeneratedImagesHistory: single-scene 1×, multi-scene 2×.
  const videoCreditCostPerImage = videoMultiScene
    ? videoDuration * 2
    : videoDuration;

  // Scroll save/restore para la home V2.
  //
  // Save: lo llamamos antes de cada navegación a sub-rutas. Guarda
  // `window.scrollY` en sessionStorage.
  //
  // Restore: el contenido de la home se llena en fases async
  // (collections cache → user info + design-row → allPoses + e-commerce),
  // así que el documento crece después del primer paint. Para evitar
  // mostrar contenido intermedio o saltos:
  //   1) Inicializamos `isRestoringHomeScroll = true` si hay scroll
  //      guardado.
  //   2) Mientras es true, renderizamos un SKELETON en vez del home
  //      real — el user ve "loading" en lugar de un home parcial.
  //   3) En cada render, un useLayoutEffect chequea si el documento
  //      es suficientemente alto para alcanzar la posición. Cuando sí,
  //      aplica scrollTo y baja el flag → swap a contenido real, ya
  //      scrolleado a la posición correcta. Sin flash, sin saltos.
  //   4) Safety timeout: 1.5s para no quedar atrapado en el skeleton si
  //      las async deps no resuelven.
  const saveHomeScroll = useCallback(() => {
    try {
      sessionStorage.setItem(HOME_SCROLL_KEY, String(window.scrollY));
    } catch {
      /* noop */
    }
  }, []);
  const pendingScrollRef = useRef<number | null>(null);
  const [isRestoringHomeScroll, setIsRestoringHomeScroll] = useState<boolean>(
    () => {
      try {
        return !!sessionStorage.getItem(HOME_SCROLL_KEY);
      } catch {
        return false;
      }
    },
  );
  useLayoutEffect(() => {
    if (!isRestoringHomeScroll) return;
    try {
      const sy = sessionStorage.getItem(HOME_SCROLL_KEY);
      if (sy) {
        pendingScrollRef.current = Number(sy);
        sessionStorage.removeItem(HOME_SCROLL_KEY);
      } else {
        setIsRestoringHomeScroll(false);
      }
    } catch {
      setIsRestoringHomeScroll(false);
    }
    // Safety timeout — si los datos async no llegan, levantamos el
    // skeleton igual para no dejar al user mirando placeholders.
    const t = window.setTimeout(() => {
      setIsRestoringHomeScroll(false);
      pendingScrollRef.current = null;
    }, 1500);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useLayoutEffect(() => {
    if (!isRestoringHomeScroll) return;
    const y = pendingScrollRef.current;
    if (y == null) {
      setIsRestoringHomeScroll(false);
      return;
    }
    const maxY = document.documentElement.scrollHeight - window.innerHeight;
    if (maxY >= y) {
      window.scrollTo({ top: y });
      document.documentElement.scrollTo({ top: y });
      pendingScrollRef.current = null;
      setIsRestoringHomeScroll(false);
    }
    // Si el documento todavía no es suficientemente alto, NO scrolleamos
    // a nada — esperamos al próximo render. El skeleton sigue tapando
    // el contenido intermedio.
  });

  // ── User greeting + rubro ─────────────────────────────────────────────
  const [shortName, setShortName] = useState<string>('');
  // Rubros eligibles para la fila "Diseña tu imagen" (poses como cards).
  // Si el user no es de uno de estos, esa fila no aparece.
  const DESIGN_RUBROS = new Set([
    'Moda e Indumentaria',
    'Belleza y Cuidado Personal',
  ]);
  // Modos "Diseña tu imagen": visibles para TODOS los rubros. Antes se
  // gateaba a moda/belleza (DESIGN_RUBROS) y cualquier otro rubro veía la
  // home vacía (ej. "agencia de marketing"). Se fuerza a `true` para que la
  // fila de modos aparezca siempre. El resto de la lógica de rubro (cache,
  // fetch de templates) queda intacta.
  const [isDesignRubro, setIsDesignRubro] = useState<boolean>(true);
  // Rubro de la MARCA activa (no el de la empresa): gatea los modos de muebles
  // (catálogo de muebles + infografías) — ADR-2026-07-31-modos-muebles-por-rubro-de-marca.
  // Mientras no se conoce (`null`) las cards no salen; el cache del hook evita
  // que aparezcan un frame tarde para quien sí califica.
  const { rubro: rubroMarcaActiva } = useRubroMarcaActiva();
  const esMarcaHogarDeco = esRubroHogarDeco(rubroMarcaActiva);
  // Qué modos entran en el hero de la home de mobile durante la prueba gratis. La
  // decisión vive en `modesCatalog` y no acá — ver `kindsHomeGratis`.
  const kindsGratisHome = React.useMemo(
    () => kindsHomeGratis(esMarcaHogarDeco),
    [esMarcaHogarDeco],
  );
  // Nombre de la empresa — se usa en el copy del flujo "sorpréndeme" para
  // que el agente se refiera a la marca por su nombre. Si no está, el
  // texto cae a una versión genérica.
  const [companyName, setCompanyName] = useState<string>('');
  // Rubro + id_company del user — necesarios para el fetch de templates.
  // Loaded = true significa que ya intentamos cargarlos (incluso si están
  // vacíos), para evitar que el effect de templates corra antes de tiempo.
  const [userRubro, setUserRubro] = useState<string | null>(null);
  const [userCompanyId, setUserCompanyId] = useState<string | null>(null);
  // Plan del usuario (gratis / emprendimiento / pro / negocio / enterprise…)
  // — para el gating de funciones por plan al generar.
  // Seed desde el cache de localStorage (mismo que llena el Sidebar) para tener
  // el plan al instante en el primer render → el gating de modos funciona desde
  // el vamos (taps + chokepoint de URL directa), sin esperar el fetch async.
  const [userPlanReal, setUserPlanReal] = useState<string | null>(() => {
    try {
      return localStorage.getItem('atlas-user-plan');
    } catch {
      return null;
    }
  });
  /**
   * El plan con el que decide esta pantalla: el simulado por el panel CONTEXTO DEV
   * si hay uno, y si no el real — ADR-2026-08-03-el-plan-simulado-tambien-gatea.
   *
   * SUPERSEDIDO: el panel CONTEXTO DEV y sus overrides se eliminaron al mergear
   * `principal` (ADR-2026-08-05-fuera-los-overrides-de-dev). Queda el plan real y
   * nada más. El alias se conserva porque lo leen ~40 lugares de esta pantalla y
   * renombrarlos no cambiaría nada.
   */
  const userPlan = userPlanReal;
  // Modal de créditos agotados (reusa el de toda la app).
  const [showSubscriptionLimitModal, setShowSubscriptionLimitModal] =
    useState<boolean>(false);
  const [subscriptionLimitType, setSubscriptionLimitType] = useState<
    'free-trial-ended' | 'plan-limit-reached'
  >('free-trial-ended');
  // Copy del catálogo según el saldo (ADR-2026-07-31-copy-catalogo-sigue-saldo-real). Los caminos que chocan contra la
  // falta de créditos lo dejan en `sin`; sólo el CTA de la barra lo pisa, porque
  // puede abrirse con saldo todavía disponible.
  const [motivoPricing, setMotivoPricing] = useState<MotivoPricing>('sin');
  // Modal de "función exclusiva" (gating por plan). null = cerrado.
  const [featureLock, setFeatureLock] = useState<{
    title: string;
    message: string;
  } | null>(null);
  // Visor de ejemplos para modos BLOQUEADOS por plan: al tocar la card con
  // candado se abre esto (ejemplos + nota del plan + CTA "Mejorar plan") en
  // vez del FeatureLockedModal — mismo comportamiento que la pantalla Modos.
  const [lockedModeExamples, setLockedModeExamples] = useState<{
    title: string;
    description?: string;
    examples: ModeExample[];
    planNote: string;
  } | null>(null);
  // Devuelve true si abrió el visor (el modo tiene ejemplos cargados); si no,
  // el caller cae al FeatureLockedModal clásico.
  const openLockedModeExamples = (
    preset: PresetV2,
    lock: ModePlanLock,
  ): boolean => {
    const examples = getModeExamples(preset.kind);
    if (examples.length === 0) return false;
    setLockedModeExamples({
      title: preset.title,
      description: preset.description,
      examples,
      planNote: getPlanRequirementNote(lock.feature),
    });
    return true;
  };
  // Modal de "función exclusiva" para video (mismo que el flujo de video).
  const [showVideoPlanRestriction, setShowVideoPlanRestriction] =
    useState<boolean>(false);
  /* Acá vivía `showContactQR`, el modal con QR del botón flotante "Contactanos".
     Se fue con el botón (ADR-2026-08-03-fuera-boton-flotante-contactanos): era su
     único disparador. */
  const [userInfoLoaded, setUserInfoLoaded] = useState<boolean>(false);
  // ── Cursor "hand pointer" en los elementos clickeables de esta pantalla ──
  // Mientras InterfazV2 está montada, marcamos <body> con `v2-cursor-scope`.
  // El scope vive en <body> (y no en el contenedor de la página) a propósito:
  // los pickers/sheets/modales se montan con React portals a `document.body`,
  // así que sólo scopeando desde <body> el selector alcanza también ese
  // contenido portaleado. La regla CSS está en src/index.css (ver ADR-575).
  useEffect(() => {
    document.body.classList.add('v2-cursor-scope');
    return () => {
      document.body.classList.remove('v2-cursor-scope');
    };
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile } = await supabase
          .from('profiles')
          .select('name_short, id_company')
          .eq('id', user.id)
          .single();
        if (profile && (profile as any).name_short) {
          setShortName((profile as any).name_short as string);
        }
        // Rubro + nombre de empresa viven en company_profiles. Una sola query.
        const idCompany = (profile as any)?.id_company as string | undefined;
        if (idCompany) {
          setUserCompanyId(idCompany);
          const { data: company } = await supabase
            .from('company_profiles')
            .select('rubro, nombre, plan')
            .eq('id_company', idCompany)
            .single();
          const planVal = (company as any)?.plan as string | undefined;
          if (planVal) setUserPlanReal(planVal);
          const rubro = (company as any)?.rubro as string | undefined;
          if (rubro) {
            setUserRubro(rubro);
            const designRubro = DESIGN_RUBROS.has(rubro);
            if (designRubro) setIsDesignRubro(true);
            try {
              sessionStorage.setItem(
                HOME_DESIGN_RUBRO_CACHE_KEY,
                designRubro ? '1' : '0',
              );
            } catch {
              /* noop */
            }
          }
          const nombre = (company as any)?.nombre as string | undefined;
          if (nombre) setCompanyName(nombre);
        }
      } catch {
        // silent — sin nombre el greeting muestra solo "Buenos días."
      } finally {
        setUserInfoLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Productos reales del user ───────────────────────────────────────
  // Antes los pickers usaban `MOCK_USER_PRODUCTS`. Ahora fetcheamos
  // variantes reales una vez que sabemos el companyId del user. El array
  // se sirve a los descendientes vía `ProductsContext`.
  // Initializer lee el cache de sessionStorage: si el user ya abrió V2 en
  // esta sesión, arrancamos con el catálogo lleno en el primer paint (CTA +
  // thumbnails sin esperar al fetch).
  const [userProducts, setUserProducts] = useState<MockProductV2[]>(
    () => readProductsCache().products,
  );
  // Marca a la que pertenecen los productos cacheados (para detectar si el
  // primer paint pintó productos de otra marca y limpiarlos antes del fetch).
  const cachedBrandRef = useRef<string | null>(readProductsCache().brandId);
  // Espejo de `currentBrandId` accesible desde `persistUserProducts` (que es un
  // useCallback estable) sin recrearlo en cada cambio de marca.
  const currentBrandIdRef = useRef<string | null>(null);
  // `true` una vez que `fetchUserProducts` resolvió (aunque sea vacío). La
  // UI usa esto para mostrar el CTA "Elegir de mis productos" de forma
  // optimista durante la carga. Ver [[ProductsLoadedContext]]. Si hay cache,
  // arrancamos en `true` así el CTA aparece desde el primer paint.
  const [userProductsLoaded, setUserProductsLoaded] = useState<boolean>(() => {
    try {
      return !!sessionStorage.getItem(PRODUCTS_CACHE_KEY);
    } catch {
      return false;
    }
  });
  // Setter único de productos: actualiza el state, marca `loaded` y refresca
  // el cache de sessionStorage. Lo usan tanto el fetch inicial como los
  // refetch post-upload, así el cache nunca queda viejo (p.ej. sin el
  // producto recién subido).
  const persistUserProducts = useCallback((products: MockProductV2[]) => {
    setUserProducts(products);
    setUserProductsLoaded(true);
    cachedBrandRef.current = currentBrandIdRef.current;
    try {
      // Envelope `{ brandId, products }`: guardamos a qué marca pertenece el
      // catálogo para que el primer paint de la próxima visita no muestre
      // productos de otra marca.
      sessionStorage.setItem(
        PRODUCTS_CACHE_KEY,
        JSON.stringify({ brandId: currentBrandIdRef.current, products }),
      );
    } catch {
      /* sessionStorage puede fallar en modo privado — non-fatal */
    }
  }, []);

  // Refresca el catálogo de productos (re-fetch + persist). Lo usan los pickers
  // tras editar / borrar un producto desde la modal "Editar producto".
  const refreshUserProducts = useCallback(async () => {
    if (!userCompanyId) return;
    const refreshed = await fetchUserProducts(
      userCompanyId,
      currentBrandIdRef.current,
    );
    persistUserProducts(refreshed);
  }, [userCompanyId, persistUserProducts]);

  // Value estable para ProductEditContext (companyId + refresh) que consumen los
  // pickers (PickProductPanel) para abrir la modal "Editar producto".
  const productEditContextValue = useMemo(
    () => ({ companyId: userCompanyId, refreshProducts: refreshUserProducts }),
    [userCompanyId, refreshUserProducts],
  );
  // Marca activa del user (profiles.current_brand_id). Se usa para (1) filtrar
  // el catálogo por marca — igual que Estudio Virtual — y (2) asociar `brand_id`
  // al crear productos desde V2. `brandResolved` pasa a `true` una vez que el
  // fetch del profile terminó (con marca o sin ella), para no fetchear productos
  // antes de saber por qué marca filtrar.
  const [currentBrandId, setCurrentBrandId] = useState<string | null>(null);
  const [brandResolved, setBrandResolved] = useState(false);
  // Rubro de la MARCA activa (`brand_profiles.rubro`), distinto del de la
  // empresa (`userRubro`, que sale de company_profiles).
  //
  // POR QUÉ EXISTE: en n8n, `If es moda1` decide qué familia de prompts usar
  // leyendo el rubro de la EMPRESA. En cuentas con varias marcas eso es
  // incorrecto — una empresa "Moda e Indumentaria" con una marca de muebles
  // generaba el mueble con los prompts de moda. El modo muebles manda este
  // valor en el payload para que n8n rutee por la marca que el usuario está
  // usando en ese momento. Ver ADR-589.
  const [brandRubro, setBrandRubro] = useState<string | null>(null);
  useEffect(() => {
    if (!currentBrandId) {
      setBrandRubro(null);
      return;
    }
    let cancelado = false;
    (async () => {
      const { data } = await supabase
        .from('brand_profiles')
        .select('rubro')
        .eq('id', currentBrandId)
        .single();
      if (!cancelado) {
        setBrandRubro(((data as any)?.rubro as string | null) ?? null);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [currentBrandId]);
  useEffect(() => {
    currentBrandIdRef.current = currentBrandId;
  }, [currentBrandId]);
  // Esperamos a que la marca activa esté resuelta (`brandResolved`) antes de
  // fetchear, así filtramos por la marca correcta desde el primer fetch. Si el
  // cache pintó productos de OTRA marca (o de "toda la empresa", cache viejo),
  // los limpiamos antes de re-fetchear para no mostrar productos ajenos.
  useEffect(() => {
    if (!userCompanyId || !brandResolved) return;
    let alive = true;
    if ((cachedBrandRef.current ?? null) !== (currentBrandId ?? null)) {
      setUserProducts([]);
    }
    (async () => {
      const products = await fetchUserProducts(userCompanyId, currentBrandId);
      if (alive) persistUserProducts(products);
    })();
    return () => {
      alive = false;
    };
  }, [userCompanyId, brandResolved, currentBrandId, persistUserProducts]);

  // ── Modelos guardados (globales + privados) ─────────────────────────
  // Fetcheamos vía `fetchUserModels` que ya filtra por id_company y firma
  // las URLs. `userAuthId` se captura del auth — hoy solo lo necesita el
  // fallback n8n de crear modelo (flag OFF).
  const [userModels, setUserModels] = useState<ModelV2[]>([]);
  const [userAuthId, setUserAuthId] = useState<string | null>(null);
  // Paleta de la marca activa (`brand_profiles.colors`) — array de hex
  // strings tipo `['#1644EB', '#000', ...]`. Sirve como default para el
  // color picker del modo Anuncio. Vacío si la marca no tiene paleta
  // cargada o si todavía no se resolvió el fetch.
  const [brandColors, setBrandColors] = useState<string[]>([]);
  // Fuente de marca renderizable (`brand_profiles.fonts.suggested[0]` que
  // exista en TEXT_FONT_OPTIONS). Se usa como default de la tipografía de
  // los 3 textos del modo Anuncio. '' = la marca no tiene una fuente
  // renderizable → los campos quedan en "Automática".
  const [brandFont, setBrandFont] = useState<string>('');
  // Ref siempre-al-día con el brandFont, para los reset points (evita
  // closures stale al resetear el state del anuncio).
  const brandFontRef = useRef<string>('');
  useEffect(() => {
    brandFontRef.current = brandFont;
  }, [brandFont]);
  // Siembra one-shot: cuando se resuelve la fuente de marca, los 3 campos
  // del anuncio que sigan sin fuente elegida adoptan la de la marca.
  const brandFontSeededRef = useRef(false);
  const refreshUserModels = async () => {
    if (!userCompanyId) return;
    const models = await fetchUserModels(userCompanyId);
    setUserModels(models);
  };
  useEffect(() => {
    if (!userCompanyId) return;
    let alive = true;
    (async () => {
      const [{ data: { user } }, models] = await Promise.all([
        supabase.auth.getUser(),
        fetchUserModels(userCompanyId),
      ]);
      if (!alive) return;
      setUserAuthId(user?.id || null);
      setUserModels(models);
      // current_brand_id del profile — para asociar brand a productos
      // creados desde V2.
      if (user?.id) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('current_brand_id')
          .eq('id', user.id)
          .single();
        if (alive) {
          const brandId =
            ((profile as any)?.current_brand_id as string) || null;
          setCurrentBrandId(brandId);
          // Marca resuelta → habilita el fetch de productos filtrado por marca.
          setBrandResolved(true);
          // Paleta + fuente del brand para los defaults del modo Anuncio.
          // Si NO hay marca activa (current_brand_id null — típico de cuentas
          // nuevas que nunca cambiaron de marca), caemos a la marca DEFAULT de
          // la empresa (mismo fallback que useBrandProfile); si no, el anuncio
          // quedaba sin colores ni fuente aunque la marca exista. NO tocamos
          // `currentBrandId` (el filtro de productos con null = sin filtrar).
          let colorsBrandId = brandId;
          if (!colorsBrandId) {
            const { data: def } = await supabase
              .from('brand_profiles')
              .select('id')
              .eq('id_company', userCompanyId)
              .eq('is_default', true)
              .eq('is_draft', false)
              .maybeSingle();
            if (alive) colorsBrandId = ((def as any)?.id as string) || null;
          }
          if (colorsBrandId) {
            const { data: brand } = await supabase
              .from('brand_profiles')
              .select('colors, fonts')
              .eq('id', colorsBrandId)
              .maybeSingle();
            if (alive) {
              // `colors` llega como `{hex,name,role}[]` (Estudio Virtual)
              // o `string[]` (editor), a veces sin `#` o en 3 dígitos.
              // Normalizamos a `#RRGGBB` válidos para el color picker.
              setBrandColors(normalizeBrandHexColors((brand as any)?.colors));
              // `fonts.suggested[0]` renderizable → default de tipografía.
              setBrandFont(resolveBrandFont((brand as any)?.fonts));
            }
          }
        }
      } else if (alive) {
        // Sin user no hay marca que resolver, pero igual habilitamos el fetch
        // (caerá en el catálogo sin filtro de marca).
        setBrandResolved(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [userCompanyId]);

  // ── Templates desde DB ──────────────────────────────────────────────
  // Se reemplazó la mock COLLECTIONS_V2 por un fetch real a Supabase.
  // El fetch espera a que el user info esté loaded para poder pasar
  // rubro y companyId correctos al filtro. Categorías sin templates que
  // pasen los filtros NO se renderizan (las skippea templatesApi).
  // Initializer lee el cache de sessionStorage: si el user volvió desde
  // una sub-ruta dentro de la sesión, arrancamos con la grilla llena en
  // el primer paint. Esto es clave para que el scroll-restore al
  // useLayoutEffect funcione (sino el documento es muy corto y
  // `scrollTo(y)` se clampea al maxScroll).
  const [fetchedCollections, setFetchedCollections] = useState<CollectionV2[]>(
    () => {
      try {
        const raw = sessionStorage.getItem(HOME_COLLECTIONS_CACHE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) return parsed as CollectionV2[];
        }
      } catch {
        /* noop */
      }
      return [];
    },
  );
  useEffect(() => {
    if (!userInfoLoaded) return;
    let alive = true;
    (async () => {
      const collections = await fetchTemplatesV2({
        rubro: userRubro,
        companyId: userCompanyId,
      });
      if (!alive) return;
      setFetchedCollections(collections);
      try {
        sessionStorage.setItem(
          HOME_COLLECTIONS_CACHE_KEY,
          JSON.stringify(collections),
        );
      } catch {
        /* noop */
      }
    })();
    return () => {
      alive = false;
    };
  }, [userInfoLoaded, userRubro, userCompanyId]);
  /* Acá se armaba el saludo de la home vieja ("Buenas tardes, Maxi."). Se fue con
     ADR-2026-07-31-inicio-se-funde-interfaz-v2: ahora lo arma `HomeMinimal`, que saca
     el nombre de `profiles` y la franja horaria de `saludo()`. Ver el comentario del
     `getGreeting` borrado, arriba del todo. */
  // Plan gratis. Conservador: sólo cuando el plan es explícitamente 'gratis', o
  // cuando ya cargamos el perfil y no hay plan — así no le parpadea nada a un usuario
  // pago mientras resuelve el fetch en un cold-load sin cache de plan en localStorage.
  // Lo usaba además el botón flotante "Contactanos", que ya no está
  // (ADR-2026-08-03-fuera-boton-flotante-contactanos); hoy sólo decide filas de la home.
  const isFreePlan = userPlan === 'gratis' || (userInfoLoaded && !userPlan);
  /* Acá vivía `contactWaHref`, el link de WhatsApp con mensaje pre-armado del botón
     flotante y su QR. Se fue con los dos. El contacto por WhatsApp sigue estando en
     el shell (pastilla "Whatsapp" del encabezado) y en `/ayuda`. */

  // ── Poses (para el modo "Imagen con modelo") ────────────────────────
  // Cargamos todas las poses una vez si el rubro califica. Se usan en:
  //   1. La card "Imagen con modelo" de la fila "Diseña tu imagen"
  //      (tomamos la primera pose como thumbnail temporal).
  //   2. El PosePicker (multi-select grilla con tabs).
  // El catálogo de poses cambia poco y es público — fetch único acá.
  // Initializer lee el cache para que la fila E-commerce aparezca en
  // el primer paint sin esperar a `fetchAllPoses`.
  const [allPoses, setAllPoses] = useState<PoseV2[]>(() => {
    try {
      const raw = sessionStorage.getItem(HOME_POSES_CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed as PoseV2[];
      }
    } catch {
      /* noop */
    }
    return [];
  });
  useEffect(() => {
    if (!isDesignRubro) return;
    let alive = true;
    (async () => {
      const poses = await fetchAllPoses();
      if (!alive) return;
      setAllPoses(poses);
      try {
        sessionStorage.setItem(HOME_POSES_CACHE_KEY, JSON.stringify(poses));
      } catch {
        /* noop */
      }
    })();
    return () => {
      alive = false;
    };
  }, [isDesignRubro]);

  // Lista final de colecciones a renderizar: si el rubro califica,
  // prepende una fila "Diseña tu imagen" con una sola card
  // "Imagen con modelo" (kind 'design-modelo') antes de las plantillas
  // tradicionales. La imagen de la card es temporalmente la de una pose
  // del catálogo.
  //
  // NO se muestra a las marcas de Hogar y Decoración
  // (ADR-2026-07-31-hogar-deco-sin-plantillas-ni-modelo): el modo pone el
  // producto sobre un modelo IA, que para un mueble no aplica.
  const designModeloCard: PresetV2 | null =
    isDesignRubro && !esMarcaHogarDeco
    ? {
        id: 'imagen-con-modelo',
        title: 'Generar imagen personalizada',
        description:
          'Tu producto sobre un modelo IA en la pose y el fondo que vos elijas.',
        // Carátula del modo (ADR-2026-08-04-modelo-portada-unica): el collage
        // "producto + fondo + cara → resultado" reemplaza a `fot_modelo.png`.
        //
        // El hero del detail sheet NO cambia — sigue con `modelo_gia.png`. Ver
        // el porqué en la entrada `modelo` de `modesCatalog.ts`, que esta card
        // espeja: si tocás una, tocá la otra.
        imageUrl: '/images/modo_con_modelo/modo_modelo_portada.jpg',
        imageUrlFull: '/images/modo_con_modelo/modelo_gia.png',
        kind: 'design-modelo' as const,
        // Costo base por imagen — el creditCost computado en
        // PresetDetailSheet multiplica esto por la cantidad real de
        // imágenes a generar (1 por defecto en este modo).
        creditosPorUnidad: 1,
        unidadConsumo: 'imagen' as const,
      }
    : null;
  // Card del modo "Generar imagen automáticamente" para la fila
  // "Diseña tu imagen". Visible en mobile y desktop — el entrypoint
  // del modo auto vive solo acá (antes había un botón azul arriba
  // del greeting que ya se removió).
  const designAutoCard: PresetV2 | null = isDesignRubro
    ? {
        id: 'imagen-auto-card',
        title: 'Generar imagen automáticamente',
        description:
          'Sumá tu producto y la IA arma todo: fondo, escena y luz por vos.',
        // Carátula GENÉRICA del modo (ADR-2026-08-05-automatica-va-con-foto-y-sin-video).
        // La del rubro Hogar y Decoración la pisa `PORTADA_POR_RUBRO` en las superficies
        // que dibujan la carátula.
        imageUrl: '/images/modo_automatico/modo_auto_portada.jpg',
        imageUrlFull: '/images/modo_automatico/modo_auto.png',
        kind: 'design-auto' as const,
        // Costo base por imagen — el creditCost de PresetDetailSheet
        // multiplica esto por `autoImageCount` (1-6 imágenes).
        creditosPorUnidad: 1,
        unidadConsumo: 'imagen' as const,
      }
    : null;
  // Card del modo "Fotos de producto" — el hermano SIN modelo de "Imagen
  // personalizada": un producto, las vistas que elija el user y fondo blanco
  // o escena descrita. Visible para todas las marcas (incluidas Hogar y
  // Decoración: un mueble también se fotografía solo).
  //
  // DUPLICADA con `modesCatalog.ts` — es el patrón de todas las cards de modo:
  // la de acá alimenta la home V2 y la de allá la pantalla /modos. Si tocás
  // una, tocá la otra. Ver ADR-2026-08-13-modo-fotos-producto.
  const fotosProductoCard: PresetV2 | null = isDesignRubro
    ? {
        id: 'fotos-producto-card',
        title: 'Fotos de producto',
        description:
          'Tu producto solo, en las vistas que necesites: fondo blanco o la escena que describas.',
        // Carátula de la card: PLACEHOLDER (thumb del catálogo de vistas) hasta
        // tener una propia. El `imageUrlFull` sí es el asset definitivo: es el
        // hero de "Elegí el producto" — el collage antes → después.
        imageUrl: '/images/multi-vistas/zapatillas/lateral.webp',
        imageUrlFull: '/images/modo_fotos_producto/hero_producto.png',
        kind: 'fotos-producto' as const,
        // Costo base por imagen — PresetDetailSheet lo multiplica por la
        // cantidad de vistas elegidas (una imagen por vista).
        creditosPorUnidad: 1,
        unidadConsumo: 'imagen' as const,
      }
    : null;
  // Card del modo "Animar imagen" — foto (subida o generada) → video. Tiene
  // pantalla propia (/animar-imagen), así que el click NO abre sheet: navega
  // (ver MODE_KIND_TO_ROUTE). Visible para todos los rubros: animar una foto no
  // depende de tener productos cargados.
  //
  // DUPLICADA con `modesCatalog.ts` — es el patrón de todas las cards de modo: la
  // de acá alimenta la home V2 y la de allá la pantalla /modos. Si tocás una,
  // tocá la otra. Ver ADR-2026-08-14-modo-animar-imagen.
  const animarImagenCard: PresetV2 = {
    id: 'animar-imagen-card',
    title: 'Animar imagen',
    description: 'Convertí cualquier foto en un video con movimiento realista.',
    imageUrl: '/images/modo_video_ugc/modo_video_ugc_miniatura.png',
    imageUrlFull: '/images/modo_video_ugc/modo_video_ugc_miniatura.png',
    kind: 'animar-imagen' as const,
    outputType: 'video' as const,
    creditosPorUnidad: 1,
    unidadConsumo: 'segundo' as const,
  };
  // Card del modo "Carrusel" — foto (subida o generada) → 2-6 tomas nuevas del
  // mismo producto. Tiene pantalla propia (/carrusel), así que el click NO abre
  // sheet: navega (ver MODE_KIND_TO_ROUTE). Visible para todos los rubros: partir
  // de una foto no depende de tener productos cargados.
  //
  // DUPLICADA con `modesCatalog.ts` — es el patrón de todas las cards de modo: la
  // de acá alimenta la home V2 y la de allá la pantalla /modos. Si tocás una,
  // tocá la otra. Ver ADR-2026-08-14-modo-carrusel.
  const carruselCard: PresetV2 = {
    id: 'carrusel-card',
    title: 'Carrusel de imágenes',
    description:
      'De una foto, varias: nuevas tomas del mismo producto con otros ángulos y encuadres.',
    // Carátula 1:1 (el abanico centrado con fondo): los slots de card recortan
    // por ejes opuestos —3:2 en desktop, 4:5 en mobile— y el PNG original, que es
    // 1.75:1 con alpha, perdía las fotos de los costados.
    imageUrl: '/images/modo_carrusel/caratula.jpg',
    imageUrlFull: '/images/modo_carrusel/caratula.jpg',
    kind: 'carrusel' as const,
    creditosPorUnidad: 1,
    unidadConsumo: 'imagen' as const,
  };
  // Card del modo "Catálogo IA" — tercera card del design-row. Pensada
  // para generar photoshoots de catálogo: el user elige productos +
  // varias vistas (poses con-modelo + ángulos solo-producto) y el sheet
  // dispara una llamada V3 por cada (vista, producto) compatible. El
  // entrypoint visible está siempre que `isDesignRubro` califica (igual
  // que las otras dos cards de modo), MENOS para las marcas de Hogar y
  // Decoración: ahí su lugar lo ocupa el catálogo de muebles, que se muestra
  // con este mismo título — ADR-2026-08-01-hogar-deco-solo-catalogo-muebles-renombrado.
  const catalogoIACard: PresetV2 | null =
    isDesignRubro && !esMarcaHogarDeco
    ? {
        id: 'catalogo-ia-card',
        title: 'Generar catálogo con IA',
        description:
          'Photoshoots por producto con varias vistas y poses en un solo proceso.',
        // El `imageUrl` (thumbnail home) sigue siendo el mismo modelo_gia
        // hasta tener una carátula chica propia. El `imageUrlFull` (hero
        // que se muestra en choose-product) ya tiene su asset dedicado.
        imageUrl: '/images/modo_con_modelo/fot_modelo.png',
        imageUrlFull: '/images/modo_catalogo_ia/modo_catalogo_hero.png',
        kind: 'catalogo-ia' as const,
        // Costo base por imagen — el creditCost de PresetDetailSheet
        // lo multiplica por la cantidad de (vista × producto) cards
        // confirmadas en el step final.
        creditosPorUnidad: 1,
        unidadConsumo: 'imagen' as const,
      }
    : null;
  // Card del modo "Catálogo de muebles" (ADR-582). Clona el flujo del Catálogo
  // IA pero con vistas propias (fondo blanco / infografía con medidas / foto en
  // contexto), sin poses ni modelo, y con una pantalla donde el user carga las
  // medidas de cada producto antes de generar.
  //
  // Esta definición está DUPLICADA con la de `modesCatalog.ts` — es el patrón
  // (feo) que siguen todas las cards de modo: la de acá alimenta la home V2 y la
  // de allá la pantalla /modos. Si tocás una, tocá la otra.
  const catalogoMueblesCard: PresetV2 | null =
    esMarcaHogarDeco && isDesignRubro
      ? {
          id: 'catalogo-muebles-card',
          // Mismo título que el Catálogo IA: este modo solo lo ven las marcas de
          // Hogar y Decoración, y para ellas el Catálogo IA no existe — ver
          // ADR-2026-08-01-hogar-deco-solo-catalogo-muebles-renombrado.
          title: 'Generar catálogo con IA',
          description:
            'Fondo blanco, infografía con medidas y foto en contexto, para cada uno de tus productos.',
          // Carátula propia (el ropero en contexto, ADR-817) — la card no tiene
          // video, así que HomeCards cae a este `imageUrl`. El `imageUrlFull` no
          // se ve: el hero de "Elegí el producto" lo arma ProductHeroCollage.
          imageUrl: '/images/modo_catalogo_muebles/hero_contexto.jpg',
          imageUrlFull: '/images/modo_catalogo_ia/modo_catalogo_hero.png',
          kind: 'catalogo-muebles' as const,
          creditosPorUnidad: 1,
          unidadConsumo: 'imagen' as const,
        }
      : null;
  // Card del modo "Infografía" (ADR-814). Mismo gate de rubro de marca que el
  // catálogo de muebles: son el mismo flujo y el mismo público.
  //
  // Antes solo existía en `/modos`; al habilitar los modos se sumó también acá
  // (ADR-2026-07-31-modos-muebles-por-rubro-de-marca) para que la home y Modos
  // muestren lo mismo y para que el deep-link `/interfaz-v2/modo/infografia`
  // pueda resolver su card. Duplicada con `modesCatalog.ts` — si tocás una,
  // tocá la otra.
  //
  // El rediseño había llegado a la misma card con `rubroVeInfografia(brandRubro ??
  // userRubro)`; gana esta versión, que usa el gate único
  // (ADR-2026-08-05-un-solo-gate-de-rubro-para-los-modos). La diferencia real es el
  // `?? userRubro`: acá NO se cae al rubro de la empresa, que es la decisión
  // explícita de `principal`.
  const infografiaCard: PresetV2 | null =
    esMarcaHogarDeco && isDesignRubro
      ? {
          id: 'infografia-card',
          title: 'Infografías de producto con IA',
          description:
            'Fichas técnicas con las medidas y características de cada uno de tus productos.',
          imageUrl: '/images/modo_infografia/hero_foto.jpg',
          imageUrlFull: '/images/modo_catalogo_ia/modo_catalogo_hero.png',
          kind: 'infografia' as const,
          creditosPorUnidad: 1,
          unidadConsumo: 'imagen' as const,
        }
      : null;
  // Card del modo "Asistente de marketing" — cuarta card del design-row.
  // Un flow tipo chat con input libre + adjuntos (productos / modelos /
  // imágenes anteriores) + pill options abajo (formato, cantidad,
  // duración video, escena múltiple). El backend / costo no están
  // wireados todavía — esta primera iteración es el skeleton visual.
  const asistenteCard: PresetV2 | null = isDesignRubro
    ? {
        id: 'asistente-marketing-card',
        title: 'Crear desde cero',
        description: 'Generá, analizá y planificá tu contenido',
        // Placeholders — el user va a subir assets dedicados cuando
        // los tenga. Mientras, reusamos los de catalogo para no romper
        // el render.
        imageUrl: '/images/modo_con_modelo/fot_modelo.png',
        imageUrlFull: '/images/modo_catalogo_ia/modo_catalogo_hero.png',
        kind: 'asistente' as const,
        creditosPorUnidad: 1,
        unidadConsumo: 'imagen' as const,
      }
    : null;
  // Card del modo "Anuncio" — quinta card del design-row. El user elige
  // producto(s), después configura formato + 3 campos de texto (título,
  // descripción, llamada a acción) con opción de "automático" o de
  // quitar cada uno, y hasta 4 colores (default = paleta del
  // brand_profile activo). El backend / costo todavía no están wireados.
  const anuncioCard: PresetV2 | null = isDesignRubro
    ? {
        id: 'anuncio-card',
        title: 'Anuncio',
        description:
          'Texto, colores y formato — armá un anuncio listo para publicar.',
        // Carátula del row de modos. Era un placeholder prestado de modelo; ahora es
        // la foto propia del modo (ADR-2026-08-04-anuncio-va-con-foto-y-sin-video),
        // la misma que sirve `MODES` para las otras superficies. Es la GENÉRICA: la del
        // aviso de mueble quedó para Hogar y Decoración y la pisa `PORTADA_POR_RUBRO`
        // en las superficies que dibujan la carátula
        // (ADR-2026-08-05-la-portada-de-anuncio-es-de-hogar).
        imageUrl: '/images/modo_anuncio/modo_anuncio_portada.jpg',
        // Hero dedicado del detalle del modo anuncio (mobile + desktop).
        imageUrlFull: '/images/modo_anuncio/modo_anuncio_hero.png',
        kind: 'anuncio' as const,
        creditosPorUnidad: 1,
        unidadConsumo: 'imagen' as const,
      }
    : null;
  // Card del modo "Video con influencer". Pantalla 1 = selección de
  // productos (igual que los otros modos); pantalla 2 = el mismo layout de
  // plantilla-video del ConfigurePanel + la sección "Gancho"/"Voz y guion"
  // vía `showGancho`. Visible para TODOS los planes (ADR-561): la
  // restricción es al GENERAR — `checkGenerationGuards('video')` muestra el
  // VideoPlanRestrictionModal si el plan no es Pro/Ultra.
  const videoInfluencerCard: PresetV2 | null =
    VIDEO_INFLUENCER_ENABLED && isDesignRubro
      ? {
          id: 'video-influencer-card',
          title: 'Video con influencer',
          description:
            'Generá un video de tu producto presentado por un influencer IA, con un gancho que enganche en los primeros segundos.',
          imageUrl: '/images/modo_video_ugc/modo_video_ugc_miniatura.png',
          imageUrlFull: '/images/modo_video_ugc/modo_video_ugc_miniatura.png',
          // Poster landscape para la carátula en video del hero desktop.
          poster: '/images/modo_video_ugc/modo_video_ugc_caratula_desktop.jpg',
          kind: 'video-influencer' as const,
          outputType: 'video' as const,
          creditosPorUnidad: 1,
          unidadConsumo: 'segundo' as const,
        }
      : null;
  // Colección "E-commerce" — cada pose del catálogo se renderiza como
  // una card con `kind: 'design'` (legacy single-pose flow). Cuando el
  // user tapea una card, abre el sheet del preset con esa pose como
  // hero. Solo se muestra para rubros que califican (isDesignRubro) y
  // si hay poses cargadas. Se inserta entre el design-row (modos) y
  // las categorías de templates de la DB.
  // Orden custom de las poses dentro del row de E-commerce:
  //   1. cuerpo_completo
  //   2. plano_medio
  //   3. resto (recomendado, sin categoría, etc.)
  //   4. detalle_close-up (al final)
  // Pedido del producto para guiar al user de "general → particular".
  const ecommerceCategoryRank = (cat: string | null): number => {
    if (cat === 'cuerpo_completo') return 0;
    if (cat === 'plano_medio') return 1;
    if (cat === 'detalle_close-up') return 3;
    return 2;
  };
  const ecommercePosesCollection: CollectionV2 | null =
    isDesignRubro && allPoses.length > 0
      ? {
          id: 'e-commerce',
          title: 'Fondo blanco',
          // "Fondo blanco" no es una categoría de la base: se arma acá con las poses, así
          // que su descripción también se escribe acá. Va con el mismo criterio que las
          // de `template_categorias`: menos de 100 caracteres
          // (ADR-2026-08-03-descripciones-para-las-categorias-sin-una).
          subtitle:
            'Tu producto sobre fondo blanco, en distintas poses y planos, listo para el e-commerce.',
          presets: [
            ...[...allPoses]
              .sort(
                (a, b) =>
                  ecommerceCategoryRank(a.categoria) -
                  ecommerceCategoryRank(b.categoria),
              )
              .map(
                (p): PresetV2 => ({
                  id: `pose-${p.id}`,
                  title: p.nombre,
                  imageUrl: p.imageUrl,
                  imageUrlFull: p.imageUrl,
                  kind: 'design' as const,
                  personCount: p.cantidadPersonas,
                }),
              ),
            // Vistas "solo producto" (mismo catálogo que Catálogo IA) al
            // final del row — cards kind 'design-producto'.
            ...buildVistasSoloProductoPresets(),
          ],
        }
      : null;
  // Flag "promover Imagen personalizada": viaja en el navigation state
  // desde los CTAs "Seguir generando"/"Listo" de la pantalla de resultados
  // cuando la tanda se generó en modo AUTOMÁTICO. Se captura una vez al
  // montar (useState initializer, sobrevive al clear de abajo) y se limpia
  // del history para que un refresh o un back a esta entrada no lo
  // re-aplique. Efecto: la fila de modos muestra modelo primero (primer
  // slide del hero mobile + primera card del row desktop), solo esa visita.
  const [promoteModeloFirst] = useState<boolean>(() => {
    try {
      const navState = window.history.state?.usr as
        | { promoteModeloFirst?: boolean }
        | undefined;
      return navState?.promoteModeloFirst === true;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (!promoteModeloFirst) return;
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Orden de la fila: [auto, modelo, catalogo-ia, catalogo-muebles,
  // infografia, video-influencer, asistente, anuncio]. Con
  // `promoteModeloFirst`, modelo pasa al frente: [modelo, auto, …]. Cada card
  // decide sola si existe (rubro / plan / marca), así que acá solo se
  // concatenan las que quedaron.
  const designRowPresets: PresetV2[] = [
    ...(promoteModeloFirst
      ? [
          ...(designModeloCard ? [designModeloCard] : []),
          ...(designAutoCard ? [designAutoCard] : []),
        ]
      : [
          ...(designAutoCard ? [designAutoCard] : []),
          ...(designModeloCard ? [designModeloCard] : []),
        ]),
    // Fotos de producto va pegado a los dos modos de imagen suelta: es el
    // mismo gesto (una generación puntual) pero sin modelo.
    ...(fotosProductoCard ? [fotosProductoCard] : []),
    ...(catalogoIACard ? [catalogoIACard] : []),
    // Va justo después del Catálogo IA: son hermanos conceptuales y
    // conviene que se lean juntos en el row.
    ...(catalogoMueblesCard ? [catalogoMueblesCard] : []),
    // Infografías va pegado al catálogo de muebles: comparten flujo,
    // motor y público (marcas de Hogar y Decoración).
    ...(infografiaCard ? [infografiaCard] : []),
    ...(videoInfluencerCard ? [videoInfluencerCard] : []),
    // Animar imagen va pegado al video con influencer: son los dos modos que
    // devuelven un video.
    animarImagenCard,
    // Carrusel va pegado a Animar: son los dos modos que parten de una imagen que
    // el usuario ya tiene (subida o generada) en vez de un producto del catálogo.
    carruselCard,
    ...(asistenteCard ? [asistenteCard] : []),
    // "Anuncio" ya NO va en la tira —
    // ADR-2026-08-20-anuncio-sale-de-las-listas-sin-salir-del-catalogo.
    //
    // Esta lista se arma a mano y no sale de `MODES`, así que el flag `ocultoEnListas`
    // del catálogo no la alcanza: es la quinta superficie y hay que sacarlo acá.
    // `anuncioCard` se sigue calculando más arriba porque de él salen el hero de mobile
    // y el visor de modo, que no son listas de descubrimiento.
    //
    // El modo sigue vivo: lo abren el atajo de "Ediciones rápidas", los botones del
    // visor y del historial, y el deep-link `/interfaz-v2/modo/anuncio`.
  ];
  // Las plantillas las ve SÓLO el rubro Moda — ADR-2026-08-03-plantillas-solo-para-moda,
  // que al mergear le gana al `!esMarcaHogarDeco` de `principal`
  // (ADR-2026-08-05-plantillas-solo-moda-gana-al-merge). Los dos apuntaban a lo mismo
  // desde lados opuestos: son escenas y poses con modelo, que no le sirven a un mueble
  // —ni a un plato, ni a un frasco de crema—. Esta regla es más estricta y cubre el
  // caso de Hogar y Decoración igual, así que no quedan dos condiciones discutiendo.
  //
  // Apaga TODO de una vez: la sección embebida de desktop, los rows de mobile, el
  // "Fondo blanco" de poses y el "Ver todo". Los datos se siguen fetcheando (el fetch
  // no depende de esto); acá se decide qué se renderiza.
  // `rubroMarcaActiva` y NO `brandRubro` — ADR-2026-08-21-plantillas-rebotaban-por-dos-marcas-activas.
  //
  // `brandRubro` resuelve la marca SOLO por `profiles.current_brand_id`; cuando está
  // en `null` (el 98,5% de las cuentas) se queda en `null` y el `??` cae al rubro de
  // la EMPRESA, salteándose la marca. El guard de `/plantillas` usa el criterio
  // canónico (`current_brand_id` → `is_default` → la primera, ver `useRubroMarca.ts`),
  // así que leía el rubro de la marca default. Con empresa Moda + marca default de
  // otro rubro, la home mostraba las filas y el "Ver más" rebotaba a la home.
  //
  // El `?? userRubro` SÍ se conserva (a diferencia del gate de Infografía): para
  // plantillas el fallback a empresa es parte de la regla (`lib/rubro.ts`) y es
  // exactamente lo que hace el guard (`activeBrand?.rubro ?? perfilEmpresa?.rubro`).
  const mostrarPlantillas = rubroVePlantillas(rubroMarcaActiva ?? userRubro);
  const displayCollections: CollectionV2[] = [
    ...(designRowPresets.length > 0
      ? [
          {
            id: 'design-row',
            title: 'Diseña tu imagen',
            subtitle: undefined,
            presets: designRowPresets,
          },
        ]
      : []),
    ...(mostrarPlantillas && ecommercePosesCollection
      ? [ecommercePosesCollection]
      : []),
    ...(mostrarPlantillas ? fetchedCollections : []),
  ];

  /* ── Sección Plantillas embebida (desktop) ────────────────────────────
     Acá vivía `plantillasChipsStuck`, en `false`: escondía el botón flotante
     "Contactanos" cuando la chip-bar de `HomeTemplatesSection` se volvía `fixed`. La
     sección se había ido de la home (ADR-2026-07-31-inicio-se-funde-interfaz-v2) y la
     constante se conservaba sólo porque el botón la leía. Sin el botón
     (ADR-2026-08-03-fuera-boton-flotante-contactanos) no queda ni una cosa ni la
     otra. */

  // ── Prompt bar ────────────────────────────────────────────────────────
  const [promptFocused, setPromptFocused] = useState(false);
  const [prompt, setPrompt] = useState('');
  const promptRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-resize del textarea cuando crece el contenido
  useEffect(() => {
    const el = promptRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [prompt, promptFocused]);

  // ── Chat agent ───────────────────────────────────────────────────────
  // Le pasamos los productos REALES del usuario para que el agente
  // preseleccione IDs que existen en el catálogo (antes usaba mock).
  const chat = useV2Chat(shortName, userProducts);
  const [chatOpen, setChatOpen] = useState(false);

  // "Sorpréndeme": abre el chat con un flujo SCRIPTED (sin pegarle al
  // agente). Se ve como una conversación normal — user dice "No tengo
  // idea, sorpréndeme", el agente "responde" con texto pre-armado, y
  // se le muestran las tool cards de pick_product → confirm_generate.
  // El texto del agente varía si hace poco generó algo (sessionStorage),
  // así suena natural y no repetitivo.
  const LAST_SURPRISE_KEY = 'atlas-v2-last-surprise-ts';
  const handleSurpriseMe = () => {
    setChatOpen(true);
    setPrompt('');
    setPromptFocused(false);
    promptRef.current?.blur();

    let recentSurprise = false;
    try {
      const last = sessionStorage.getItem(LAST_SURPRISE_KEY);
      if (last) {
        const diffMin = (Date.now() - parseInt(last, 10)) / 1000 / 60;
        // < 10 minutos = "hace poco". Si pasó más, vamos con el texto
        // estándar.
        recentSurprise = diffMin < 10;
      }
    } catch {
      /* noop */
    }

    // Corre el script en background (la UI ya está abierta). El delay
    // chico antes del primer push asegura que el chat panel ya animó in.
    (async () => {
      await new Promise((r) => setTimeout(r, 250));
      chat.pushUserMessage('No tengo idea, sorpréndeme');

      await new Promise((r) => setTimeout(r, 350));
      chat.pushAssistantText(
        recentSurprise
          ? '¡Otra vamos! ¿Qué producto usamos esta vez?'
          : 'Perfecto. ¿Qué producto querés que use?',
      );

      await new Promise((r) => setTimeout(r, 200));
      const productResp = await chat.pushToolCard('pick_product', {
        message: 'Elegí un producto',
        allow_multi: false,
      });
      if (productResp?.cancelled) return;

      await new Promise((r) => setTimeout(r, 350));
      // Copy explica qué pasa al tocar Generar — el agente "se encarga
      // de pensar y generar". Si tenemos companyName lo usamos para que
      // se sienta personal; sino cae al fallback "tu marca".
      const brand = companyName || 'tu marca';
      chat.pushAssistantText(
        recentSurprise
          ? `Dale a generar y sigo armando imágenes para ${brand} que puedas subir a tus redes.`
          : `Ok, dale a generar entonces y yo me encargaré de pensar y generar imágenes para ${brand} que puedas subir a tus redes.`,
      );

      await new Promise((r) => setTimeout(r, 200));
      const lastFormat = (() => {
        try {
          return localStorage.getItem('atlas-v2-last-format') || 'square';
        } catch {
          return 'square';
        }
      })();
      const confirmResp = await chat.pushToolCard('confirm_generate', {
        format: lastFormat,
        scene_summary: recentSurprise
          ? 'Una escena sorpresa nueva con el producto elegido'
          : 'Una escena sorpresa con el producto elegido',
      });
      if (confirmResp?.cancelled) return;

      // Persistir timestamp para que la próxima sorpresa sepa que el
      // user generó hace poco.
      try {
        sessionStorage.setItem(LAST_SURPRISE_KEY, String(Date.now()));
      } catch {
        /* noop */
      }

      await new Promise((r) => setTimeout(r, 200));
      chat.pushAssistantText('¡Listo! Estamos generando tu imagen.');
    })();
  };

  // Cierra el chat y resetea el historial. Es lo que pasa al tocar X o
  // el back mobile. Volvemos a la home V2.
  const closeChat = () => {
    setChatOpen(false);
    setMobileBackOverride(null);
    setMobileTitleOverride(null);
    // Reset del chat con un pequeño delay para no ver el wipe del historial
    // mientras la animación de salida del panel termina.
    setTimeout(() => chat.reset(), 350);
  };

  // Cuando se abre el chat, instalamos el back override mobile para que
  // el botón "<" de la top bar también lo cierre.
  useEffect(() => {
    if (chatOpen) {
      setMobileBackOverride(() => () => closeChat());
      setMobileTitleOverride(' ');
      return () => {
        setMobileBackOverride(null);
        setMobileTitleOverride(null);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatOpen]);

  const handleGeneratePrompt = () => {
    if (!prompt.trim()) return;
    // Abre el chat fullscreen + manda el primer mensaje al agente. La
    // animación de "expand" del prompt bar es el panel fullscreen
    // apareciendo con fade + translateY.
    const firstMsg = prompt.trim();
    chat.sendMessage(firstMsg);
    setChatOpen(true);
    setPrompt('');
    setPromptFocused(false);
    promptRef.current?.blur();
  };

  // ── Preset flow ───────────────────────────────────────────────────────
  // Initializer sincrónico: si el user entró desde ColeccionDetalle /
  // InterfazV2Plantillas con `{ presetData, collectionPresets }` en el
  // navigation state, abrimos el sheet en el PRIMER paint. Así no hay
  // ni un frame de home visible, ni hace falta overlay blanco — el
  // sheet sustituye al home directamente.
  const initialNavPreset = (() => {
    try {
      const navState = window.history.state?.usr as
        | { presetData?: PresetV2; collectionPresets?: PresetV2[] }
        | undefined;
      return navState?.presetData ?? null;
    } catch {
      return null;
    }
  })();
  const initialNavPresetList = (() => {
    try {
      const navState = window.history.state?.usr as
        | { presetData?: PresetV2; collectionPresets?: PresetV2[] }
        | undefined;
      return Array.isArray(navState?.collectionPresets)
        ? (navState!.collectionPresets as PresetV2[])
        : [];
    } catch {
      return [];
    }
  })();
  /**
   * Productos ya elegidos afuera — ADR-2026-08-04-el-producto-entra-con-el-modo.
   *
   * Los manda el sheet de modos de "Editar marca", donde el usuario eligió PRIMERO el
   * producto y después el modo: pedirle el producto otra vez al entrar sería repetir
   * el paso que acaba de dar.
   *
   * Viajan como objetos y no como ids porque acá se leen SINCRÓNICAMENTE, en el
   * initializer: son el valor inicial de `selectedProducts` y lo que decide el paso en
   * el que abre el panel. Resolver ids contra `userProducts` —lo que hace el restore
   * post-generación— llega tarde para las dos cosas y dejaría ver el paso de elegir
   * producto antes de saltearlo.
   */
  const initialNavProducts = (() => {
    try {
      const navState = window.history.state?.usr as
        | { productosPreseleccionados?: MockProductV2[] }
        | undefined;
      return Array.isArray(navState?.productosPreseleccionados)
        ? (navState!.productosPreseleccionados as MockProductV2[])
        : [];
    } catch {
      return [];
    }
  })();
  /**
   * La creación desde la que se abrió el anuncio —
   * ADR-2026-08-05-el-anuncio-desde-una-creacion-usa-el-modo.
   *
   * Mismo canal y misma urgencia que los dos de acá abajo: se lee sincrónico porque de
   * él dependen el paso en el que abre el panel y lo que se muestra como sujeto. Trae
   * `url` para pintarla e `id` porque es lo que viaja al backend en vez del producto.
   */
  const initialNavImagenBase = (() => {
    try {
      const navState = window.history.state?.usr as
        | { imagenBase?: { id: string; url: string } }
        | undefined;
      const img = navState?.imagenBase;
      return img && img.id && img.url ? img : null;
    } catch {
      return null;
    }
  })();

  /**
   * Modelo ya elegido afuera — ADR-2026-08-04-desde-un-modelo-se-elige-el-modo.
   *
   * El gemelo del de arriba, para el otro camino de entrada de "Marca": tocar un
   * modelo de la grilla y elegir el modo. Viaja por el mismo canal —el `state` de la
   * navegación— y se lee igual de sincrónico, porque es el valor inicial de
   * `modelSelection` y el efecto de defaults sólo completa lo que sigue en `null`.
   *
   * Es una `ModelSelection` armada ya en el sheet y no el id del modelo: resolverlo
   * contra `userModels` es asíncrono, y hasta que llegara el efecto de defaults ya le
   * habría puesto el modelo de la última generación.
   */
  const initialNavModel = (() => {
    try {
      const navState = window.history.state?.usr as
        | { modeloPreseleccionado?: ModelSelection }
        | undefined;
      const sel = navState?.modeloPreseleccionado;
      return sel && typeof sel === 'object' && typeof sel.mode === 'string'
        ? (sel as ModelSelection)
        : null;
    } catch {
      return null;
    }
  })();
  const [selectedPreset, setSelectedPreset] = useState<PresetV2 | null>(
    initialNavPreset,
  );
  // Para deep-link (InterfazV2Plantillas / ColeccionDetalle) arrancamos
  // `animated = true` así el sheet aparece ya posicionado desde el
  // primer paint, sin la animación de slide-up. Si dejáramos `false`,
  // el sheet entra desde abajo durante ~300ms y en esos frames se ve la
  // home detrás (el "flash" reportado). Para clicks dentro de la home
  // arrancamos en `false` y el `useEffect` de selectedPreset dispara la
  // animación de entrada normal.
  const [presetSheetAnimated, setPresetSheetAnimated] = useState(
    !!initialNavPreset,
  );

  // Barra superior de créditos (ADR-2026-07-30-barra-superior-creditos-home-ancho, criterio de ADR-2026-07-30-solo-criterio-saldo-toda-app). Ya NO depende del
  // plan ni de `kind`: sale para cualquiera que esté sin créditos o por debajo del
  // 5% de la capacidad de SU plan. No va con un preset abierto: ahí la home está
  // oculta y la barra quedaría flotando arriba de otra pantalla.
  //
  // Ya no hay flag de dev para forzarla (supersede ADR-2026-07-30-flag-dev-forzar-barra-creditos):
  // el panel CONTEXTO DEV se eliminó — ADR-2026-08-05-fuera-los-overrides-de-dev.
  const estadoCreditos = estadoDeCreditos(
    homeBanners.freeImagesRemaining,
    homeBanners.creditsTotal,
  );
  const estadoTopBar: "sin" | "pocos" =
    estadoCreditos === "ok" ? "sin" : estadoCreditos;
  const mostrarTopBar =
    estadoCreditos !== "ok" &&
    !topBarDescartada &&
    !selectedPreset &&
    // El shell nuevo pinta la suya, con los mismos datos y el mismo CTA
    // (ADR-2026-07-31-shell-provee-flag-hospedaje). En desktop las dos se apilaban.
    !dentroDelShell;

  /**
   * El alto de la barra de créditos de mobile, publicado como
   * `--barra-creditos-mobile` — ADR-2026-08-04-la-pastilla-de-creditos-va-abajo-del-cartel.
   *
   * La barra es `fixed` arriba de todo, así que no empuja nada; el header mobile —donde
   * vive la pastilla de créditos— se corre solo leyendo esta variable. Se MIDE y no se
   * escribe a mano porque el alto depende del texto: "Te quedan 2 créditos…" con
   * descuento envuelve en dos líneas y sin descuento en una.
   *
   * Sólo en mobile: de `sm:` para arriba la barra vuelve al flujo y no hay nada que
   * correr, así que la variable vale 0 y el header queda como estaba.
   */
  const refBarraCreditos = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const raiz = document.documentElement;
    const limpiar = () => raiz.style.removeProperty('--barra-creditos-mobile');
    const el = refBarraCreditos.current;
    if (!mostrarTopBar || !el) {
      limpiar();
      return;
    }
    const medir = () => {
      const enMobile = window.matchMedia('(max-width: 639.98px)').matches;
      raiz.style.setProperty(
        '--barra-creditos-mobile',
        enMobile ? `${el.offsetHeight}px` : '0px',
      );
    };
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    window.addEventListener('resize', medir);
    return () => {
      observador.disconnect();
      window.removeEventListener('resize', medir);
      limpiar();
    };
  }, [mostrarTopBar]);
  /**
   * La tarjeta al lado del saludo — ADR-2026-07-31-banners-desktop-solo-upsell-cede.
   *
   * Ese ADR hacía que el **upsell** cediera ante la barra superior: los dos hablaban
   * de créditos y del descuento, así que juntos decían lo mismo dos veces. Ya no hay
   * nada que ceder — el upsell se borró y la barra ocupó su lugar
   * (ADR-2026-08-03-barra-creditos-reemplaza-upsell)—, y los tres que quedan
   * —describir la marca, encuesta, Tienda Nube— son temas distintos de los créditos:
   * conviven con la barra sin repetirse.
   */
  const bannerLateral = !!homeBanners.kind;

  // ── Preservar scroll de la home al abrir/cerrar un preset ─────────────
  // Con un preset abierto la home devuelve `null` (ahorro de GPU de los
  // videos) → el documento se achica y el scroll se pierde; al cerrar, la home
  // se re-renderiza EN EL TOPE. Trackeamos la última posición REAL de la home
  // (mientras NO hay preset abierto) y la restauramos al cerrar, reintentando
  // hasta que el documento vuelva a crecer lo suficiente.
  const lastHomeScrollRef = useRef(0);
  const presetOpenRef = useRef(false);
  useEffect(() => {
    const onScroll = () => {
      if (!presetOpenRef.current) lastHomeScrollRef.current = window.scrollY;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useLayoutEffect(() => {
    const wasOpen = presetOpenRef.current;
    const isOpen = !!selectedPreset;
    presetOpenRef.current = isOpen;
    // Solo en la transición abierto → cerrado (la home se re-montó en el tope).
    if (!wasOpen || isOpen) return;
    const y = lastHomeScrollRef.current;
    if (y <= 0) return;
    let cancelled = false;
    let raf = 0;
    const start = Date.now();
    const step = () => {
      if (cancelled) return;
      window.scrollTo({ top: y });
      if (Math.abs(window.scrollY - y) > 2 && Date.now() - start < 1000) {
        raf = requestAnimationFrame(step);
      }
    };
    raf = requestAnimationFrame(step);
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
    };
  }, [selectedPreset]);
  // Flag: si el user llegó por deep-link (desde ColeccionDetalle o
  // InterfazV2Plantillas), al cerrar el sheet hacemos `navigate(-1)`
  // para volver a la categoría — no a la home de V2. Sin esto el
  // closePresetSheet solo cierra el sheet y deja al user mirando el
  // home, perdiendo su contexto.
  const cameFromDeepLinkRef = useRef(!!initialNavPreset);
  // Scroll de la home al abrir un preset in-state (plantillas embebidas
  // en desktop / rows en mobile): el contenedor de la home pasa a
  // `hidden`, el contenido del scroller se acorta y su scroll colapsa a
  // 0 — al cerrar el sheet el user quedaba arriba de todo. Guardamos la
  // posición en los handlers de apertura y la restauramos cuando
  // `selectedPreset` vuelve a null. Doble rAF: el primero deja pintar
  // la home re-mostrada; el segundo asegura que el layout ya midió.
  // OJO: siempre vía utils/appScroll — en desktop el scroller es el
  // panel `.content-panel-clip`, no el window (window.scrollY da 0).
  const homeScrollBeforeSheetRef = useRef<number | null>(null);
  const hadSelectedPresetRef = useRef(!!initialNavPreset);
  useEffect(() => {
    const hadPreset = hadSelectedPresetRef.current;
    hadSelectedPresetRef.current = !!selectedPreset;
    if (!hadPreset || selectedPreset) return;
    const y = homeScrollBeforeSheetRef.current;
    homeScrollBeforeSheetRef.current = null;
    if (y == null || y <= 0) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setAppScrollY(y);
      });
    });
  }, [selectedPreset]);
  // El flujo de plantilla ahora soporta multi-selección (lista de productos).
  // Mantenemos array vacío como "ninguno seleccionado todavía".
  const [selectedProducts, setSelectedProducts] =
    useState<MockProductV2[]>(initialNavProducts);
  /** La creación base del anuncio (ver `initialNavImagenBase`). */
  const [imagenBase, setImagenBase] = useState(initialNavImagenBase);
  // "Sin producto" (ADR-673) — solo el modo "Video con influencer": la
  // influencer habla a cámara sin mostrar nada. NO se modela como un producto
  // fantasma en `selectedProducts` (rompería todo lo que mapea ids a variantes):
  // es un flag aparte, y `selectedProducts` queda vacío.
  const [sinProducto, setSinProducto] = useState<boolean>(false);
  // Autogeneración del diálogo (ADR-674): al entrar a configurar con un producto
  // elegido pedimos el guion y lo dejamos escrito en el textarea. `dialogoForRef`
  // guarda la variante ya resuelta para no re-pedirlo al volver a esta pantalla
  // (pisaría lo que el usuario haya editado); cambiar de producto sí re-dispara.
  const [dialogoLoading, setDialogoLoading] = useState<boolean>(false);
  const [dialogoError, setDialogoError] = useState<string | null>(null);
  const dialogoForRef = useRef<string | null>(null);
  // `panelStep` controla qué muestra el panel lateral / bottom sheet:
  //   • choose-product → upload + ver productos (estado inicial)
  //   • pick-product   → grid de productos del usuario (inline en el panel)
  //   • configure      → opciones de Pose/Formato + Generar (después de elegir)
  // Antes había un ProductPickerSheet separado para `pick-product`; ahora
  // todo vive en el mismo panel.
  const [panelStep, setPanelStep] = useState<
    | 'preview'
    | 'choose-product'
    | 'pick-product'
    | 'upload-product'
    | 'select-vistas'
    | 'configure'
    | 'asistente'
  >(() => {
    // Deep-link: si entramos con un preset preseteado, arrancamos en
    // 'choose-product' para los modos "design" virtuales (no tienen
    // preview útil) y 'preview' para template / design (E-commerce).
    if (initialNavPreset) {
      const k = initialNavPreset.kind;
      if (k === 'asistente') return 'asistente';
      // Con el producto ya elegido afuera (ADR-2026-08-04-el-producto-entra-con-el-modo)
      // arrancamos donde dejaría `handleConfirmProducts`, que es lo que habría corrido
      // si el usuario lo hubiera elegido acá: los modos de catálogo siguen con sus
      // vistas, el resto va derecho a configurar.
      //
      // Infografía queda afuera del salto a propósito: no tiene picker de vistas y su
      // camino pasa por presetear una (ver `handleConfirmProducts`). Hoy no llega por
      // acá —el modo está apagado por flag y el sheet no lo ofrece—, así que se deja
      // en el flujo normal en vez de duplicar ese preseteo en un initializer.
      // Con una creación de base no hay producto que elegir: el sujeto ya está
      // (ADR-2026-08-05-el-anuncio-desde-una-creacion-usa-el-modo).
      if (initialNavImagenBase) return 'configure';
      if (initialNavProducts.length > 0 && k !== 'infografia') {
        return esModoCatalogo(k) ? 'select-vistas' : 'configure';
      }
      return k === 'design-modelo' ||
        k === 'design-auto' ||
        k === 'fotos-producto' ||
        k === 'catalogo-ia' ||
        k === 'catalogo-muebles' ||
        k === 'infografia' ||
        k === 'anuncio' ||
        k === 'video-influencer'
        ? 'choose-product'
        : 'preview';
    }
    return 'preview';
  });
  // State para el step 'upload-product' — archivos pickeados desde el
  // input y nombre que el user puede editar antes de confirmar.
  const [pendingProductFiles, setPendingProductFiles] = useState<File[]>([]);
  const [pendingProductName, setPendingProductName] = useState<string>('');
  const [uploadingProduct, setUploadingProduct] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Subida en bloque (catálogo): cuando el user sube 2+ archivos a la vez,
  // cada archivo es un producto distinto. Mientras corre el loop mostramos
  // un overlay de carga, sin la pantalla intermedia "Nuevo producto".
  const [bulkUploadingProducts, setBulkUploadingProducts] =
    useState<boolean>(false);
  // Lista de presets sobre los que se puede navegar en el step 'preview'.
  // Cuando el user toca un preset desde un row, prepop esta lista con los
  // presets de esa misma colección — así puede swipear / cambiar a otro
  // antes de comprometerse con "Utilizar".
  const [previewList, setPreviewList] = useState<PresetV2[]>(
    initialNavPresetList,
  );
  // Multi-select: cuando el user activa "Seleccionar varias" en preview,
  // junta varios templates de la misma colección. En `handleGenerateFinal`
  // estos ids se mandan como `template_id` array para que el backend
  // genere una imagen por cada plantilla. Solo aplica a kind 'template' —
  // los 'design' no soportan multi.
  const [multiPresetIds, setMultiPresetIds] = useState<string[]>([]);
  // Cantidad de imágenes a generar en modo 'design-auto'. Default 1.
  // El control con +/- se renderiza arriba del Generar en el
  // ConfigurePanel cuando el kind del preset es 'design-auto'. El
  // payload manda `cantidad_imagenes: autoImageCount`.
  const [autoImageCount, setAutoImageCount] = useState<number>(1);
  const AUTO_IMAGE_COUNT_MAX = 6;

  /**
   * Cuándo se aparta la barra inferior de mobile —
   * ADR-2026-08-04-el-modo-se-lleva-la-barra.
   *
   * En el inicio y en el PRIMER paso de un modo (elegir el producto) la barra queda:
   * el modo todavía es un lugar del que se sale cambiando de tab, y ese paso no tiene
   * ninguna acción fija abajo con la que competir — sólo dos botones en el flujo, que
   * le dejan el hueco (ver `ALTO_NAV_INFERIOR` en `PresetDetailSheet`).
   *
   * De ahí en adelante —la grilla de productos, configurar, el preview de plantillas,
   * el picker de vistas, el asistente— la pantalla monta su propia acción fija abajo
   * ("Confirmar", "Generar") o es una conversación a pantalla completa. La barra se
   * desliza afuera: dos barras apiladas dejaban el CTA a media pantalla, y una
   * navegación por encima de un flujo a medio hacer es una salida sin aviso.
   *
   * El shell es quien la monta, así que esto sólo levanta la bandera del contexto;
   * `NavInferiorMobile` la lee y se va con su animación.
   */
  const barraSeAparta = !!selectedPreset && panelStep !== 'choose-product';
  useEffect(() => {
    setMobileHideBottomNav(barraSeAparta);
    return () => setMobileHideBottomNav(false);
  }, [barraSeAparta, setMobileHideBottomNav]);

  // ── State del modo Anuncio ─────────────────────────────────────────
  // Cada campo textual del anuncio (Título, Descripción, Llamada a acción)
  // tiene 2 dimensiones:
  //   • `hidden` — el user descartó el campo (icono ojo). No va al anuncio.
  //   • `value`  — texto custom; si está vacío y `hidden` es false,
  //                queda implícito el modo "automático" (Atlas decide).
  // Reseteo total en `closePresetSheet` y en el cierre URL-driven.
  type AnuncioFieldState = {
    hidden: boolean;
    value: string;
    // Familia tipográfica elegida; '' = Automática (la IA elige).
    font: string;
  };
  const ANUNCIO_FIELD_DEFAULT: AnuncioFieldState = {
    hidden: false,
    value: '',
    font: '',
  };
  const [anuncioTitulo, setAnuncioTitulo] = useState<AnuncioFieldState>(
    ANUNCIO_FIELD_DEFAULT,
  );
  const [anuncioDescripcion, setAnuncioDescripcion] =
    useState<AnuncioFieldState>(ANUNCIO_FIELD_DEFAULT);
  const [anuncioCta, setAnuncioCta] = useState<AnuncioFieldState>(
    ANUNCIO_FIELD_DEFAULT,
  );
  // Hasta 4 hex strings — colores personalizados del anuncio. Si el array
  // está vacío, el AnuncioPanel cae al default del `brand_profile`
  // (primeros 4 colores de `brandColors`). Al tocar un slot se inicializa
  // a la copia del default antes de editarse, para que el user arranque
  // editando la paleta de la marca.
  const ANUNCIO_COLORS_MAX = 4;
  const [anuncioColors, setAnuncioColors] = useState<string[]>([]);
  // Default de tipografía de marca: cuando `brandFont` se resuelve, los 3
  // textos que sigan sin fuente elegida (`font === ''`) adoptan la de la
  // marca. One-shot (ref) para no pisar una elección posterior del user
  // — incluido si elige "Automática" a propósito. Los reset points
  // restablecen `font` a `brandFontRef.current` directamente.
  useEffect(() => {
    if (!brandFont || brandFontSeededRef.current) return;
    brandFontSeededRef.current = true;
    const seed = (s: AnuncioFieldState): AnuncioFieldState =>
      s.font ? s : { ...s, font: brandFont };
    setAnuncioTitulo(seed);
    setAnuncioDescripcion(seed);
    setAnuncioCta(seed);
  }, [brandFont]);

  // Cambio de marca EN VIVO. El sidebar / "Mi marca" disparan
  // `atlas:brand-changed` al cambiar la marca activa, pero InterfazV2
  // resuelve `current_brand_id` UNA sola vez al montar (effect de
  // `userCompanyId`). Sin esto la marca quedaba "pegada": el filtro de
  // productos Y los colores/fuente del anuncio seguían en la marca
  // anterior hasta re-entrar a la pantalla. Acá re-resolvemos la marca y
  // refrescamos `currentBrandId` (→ el effect de productos refetcha solo),
  // `brandColors` y `brandFont`. Mismo patrón que `useBrandProfile`.
  useEffect(() => {
    if (!userCompanyId) return;
    let alive = true;
    const onBrandChanged = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user || !alive) return;
      const { data: profile } = await supabase
        .from('profiles')
        .select('current_brand_id')
        .eq('id', user.id)
        .single();
      if (!alive) return;
      const newBrandId =
        ((profile as any)?.current_brand_id as string) || null;
      // Actualiza el filtro de productos (el effect de productos depende de
      // `currentBrandId` y refetcha la marca nueva + huérfanos).
      setCurrentBrandId(newBrandId);
      // Colores + fuente: si no hay marca activa, mismo fallback que el effect
      // de montaje → marca DEFAULT de la empresa (si no, queda vacío).
      let colorsBrandId = newBrandId;
      if (!colorsBrandId) {
        const { data: def } = await supabase
          .from('brand_profiles')
          .select('id')
          .eq('id_company', userCompanyId)
          .eq('is_default', true)
          .eq('is_draft', false)
          .maybeSingle();
        if (!alive) return;
        colorsBrandId = ((def as any)?.id as string) || null;
      }
      let nextColors: string[] = [];
      let nextFont = '';
      if (colorsBrandId) {
        const { data: brand } = await supabase
          .from('brand_profiles')
          .select('colors, fonts')
          .eq('id', colorsBrandId)
          .maybeSingle();
        if (!alive) return;
        nextColors = normalizeBrandHexColors((brand as any)?.colors);
        nextFont = resolveBrandFont((brand as any)?.fonts);
      }
      setBrandColors(nextColors);
      // Re-siembra de la fuente: los textos sin tocar (font '' o la fuente
      // vieja de marca) adoptan la nueva; los que el user eligió a mano se
      // respetan. `anuncioColors` NO se toca: si el user editó colores se
      // conservan; si están vacíos, la grilla cae sola a la paleta nueva.
      const oldFont = brandFontRef.current;
      const reseed = (s: AnuncioFieldState): AnuncioFieldState =>
        s.font === '' || s.font === oldFont ? { ...s, font: nextFont } : s;
      setAnuncioTitulo(reseed);
      setAnuncioDescripcion(reseed);
      setAnuncioCta(reseed);
      setBrandFont(nextFont);
    };
    window.addEventListener('atlas:brand-changed', onBrandChanged);
    return () => {
      alive = false;
      window.removeEventListener('atlas:brand-changed', onBrandChanged);
    };
  }, [userCompanyId]);

  // ── Restore post-generación (Atrás desde Imágenes Generadas) ─────────
  // En `handleGenerateFinal` antes de navegar persistimos el state
  // completo de la UI a `sessionStorage.atlas-v2-restore`. Al mount
  // leemos ese blob, aplicamos los setters "directos" (modelo, pose,
  // fondo, formato, etc) y guardamos los IDs que requieren lookup
  // async en este ref. Dos effects separados (uno por
  // `fetchedCollections`, otro por `userProducts`) resuelven el
  // preset abierto y los productos seleccionados cuando los catálogos
  // llegan. El blob es single-shot: se borra al leerlo.
  const pendingRestoreRef = useRef<{
    presetId?: string | null;
    selectedProductIds?: string[];
    multiSlotProductIds?: Record<string, string[]>;
  } | null>(null);
  // Estado de la auto-selección de pose por defecto (solo design-modelo):
  //   • 'pending' → todavía no aplicamos el default; cuando haya producto
  //                 elegido y el catálogo cargado, seleccionamos la primera
  //                 pose RECOMENDADA para ese producto.
  //   • 'applied' → ya aplicamos el default para el producto actual; no lo
  //                 re-pisamos en recargas async del catálogo.
  //   • 'manual'  → el user eligió una pose a mano (o vino de un restore);
  //                 no la tocamos.
  // Vuelve a 'pending' al cerrar el sheet y al confirmar/cambiar de
  // producto, así cada producto arranca con su pose recomendada.
  const poseAutoSelectRef = useRef<'pending' | 'applied' | 'manual'>(
    'pending',
  );
  // Overlay blanco que cubre el "home" mientras el restore async termina
  // de abrir el preset. El initializer es lazy y mira sessionStorage en
  // el PRIMER render — así el overlay está presente desde el primer
  // paint y nunca se ve el home. (Si dependiera del useEffect, el
  // primer render mostraría el home un frame antes del setState.)
  // El overlay `restoringPreset` solo se usa para el path de
  // sessionStorage (restore post-back desde Imágenes Generadas, donde
  // el preset no se conoce sincrónicamente). El path de deep-link via
  // navigation state ya inicializa `selectedPreset` / `previewList` /
  // `panelStep` con los datos sincrónicos del `history.state.usr`, así
  // que el sheet aparece desde el primer paint y no hace falta tapar
  // el home.
  const [restoringPreset, setRestoringPreset] = useState<boolean>(() => {
    try {
      const raw = sessionStorage.getItem('atlas-v2-restore');
      if (raw) {
        const blob = JSON.parse(raw);
        if (blob?.presetId) return true;
      }
    } catch {
      /* noop */
    }
    return false;
  });
  useEffect(() => {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem('atlas-v2-restore');
    } catch {
      /* sessionStorage puede fallar en modo privado */
    }
    if (!raw) return;
    try {
      const blob = JSON.parse(raw);
      if (blob.modelSelection) setModelSelection(blob.modelSelection);
      if (Array.isArray(blob.poseIds)) {
        // En "Imagen personalizada" (design-modelo) el restore post-generar
        // vuelve con UNA sola pose (la primera) y el toggle "Seleccionar
        // múltiples" apagado — generar de nuevo no debe multiplicar
        // imágenes sin un opt-in explícito. En los otros kinds (e-commerce
        // multi-preset) el array de poses ES el contrato del payload: se
        // restaura entero y se sincroniza el toggle para mantener la
        // invariante >1 pose ⇒ toggle ON.
        const isDesignModeloRestore = blob.presetId === 'imagen-con-modelo';
        const restoredPoseIds =
          isDesignModeloRestore && blob.poseIds.length > 1
            ? [blob.poseIds[0]]
            : blob.poseIds;
        setPoseIds(restoredPoseIds);
        setMultiPoseEnabled(restoredPoseIds.length > 1);
        // Restore = poses ya elegidas en la generación anterior; no las
        // pisamos con el default recomendado.
        poseAutoSelectRef.current = 'manual';
      }
      if (blob.fondoSelection) setFondoSelection(blob.fondoSelection);
      if (blob.formatoVal) setFormatoVal(blob.formatoVal);
      if (typeof blob.otraConfig === 'string') setOtraConfig(blob.otraConfig);
      if (typeof blob.multiModelEnabled === 'boolean')
        setMultiModelEnabled(blob.multiModelEnabled);
      if (Array.isArray(blob.modelSelectionsExtra))
        setModelSelectionsExtra(blob.modelSelectionsExtra);
      if (Array.isArray(blob.multiPresetIds))
        setMultiPresetIds(blob.multiPresetIds);
      pendingRestoreRef.current = {
        presetId: blob.presetId || null,
        selectedProductIds: Array.isArray(blob.selectedProductIds)
          ? blob.selectedProductIds
          : undefined,
        multiSlotProductIds:
          blob.multiSlotProductIds && typeof blob.multiSlotProductIds === 'object'
            ? blob.multiSlotProductIds
            : undefined,
      };
      if (blob.presetId) {
        setRestoringPreset(true);
        // Safety: si en 2.5s no resolvimos el preset (catálogo no
        // llegó o el id ya no existe), levantamos el overlay para no
        // dejar la UI bloqueada.
        window.setTimeout(() => setRestoringPreset(false), 2500);
      }
    } catch (err) {
      console.warn('[InterfazV2] restore parse error', err);
    }
    try {
      sessionStorage.removeItem('atlas-v2-restore');
    } catch {
      /* noop */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore async — preset abierto. Espera a que las colecciones lleguen
  // y abre el preset directo en step 'configure' (saltando preview /
  // choose-product, porque el user ya había configurado todo).
  // Usamos `displayCollections` (no `fetchedCollections`) porque incluye
  // la fila virtual "Diseña tu imagen" con el preset 'imagen-con-modelo'
  // — sino el restore desde el modo design-modelo no encuentra el
  // preset y la pantalla queda sin abrir.
  useEffect(() => {
    const pending = pendingRestoreRef.current;
    if (!pending?.presetId) return;
    if (displayCollections.length === 0) return;
    const found = displayCollections
      .flatMap((c) => c.presets ?? [])
      .find((p) => p.id === pending.presetId);
    if (found) {
      setSelectedPreset(found);
      setPanelStep('configure');
      pendingRestoreRef.current!.presetId = null;
    }
  }, [fetchedCollections, isDesignRubro]);

  // Restore async — productos seleccionados (single + multi por slot).
  // Espera a que el catálogo `userProducts` cargue, resuelve los IDs a
  // objetos MockProductV2 y los aplica. Se ignoran IDs no encontrados
  // (producto borrado / renombrado entre generaciones).
  useEffect(() => {
    const pending = pendingRestoreRef.current;
    if (!pending) return;
    if (userProducts.length === 0) return;
    if (pending.selectedProductIds && pending.selectedProductIds.length > 0) {
      const list = pending.selectedProductIds
        .map((id) => userProducts.find((p) => p.id === id))
        .filter((p): p is MockProductV2 => !!p);
      if (list.length > 0) setSelectedProducts(list);
      pending.selectedProductIds = undefined;
    }
    if (pending.multiSlotProductIds) {
      const resolved: Record<number, MockProductV2[]> = {};
      Object.entries(pending.multiSlotProductIds).forEach(([k, ids]) => {
        const list = ids
          .map((id) => userProducts.find((p) => p.id === id))
          .filter((p): p is MockProductV2 => !!p);
        if (list.length > 0) resolved[Number(k)] = list;
      });
      if (Object.keys(resolved).length > 0) setMultiSlotProducts(resolved);
      pending.multiSlotProductIds = undefined;
    }
  }, [userProducts]);

  // Animar entrada del preset sheet
  useEffect(() => {
    if (selectedPreset) {
      requestAnimationFrame(() => setPresetSheetAnimated(true));
    }
  }, [selectedPreset]);

  // El modo "Video con influencer" no usa escenas múltiples (el toggle está
  // oculto). Forzamos `videoMultiScene=false` al entrar para que un valor
  // heredado del flujo plantilla-video no duplique el costo del Generar. Ver
  // ADR-099.
  useEffect(() => {
    if (selectedPreset?.kind === 'video-influencer') setVideoMultiScene(false);
  }, [selectedPreset]);

  // Al cambiar de modo (o cerrarlo), limpiar las vistas del catálogo. Son
  // selección por sesión de modo: sin esto, las 3 vistas de muebles quedaban
  // colgadas en el estado y se filtraban al Catálogo IA (y al revés). Se hace
  // por `selectedPreset?.id` para cubrir TODOS los caminos de salida (cerrar,
  // cambiar de modo directo, deep-link), no solo `closePresetSheet`.
  // `catalogoVistas` no se restaura, así que resetear acá no pisa nada.
  // Las vistas de "Fotos de producto" van por el mismo camino y por la misma
  // razón: son selección por sesión de modo.
  useEffect(() => {
    setCatalogoVistas([]);
    setVistaIds([]);
  }, [selectedPreset?.id]);

  // Restore overlay: lo levantamos cuando el preset sheet ya animó
  // (presetSheetAnimated true) — así el user no ve nada del home detrás.
  useEffect(() => {
    if (restoringPreset && presetSheetAnimated) {
      setRestoringPreset(false);
    }
  }, [restoringPreset, presetSheetAnimated]);

  // Safety: si por algún motivo el deep-link no resuelve un preset (el
  // id no existe / la colección no llegó), bajamos el overlay después
  // de 2.5s para no dejar la pantalla en blanco indefinidamente.
  useEffect(() => {
    if (!restoringPreset) return;
    const t = window.setTimeout(() => setRestoringPreset(false), 2500);
    return () => window.clearTimeout(t);
  }, [restoringPreset]);

  // Si el user llega desde ColeccionDetalle vía
  // `navigate('/interfaz-v2', { state: { presetId, collectionId } })`,
  // abrimos automáticamente el sheet con la colección como previewList.
  //
  // Path "rápido": cuando `presetData` / `collectionPresets` vienen en el
  // state, el `useState` initializer ya abrió el sheet en el primer paint
  // — este effect solo se encarga de animar la entrada y limpiar el
  // state para que un refresh no re-abra.
  //
  // Path "fallback": si solo viene `presetId` (sin data), buscamos en
  // `displayCollections` cuando carguen y abrimos el sheet ahí. En este
  // path sí se ve el home por un momento — pero es el caso edge de
  // links externos / históricos.
  const appliedDeepLinkRef = useRef(false);
  useEffect(() => {
    if (appliedDeepLinkRef.current) return;
    const state = location.state as
      | {
          presetId?: string;
          collectionId?: string;
          presetData?: PresetV2;
          collectionPresets?: PresetV2[];
        }
      | null;
    if (!state?.presetId) return;
    // Si ya inicializamos sincrónicamente desde state (path rápido),
    // solo limpiamos el state y marcamos como aplicado.
    if (state.presetData) {
      appliedDeepLinkRef.current = true;
      // Animar la entrada — el sheet ya está montado desde el initializer.
      requestAnimationFrame(() => setPresetSheetAnimated(true));
      navigate(location.pathname, { replace: true, state: null });
      return;
    }
    // Fallback: lookup async esperando a que carguen las colecciones.
    const presetId = state.presetId;
    const collectionId = state.collectionId;
    const targetCollection =
      displayCollections.find((c) => c.id === collectionId) ||
      displayCollections.find((c) =>
        c.presets.some((p) => p.id === presetId),
      );
    if (!targetCollection) return;
    const preset = targetCollection.presets.find((p) => p.id === presetId);
    if (!preset) return;
    appliedDeepLinkRef.current = true;
    setSelectedPreset(preset);
    setPreviewList(targetCollection.presets);
    setPanelStep('preview');
    setPresetSheetAnimated(false);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, displayCollections, navigate]);

  const closePresetSheet = () => {
    const fromDeepLink = cameFromDeepLinkRef.current;
    cameFromDeepLinkRef.current = false;
    // Caso deep-link: el sheet es la ÚNICA cosa visible (el "home" de
    // V2 está oculto detrás). Si hacemos el cierre animado y después
    // `navigate(-1)`, durante los 300ms de la animación el home aparece
    // — feo. Mejor disparamos `navigate(-1)` directo y InterfazV2 se
    // desmonta entero. El browser/router cambia a la categoría sin
    // pasar por el home.
    if (fromDeepLink) {
      navigate(-1);
      return;
    }
    // Si el modo abierto vino de una URL (`/interfaz-v2/modo/...`), antes
    // de animar el cierre navegamos al home — así el browser back desde
    // afuera funciona y la URL queda consistente. El useEffect de sync
    // URL→state ve que la URL volvió al home y dispara el cierre animado.
    if (routeMode) {
      navigate(rutaHome);
      return;
    }
    setPresetSheetAnimated(false);
    setTimeout(() => {
      setSelectedPreset(null);
      setSelectedProducts([]);
      // La creación base se va con el panel: si no, el próximo anuncio abierto desde un
      // producto seguiría mandando `imagen_generada_id`.
      setImagenBase(null);
      setSinProducto(false);
      dialogoForRef.current = null;
      setDialogoLoading(false);
      setDialogoError(null);
      setPanelStep('preview');
      setPreviewList([]);
      setModelSelection(null);
      setModelPickerOpen(false);
      setFormatoVal(null);
      setFormatPickerOpen(false);
      setFondoVal(null);
      setOtraConfig('');
      setVideoGanchoId(null);
      setVideoVoiceGuion(getVoiceGuionDefault());
      // Vuelve al default del flujo plantilla-video — si quedara el 18s del
      // modo influencer, excedería el tope de 12s de ese slider.
      setVideoDuration(VIDEO_DURATION_DEFAULT);
      setPendingProductFiles([]);
      setPendingProductName('');
      setUploadingProduct(false);
      setUploadError(null);
      setMultiPresetIds([]);
      setPoseIds([]);
      setMultiPoseEnabled(false);
      // El estado multi-modelo también es por sesión de modo (ADR-513 solo
      // reseteaba poses): sin esto "varios modelos" y sus slots quedaban
      // prendidos al reabrir un modo y cruzaban modelo↔poses (poses de
      // varias personas con la UI de un solo modelo, o al revés).
      setMultiModelEnabled(false);
      setModelSelectionsExtra([]);
      setMultiSlotProducts({});
      poseAutoSelectRef.current = 'pending';
      setPosePickerOpen(false);
      setFondoSelection(null);
      setFondoPickerOpen(false);
      setFondoView('list');
      // Reset del state del modo Anuncio. La fuente vuelve a la de marca
      // (no a '') para mantener el default de tipografía entre anuncios.
      setAnuncioTitulo({ ...ANUNCIO_FIELD_DEFAULT, font: brandFontRef.current });
      setAnuncioDescripcion({
        ...ANUNCIO_FIELD_DEFAULT,
        font: brandFontRef.current,
      });
      setAnuncioCta({ ...ANUNCIO_FIELD_DEFAULT, font: brandFontRef.current });
      setAnuncioColors([]);
    }, 300);
  };

  // Sincronización URL → state para los 4 modos URL-routed
  // (`design-auto` / `design-modelo` / `catalogo-ia` / `asistente`).
  // Cuando la URL llega a `/interfaz-v2/modo/<slug>` (sea por click en
  // una card, deeplink externo o browser forward), abrimos el sheet del
  // modo correspondiente. Cuando la URL vuelve a `/interfaz-v2` (sea por
  // click en cerrar, browser back o navigate programático), animamos el
  // cierre del sheet.
  //
  // `pendingCloseRef` evita re-ejecutar el cleanup si el effect re-corre
  // durante los 300ms de animación de salida.
  const pendingCloseRef = useRef(false);
  useEffect(() => {
    if (pendingCloseRef.current) return;
    // El slug se traduce a kind y DESPUÉS se lo lleva al rubro de la marca activa —
    // ADR-2026-08-20-el-slug-catalogo-no-tiene-card-en-hogar-y-deco.
    //
    // "Generar catálogo con IA" son dos kinds según el rubro, y hay entradas que llegan
    // acá con el slug escrito a mano: el tab "Catálogo" de la barra inferior de mobile
    // apunta fijo a `/interfaz-v2/modo/catalogo` para todo rubro que no sea Moda, o sea
    // que a una marca de Hogar y Decoración la mandaba a `catalogo-ia`, que es
    // justamente el modo que ese rubro no tiene. La card salía `null`, el efecto se iba
    // por el `if (!card) return` de abajo —que está para ESPERAR a que el rubro
    // resuelva— y la espera no terminaba nunca: el tab quedaba activo sin abrir nada.
    //
    // Va acá y no en cada entrada porque los llamadores hardcodean el slug sin saber de
    // rubros (la barra sólo sabe si la marca es de Moda), y los deep-links no tienen a
    // quién parchear. `esMarcaHogarDeco` ya está en las deps del efecto, así que cuando
    // el rubro resuelve async esto se recalcula solo.
    const expectedKind = kindDeModoParaRubro(
      routeMode ? MODE_PATH_TO_KIND[routeMode] : null,
      esMarcaHogarDeco,
    );

    if (expectedKind) {
      // URL dice "abrir modo X". Buscar la card sintética del rubro.
      // Se pasó de cadena de ternarios a un mapa al sumar el 7º modo: con
      // `catalogo-muebles` la cadena quedaba con 7 niveles de anidado y era
      // ilegible. El comportamiento es idéntico (card o null).
      const CARD_POR_KIND: Record<string, PresetV2 | null> = {
        'design-auto': designAutoCard,
        'design-modelo': designModeloCard,
        'fotos-producto': fotosProductoCard,
        'catalogo-ia': catalogoIACard,
        'catalogo-muebles': catalogoMueblesCard,
        infografia: infografiaCard,
        asistente: asistenteCard,
        anuncio: anuncioCard,
        'video-influencer': videoInfluencerCard,
      };
      const card = CARD_POR_KIND[expectedKind] ?? null;
      // Si todavía no resolvió el rubro (cards null), esperamos a que
      // el useState async se resuelva — este effect re-corre con
      // `isDesignRubro` actualizado.
      if (!card) return;
      // Gating por plan (red de seguridad para deep-links / URL directa: los
      // taps ya gatean antes de navegar). Si el plan no alcanza, no abrimos el
      // modo: mostramos el modal y volvemos al home. Si el plan aún no cargó,
      // `getModePlanLock` devuelve null y dejamos pasar (backstop al generar).
      const routeModeLock = getModePlanLock(card.kind, userPlan);
      if (routeModeLock) {
        setFeatureLock(routeModeLock);
        navigate(rutaHome);
        return;
      }
      // Ya está abierto el modo correcto — nada que hacer.
      if (selectedPreset?.id === card.id) return;
      // Defaults del modo influencer al abrir: 18s de duración (el slider
      // va de 8 a 18; el formato default 'story' lo pone el effect de
      // auto-relleno de formato, que especial-casea este modo).
      if (card.kind === 'video-influencer') {
        setVideoDuration(VIDEO_INFLUENCER_DURATION_DEFAULT);
        setVideoVoiceGuion(getVoiceGuionDefault());
      }
      setSelectedPreset(card);
      setPreviewList([]);
      setPanelStep(card.kind === 'asistente' ? 'asistente' : 'choose-product');
      setPresetSheetAnimated(false);
      // La animación de entrada la dispara el useEffect que watcha
      // `selectedPreset` (más arriba).
    } else if (selectedPreset) {
      // URL volvió al home pero state tiene un modo abierto → cerrar.
      // Solo aplicamos esta lógica a los 4 modos URL-routed; templates
      // (`design`/`template`) se cierran por su propio path en
      // `closePresetSheet` y no son URL-driven.
      const isModeKind =
        selectedPreset.kind === 'design-auto' ||
        selectedPreset.kind === 'design-modelo' ||
        selectedPreset.kind === 'fotos-producto' ||
        selectedPreset.kind === 'catalogo-ia' ||
        selectedPreset.kind === 'catalogo-muebles' ||
        selectedPreset.kind === 'infografia' ||
        selectedPreset.kind === 'asistente' ||
        selectedPreset.kind === 'anuncio' ||
        selectedPreset.kind === 'video-influencer';
      if (!isModeKind) return;
      pendingCloseRef.current = true;
      setPresetSheetAnimated(false);
      setTimeout(() => {
        setSelectedPreset(null);
        setSelectedProducts([]);
      // La creación base se va con el panel: si no, el próximo anuncio abierto desde un
      // producto seguiría mandando `imagen_generada_id`.
      setImagenBase(null);
        setSinProducto(false);
        dialogoForRef.current = null;
        setDialogoLoading(false);
        setDialogoError(null);
        setPanelStep('preview');
        setPreviewList([]);
        setModelSelection(null);
        setModelPickerOpen(false);
        setFormatoVal(null);
        setFormatPickerOpen(false);
        setFondoVal(null);
        setOtraConfig('');
        setVideoGanchoId(null);
        setVideoVoiceGuion(getVoiceGuionDefault());
        // Mismo reset que closePresetSheet: el 18s del influencer no debe
        // filtrarse al flujo plantilla-video (tope 12s).
        setVideoDuration(VIDEO_DURATION_DEFAULT);
        setPendingProductFiles([]);
        setPendingProductName('');
        setUploadingProduct(false);
        setUploadError(null);
        setMultiPresetIds([]);
        setPoseIds([]);
        setMultiPoseEnabled(false);
        // Mismo reset que closePresetSheet: el multi-modelo es por sesión
        // de modo — ver el comentario de allá.
        setMultiModelEnabled(false);
        setModelSelectionsExtra([]);
        setMultiSlotProducts({});
        poseAutoSelectRef.current = 'pending';
        setPosePickerOpen(false);
        setFondoSelection(null);
        setFondoPickerOpen(false);
        setFondoView('list');
        // Reset del state del modo Anuncio. La fuente vuelve a la de marca
        // (no a '') para mantener el default de tipografía entre anuncios.
        setAnuncioTitulo({
          ...ANUNCIO_FIELD_DEFAULT,
          font: brandFontRef.current,
        });
        setAnuncioDescripcion({
          ...ANUNCIO_FIELD_DEFAULT,
          font: brandFontRef.current,
        });
        setAnuncioCta({ ...ANUNCIO_FIELD_DEFAULT, font: brandFontRef.current });
        setAnuncioColors([]);
        // Reset del chat del agente — si el sheet cerrado era el del
        // asistente, queremos arrancar conversación nueva la próxima vez.
        // Para los otros modos URL-driven es no-op (el chat sigue vacío).
        chat.reset();
        pendingCloseRef.current = false;
      }, 300);
    }
    // Deps mínimas: `routeMode` y de qué dependen las cards sintéticas.
    // `isDesignRubro` y `esMarcaHogarDeco` cubren su disponibilidad; el segundo
    // resuelve async (fetch del rubro de la marca), así que sin él un deep-link a
    // /modo/catalogo-muebles o /modo/infografia quedaría sin abrir
    // (ADR-2026-08-05-el-deep-link-de-un-modo-espera-a-su-rubro): entrando por URL
    // directa el rubro todavía no cargó, la card es `null`, el efecto sale por el
    // `if (!card) return` y sin el dep no volvía a correr nunca — la pantalla se
    // quedaba en la home con el ítem marcado en la sidebar.
    //
    // `brandRubro`/`userRubro` van además de `esMarcaHogarDeco` porque el
    // rediseño sumó cards y carátulas que dependen del rubro CRUDO, no sólo del
    // booleano de Hogar y Decoración.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeMode, isDesignRubro, esMarcaHogarDeco, brandRubro, userRubro]);

  // Confirmá el preset desde el step 'preview' → arranca el flujo normal
  // (choose-product). Permite que el user, en preview, scrollee y elija
  // "Utilizar" en otro preset distinto al que tocó originalmente.
  const handleUtilizarPreset = (preset: PresetV2) => {
    // Video de plantilla = Pro/Ultra. Si el toggle "Imagen/Video" está en video
    // y el plan no alcanza, NO avanzamos al selector de producto: mostramos el
    // modal de "función exclusiva" (mismo que el flujo de video influencer).
    // `userPlan &&` evita bloquear si el plan aún no se conoce (backstop al
    // generar). Cubre mobile y desktop: ambos usan este handler.
    if (
      previewMediaTab === 'video' &&
      preset.kind === 'template' &&
      userPlan &&
      !isProOrUltra(userPlan)
    ) {
      setShowVideoPlanRestriction(true);
      return;
    }
    setSelectedPreset(preset);
    setMultiPresetIds([]);
    setPanelStep('choose-product');
  };

  // Multi-select: el user eligió N plantillas en preview. Usamos la
  // primera como `selectedPreset` (anchor — drive del UI de configure),
  // y guardamos todos los ids en multiPresetIds. En generate, se mandan
  // todos como `template_id` array.
  const handleUtilizarMultiplePresets = (presets: PresetV2[]) => {
    if (presets.length === 0) return;
    // Mismo guard de video que el single (modo multi-plantilla en video).
    if (
      previewMediaTab === 'video' &&
      presets[0].kind === 'template' &&
      userPlan &&
      !isProOrUltra(userPlan)
    ) {
      setShowVideoPlanRestriction(true);
      return;
    }
    setSelectedPreset(presets[0]);
    setMultiPresetIds(presets.map((p) => p.id));
    // Para multi-select de 'design' (e-commerce): los presets envuelven
    // poses, así que pre-cargamos `poseIds` con todas las ids extraídas.
    // El payload builder en handleGenerateFinal manda `pose` como array,
    // que es lo que el backend espera para multi-pose en single-modelo.
    if (presets[0].kind === 'design') {
      const ids = presets
        .map((p) =>
          p.id.startsWith('pose-') ? p.id.slice('pose-'.length) : null,
        )
        .filter((id): id is string => !!id);
      if (ids.length > 0) {
        setPoseIds(ids);
        // Invariante >1 pose ⇒ toggle multi ON (por si algún flujo abre
        // el PosePicker con este set pre-cargado).
        if (ids.length > 1) setMultiPoseEnabled(true);
      }
    }
    setPanelStep('choose-product');
  };

  // Lock body scroll cuando el preset sheet está abierto. Sin esto, el
  // usuario puede inadvertidamente scrollear la página de fondo (la home
  // de Interfaz V2) mientras tiene el sheet open.
  //
  // Bonus: DashboardLayout envuelve la app en un div con `overflow-y-auto`
  // propio (mirá App.tsx). Lockear solo `body` no alcanza — ese div
  // sigue mostrando su scrollbar inutil detrás del sheet. Locamos también
  // el wrapper (es el firstElementChild de <body>).
  useEffect(() => {
    if (selectedPreset) {
      const bodyPrev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const dashboardWrapper = document.body
        .firstElementChild as HTMLElement | null;
      const dashboardPrev = dashboardWrapper?.style.overflow ?? '';
      if (dashboardWrapper) dashboardWrapper.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = bodyPrev;
        if (dashboardWrapper) dashboardWrapper.style.overflow = dashboardPrev;
      };
    }
  }, [selectedPreset]);

  // Intercepta el botón "Atrás" mobile (custom event del sidebar) para que
  // CIERRE el preset sheet en vez de navegar fuera de la página. Si no se
  // intercepta, el back lleva al usuario 2 pasos atrás porque la sheet es
  // una capa modal, no una ruta — así se sentía como "skip" de la home V2.
  useEffect(() => {
    if (selectedPreset) {
      // El back navega un step para atrás dentro del flow del sheet
      // en lugar de cerrarlo de una. design-modelo NO tiene 'preview'
      // (arranca directo en 'choose-product'), así que su flow es
      // distinto del de templates:
      //   • template:      preview ↔ choose-product ↔ configure
      //                                       ↘ upload-product ↗
      //   • design-modelo: choose-product ↔ configure
      //                                ↘ upload-product ↗
      // En la raíz de cada flow el back cierra el sheet.
      setMobileBackOverride(() => () => {
        const noPreviewKind =
          selectedPreset?.kind === 'design-modelo' ||
          selectedPreset?.kind === 'design-auto' ||
          selectedPreset?.kind === 'fotos-producto' ||
          selectedPreset?.kind === 'catalogo-ia' ||
          selectedPreset?.kind === 'catalogo-muebles' ||
          selectedPreset?.kind === 'infografia' ||
          selectedPreset?.kind === 'anuncio' ||
          // video-influencer NO tiene 'preview' (arranca directo en
          // 'choose-product', igual que los modos de diseño) → atrás cierra
          // el sheet en vez de ir a la pantalla extra tipo plantilla. ADR-508.
          selectedPreset?.kind === 'video-influencer';
        if (panelStep === 'configure') {
          // catalogo-ia y catalogo-muebles: configure es el "review de cards"
          // y atrás va al picker de vistas. Otros kinds: a choose-product.
          setPanelStep(
            selectedPreset?.kind === 'catalogo-ia' ||
              selectedPreset?.kind === 'catalogo-muebles'
              ? 'select-vistas'
              : 'choose-product',
          );
        } else if (panelStep === 'select-vistas') {
          setPanelStep('choose-product');
        } else if (panelStep === 'upload-product') {
          // Descartar el upload en progreso al volver — igual que el viejo
          // link "Volver" interno (que llamaba handleCancelUpload). Ahora
          // ese link está oculto y el back lo da la top bar.
          setPendingProductFiles([]);
          setPendingProductName('');
          setUploadError(null);
          setPanelStep('choose-product');
        } else if (panelStep === 'pick-product') {
          // En mobile el grid de productos es una pantalla full-screen
          // POR DEBAJO del chrome (back/menu siguen visibles). El back de la
          // top bar vuelve al origen: configure si se abrió con "Añadir
          // producto al outfit", o la pantalla de elegir producto si no.
          setPanelStep(
            pickOriginRef.current === 'configure'
              ? 'configure'
              : 'choose-product',
          );
        } else if (panelStep === 'choose-product') {
          if (noPreviewKind) {
            closePresetSheet();
          } else {
            setPanelStep('preview');
          }
        } else {
          closePresetSheet();
        }
      });
      // Vacía el título de la top bar mientras el sheet está abierto, así
      // solo se ve el botón de volver (sin "Interfaz V2" centrado).
      setMobileTitleOverride(' ');
      // En step 'preview' y 'choose-product' la imagen del preset ocupa
      // toda la pantalla (preview) o la parte superior (choose-product),
      // así que pedimos al header mobile que se vuelva transparente y
      // los botones tomen fondo blanco circular semi-opaco para flotar
      // encima.
      setMobileHeaderTransparent(
        panelStep === 'preview' ||
          panelStep === 'choose-product' ||
          panelStep === 'pick-product' ||
          panelStep === 'upload-product' ||
          panelStep === 'select-vistas' ||
          panelStep === 'configure' ||
          panelStep === 'asistente',
      );
      // Toggle Imagen/Video en el centro del header — solo en preview.
      // El modo asistente tiene su propio toggle inline en el sheet, así
      // que no duplicamos en el chrome.
      if (panelStep === 'preview') {
        // Tipografía igual al "Generar con mi producto"
        // (text-[15.5px] font-semibold). El indicador azul es un div
        // absoluto que se desliza con translateX entre las dos
        // posiciones — los buttons solo cambian el color del texto.
        const tabBaseClass =
          'relative z-10 inline-flex items-center justify-center px-4 h-8 rounded-full text-[15.5px] font-semibold transition-colors duration-300';
        setMobileHeaderCenter(
          <div className="relative inline-grid grid-cols-2 p-0.5 rounded-full bg-white/95 backdrop-blur-md shadow-md ring-1 ring-black/[0.05]">
            {/* Indicador azul deslizable */}
            <div
              className="absolute top-0.5 bottom-0.5 left-0.5 right-1/2 rounded-full bg-brand-primary transition-transform duration-300 ease-out"
              style={{
                transform:
                  previewMediaTab === 'imagen'
                    ? 'translateX(0)'
                    : 'translateX(100%)',
              }}
              aria-hidden
            />
            <button
              type="button"
              onClick={() => setPreviewMediaTab('imagen')}
              className={`${tabBaseClass} ${
                previewMediaTab === 'imagen'
                  ? 'text-white'
                  : 'text-brand-black/65'
              }`}
            >
              Imagen
            </button>
            <button
              type="button"
              onClick={() => setPreviewMediaTab('video')}
              className={`${tabBaseClass} ${
                previewMediaTab === 'video'
                  ? 'text-white'
                  : 'text-brand-black/65'
              }`}
            >
              Video
            </button>
          </div>,
        );
      } else {
        setMobileHeaderCenter(null);
      }
      return () => {
        setMobileBackOverride(null);
        setMobileTitleOverride(null);
        setMobileHeaderTransparent(false);
        setMobileHeaderCenter(null);
      };
    }
    // Home de Interfaz V2 mobile (sin preset abierto): header
    // transparente sin back ni logo. El menu queda como pill blanca
    // circular flotando sobre el contenido — mismo chrome que las
    // pantallas internas del flow del preset, para que la transición
    // entre la home y el sheet sea continua.
    setMobileHeaderTransparent(true);
    setMobileHideBack(true);
    return () => {
      setMobileHeaderTransparent(false);
      setMobileHideBack(false);
    };
  }, [
    selectedPreset,
    panelStep,
    previewMediaTab,
    setMobileBackOverride,
    setMobileTitleOverride,
    setMobileHeaderTransparent,
    setMobileHeaderCenter,
    setMobileHideBack,
  ]);

  // Configuración final
  // `modelSelection` reemplazó al antiguo `modeloVal: string | null`. Es
  // un struct con `mode: 'auto' | 'preset' | 'custom' | 'upload'` para
  // que el config panel pueda mostrar el chip con la info correcta y la
  // generación pueda usar los detalles (custom fields, preset id, etc.).
  // Arranca con el modelo que vino en el `state` de la navegación, si lo hubo
  // (ADR-2026-08-04-desde-un-modelo-se-elige-el-modo); si no, en `null` y lo completa
  // el efecto de defaults con el de la última generación.
  const [modelSelection, setModelSelection] = useState<ModelSelection | null>(
    initialNavModel,
  );
  // ── Modo "varios modelos" (UI-only por ahora, sin payload backend) ──
  // El user puede activarlo desde el ModelPicker. Cuando está activo, el
  // picker muestra varios containers (slot principal = modelSelection,
  // slots extra = modelSelectionsExtra). Hasta 4 modelos en total.
  const [modelSelectionsExtra, setModelSelectionsExtra] = useState<
    (ModelSelection | null)[]
  >([]);
  const [multiModelEnabled, setMultiModelEnabled] = useState<boolean>(false);
  // En mobile el botón "Modelo" abre un bottom sheet (esta state). En
  // desktop se usa para abrir el sheet como side panel cuando el user
  // toca "Creá tu modelo" o "Cargá tu modelo" desde el inline picker.
  const [modelPickerOpen, setModelPickerOpen] = useState<boolean>(false);
  // Cuando el inline (desktop) está en sub-list de un slot multi y el
  // user toca Crea/Carga, delegamos al sheet pasando este idx. El sheet
  // arranca con view = 'create'/'upload' y editingMultiIdx = este idx.
  const [modelPickerInitialEditingIdx, setModelPickerInitialEditingIdx] =
    useState<number | null>(null);
  // ── Selección de productos por slot (modo varios modelos) ──
  // Cuando el user toca "Utilizando" / miniaturas en un slot, abrimos
  // el MultiSlotProductPickerSheet con `productPickerForSlot` seteado
  // al idx correspondiente. Al confirmar, los productos quedan en
  // `multiSlotProducts[idx]`. Si un slot no tiene entrada en este
  // record, hereda visualmente del `selectedProducts` global.
  const [productPickerOpen, setProductPickerOpen] = useState<boolean>(false);
  const [productPickerForSlot, setProductPickerForSlot] = useState<
    number | null
  >(null);
  const [multiSlotProducts, setMultiSlotProducts] = useState<
    Record<number, MockProductV2[]>
  >({});
  // Vista inicial del sheet cuando se abre — permite entrar directo a
  // 'create' o 'upload' (desktop design-modelo) sin pasar por 'list'.
  const [modelPickerInitialView, setModelPickerInitialView] =
    useState<ModelPickerView>('list');
  // Formato ahora se guarda como id (ej: 'square', 'story') de
  // FORMAT_OPTIONS_V2. El display label se computa con getFormatLabel.
  const [formatoVal, setFormatoVal] = useState<string | null>(null);
  // Resolución de salida de los modos con selector (imagen automática e imagen con
  // modelo). Arranca SIEMPRE en la barata: nadie gasta de más por no haber tocado
  // nada. Ver ADR-2026-08-25-resolucion-de-generacion-2k-4k.
  const [resolucionVal, setResolucionVal] =
    useState<ResolucionImagen>(RESOLUCION_DEFAULT);
  const [formatPickerOpen, setFormatPickerOpen] = useState<boolean>(false);
  const [otraConfig, setOtraConfig] = useState('');
  // Solo aplica al kind 'design' (fila "Diseña tu imagen"). En 'template'
  // no se renderiza y el state queda en null.
  const [fondoVal, setFondoVal] = useState<string | null>(null);

  // ── Selecciones del modo 'catalogo-ia' ───────────────────────────────
  // Vistas elegidas en el step `select-vistas`. El user puede mezclar
  // poses con-modelo (subset de `allPoses`, solo 1 persona) con vistas
  // solo-producto (del catálogo `VISTAS_SOLO_PRODUCTO`). El backend
  // recibe una llamada V3 por cada (vista × producto) compatible —
  // ver `handleGenerateCatalog`.
  const [catalogoVistas, setCatalogoVistas] = useState<VistaCatalogo[]>([]);
  // ── Selecciones del modo 'fotos-producto' ────────────────────────────
  // Slugs de las vistas solo-producto elegidas (`VISTAS_SOLO_PRODUCTO`).
  // Cada vista es UNA imagen: la generación manda un request por vista
  // (ver ADR-2026-08-13-fotos-producto-fan-out-por-vista). El fondo, el
  // formato y la instrucción adicional reusan el state compartido
  // (`fondoSelection` / `formatoVal` / `otraConfig`).
  const [vistaIds, setVistaIds] = useState<string[]>([]);
  // ── Selecciones del modo 'design-modelo' ─────────────────────────────
  // Poses (multi-select) y fondo (auto / biblioteca / guardado /
  // descripción). Solo se usan cuando `selectedPreset.kind === 'design-modelo'`.
  const [poseIds, setPoseIds] = useState<string[]>([]);
  // Toggle "Seleccionar múltiples" del menú de poses (design-modelo).
  // Default OFF: cada tap reemplaza la pose. Solo con ON el user puede
  // acumular varias (1 imagen generada por pose). Mismo patrón que
  // `multiPoseToggle` de PruebaNuevaInterfaz. Invariante:
  // poseIds.length > 1 ⇒ multiPoseEnabled (la UI no permite acumular
  // con el toggle apagado; los set programáticos lo sincronizan).
  const [multiPoseEnabled, setMultiPoseEnabled] = useState<boolean>(false);
  const [posePickerOpen, setPosePickerOpen] = useState<boolean>(false);
  // Poses personalizadas del cliente (tab "Mis poses" del PosePicker).
  const [customPoses, setCustomPoses] = useState<PoseV2[]>([]);
  const [uploadingPose, setUploadingPose] = useState<boolean>(false);
  // Carga las poses custom de la empresa (las globales viven en `allPoses`).
  useEffect(() => {
    if (!userCompanyId) return;
    let cancelled = false;
    (async () => {
      const mine = await fetchMyCustomPoses(userCompanyId);
      if (!cancelled) setCustomPoses(mine);
    })();
    return () => {
      cancelled = true;
    };
  }, [userCompanyId]);
  // Subir una pose: dispara el flujo del backend y refetchea. GRATIS desde
  // ADR-2026-08-03-subir-pose-es-gratis — por eso no dispara
  // `atlas:credits-updated` ni hace falta avisar un precio en el PosePicker.
  const handleUploadPose = async (file: File) => {
    if (!userCompanyId) return;
    setUploadingPose(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Sesión no encontrada');
      await uploadCustomPose({
        userId: user.id,
        idCompany: userCompanyId,
        file,
        cantidadPersonas: expectedPeopleInPose,
      });
      setCustomPoses(await fetchMyCustomPoses(userCompanyId));
    } catch (e) {
      console.error('[InterfazV2] handleUploadPose error', e);
      alert(
        'No pudimos procesar la pose. Probá de nuevo con otra foto.',
      );
    } finally {
      setUploadingPose(false);
    }
  };
  // Borrar una pose propia: saca la fila + la imagen, y la deselecciona.
  const handleDeletePose = async (pose: PoseV2) => {
    if (!userCompanyId) return;
    try {
      await deleteCustomPose({
        id: pose.id,
        idCompany: userCompanyId,
        filename: pose.filename,
      });
      setCustomPoses((prev) => prev.filter((p) => p.id !== pose.id));
      setPoseIds((prev) => prev.filter((id) => id !== pose.id));
      // También quitarla de las vistas del catálogo si estaba elegida.
      setCatalogoVistas((prev) =>
        prev.filter((v) => !(v.kind === 'mis-pose' && v.id === pose.id)),
      );
    } catch (e) {
      console.error('[InterfazV2] handleDeletePose error', e);
    }
  };
  // Subir una pose desde el CATÁLOGO (paso "Elegí las vistas"). Igual que
  // handleUploadPose pero forzando 1 persona (catálogo = 1 modelo) y
  // auto-seleccionando la pose creada como vista, para que el user solo
  // tenga que generar.
  const handleCatalogUploadPose = async (file: File) => {
    if (!userCompanyId) return;
    setUploadingPose(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Sesión no encontrada');
      const beforeIds = new Set(customPoses.map((p) => p.id));
      await uploadCustomPose({
        userId: user.id,
        idCompany: userCompanyId,
        file,
        cantidadPersonas: 1,
      });
      const refreshed = await fetchMyCustomPoses(userCompanyId);
      setCustomPoses(refreshed);
      // Auto-seleccionar la(s) pose(s) nueva(s) como vista del catálogo.
      const nuevas = refreshed.filter(
        (p) => !beforeIds.has(p.id) && (p.cantidadPersonas ?? 1) === 1,
      );
      if (nuevas.length > 0) {
        setCatalogoVistas((prev) => {
          const yaEsta = new Set(prev.map((v) => `${v.kind}:${v.id}`));
          const add: VistaCatalogo[] = nuevas
            .filter((p) => !yaEsta.has(`mis-pose:${p.id}`))
            .map((p) => ({
              id: p.id,
              nombre: p.nombre,
              imageUrl: p.imageUrl,
              kind: 'mis-pose' as const,
              filename: p.filename,
            }));
          return [...prev, ...add];
        });
      }
    } catch (e) {
      console.error('[InterfazV2] handleCatalogUploadPose error', e);
      alert(
        'No pudimos procesar la pose. Probá de nuevo con otra foto.',
      );
    } finally {
      setUploadingPose(false);
    }
  };
  const [fondoSelection, setFondoSelection] = useState<FondoSelection | null>(
    null,
  );
  const [fondoPickerOpen, setFondoPickerOpen] = useState<boolean>(false);
  // Vista inicial del FondoPickerSheet — permite entrar directo a
  // 'describe' o 'upload' desde el inline picker en desktop.
  const [fondoPickerInitialView, setFondoPickerInitialView] =
    useState<FondoPickerView>('list');
  // Categoría inicial de la vista 'all' — seteada al tocar una card de
  // categoría en el carrusel inline desktop (abre el sheet lateral
  // directo en esa categoría).
  const [fondoPickerInitialCategory, setFondoPickerInitialCategory] =
    useState<string | null>(null);
  // View del FondoPicker en desktop inline — controlada por el padre
  // igual que la del ModelPickerInline para que el ConfigurePanel pueda
  // esconder otras secciones cuando el user está en describe/upload.
  const [fondoView, setFondoView] = useState<FondoPickerView>('list');
  // Catálogo de fondos — biblioteca pública filtrada por rubro + los
  // subidos por el user. Se fetchean cuando el user abre un preset
  // design-modelo (lazy) para no pagar el query si no aplica.
  const [bibliotecaFondos, setBibliotecaFondos] = useState<FondoBiblioteca[]>(
    [],
  );
  const [fondosGuardados, setFondosGuardados] = useState<FondoGuardado[]>([]);
  const refreshFondosGuardados = async () => {
    if (!userCompanyId) return;
    const list = await fetchFondosGuardados(userCompanyId);
    setFondosGuardados(list);
  };
  useEffect(() => {
    if (!isDesignRubro || !userCompanyId) return;
    (async () => {
      const [biblio, guardados] = await Promise.all([
        // Sin filtro de rubro — mostramos toda la biblioteca de fondos
        // para que el user pueda ver todas las categorías en "Ver más".
        fetchBibliotecaFondos(null),
        fetchFondosGuardados(userCompanyId),
      ]);
      setBibliotecaFondos(biblio);
      setFondosGuardados(guardados);
    })();
  }, [isDesignRubro, userCompanyId, userRubro]);

  // Auto-rellenar formato/modelo/fondo/pose con los de la última
  // generación cuando el user abre un preset sheet. Copiado del flujo
  // de PruebaNuevaInterfaz: usamos el storage compartido
  // `loadLastCrearAnuncioState` para que ambas pantallas mantengan
  // continuidad. Fallbacks hardcodeados cuando no hay nada en storage:
  //   • formato → 'post'
  //   • modelo  → null (el user opta in eligiendo una card)
  //   • fondo   → { mode: 'auto' } (solo en design-modelo)
  //   • pose    → primera pose del catálogo (manejado en otro effect
  //               porque depende de `allPoses` async)
  //
  // Si el user ya tocó algo (no-null), no pisamos su elección. El
  // effect se re-dispara cuando userModels / bibliotecaFondos /
  // fondosGuardados se llenan vía fetch async, por si el restore
  // necesita un lookup por id.
  useEffect(() => {
    if (!selectedPreset) return;
    const isDesignModelo = selectedPreset.kind === 'design-modelo';
    const lastState = loadLastCrearAnuncioState();

    // ─── Formato ───────────────────────────────────────────────────
    if (formatoVal === null) {
      if (selectedPreset.kind === 'video-influencer') {
        // Video con influencer: default fijo Historias/Reels (9:16) — es un
        // video vertical UGC; no restauramos el formato de la última
        // generación de imagen (suele ser 'post'/'square' y no aplica acá).
        setFormatoVal('story');
      } else {
        const fromLast = lastState?.formato;
        const fromLegacy = (() => {
          try {
            return localStorage.getItem('atlas-v2-last-format');
          } catch {
            return null;
          }
        })();
        setFormatoVal(fromLast || fromLegacy || 'post');
      }
    }

    // ─── Modelo ────────────────────────────────────────────────────
    // Si la plantilla declara `inputs_aceptados` sin "modelo", no
    // restauramos modelo (la UI lo esconde y el payload no lo manda).
    const templateAcceptsModelo =
      !selectedPreset.inputsAceptados ||
      selectedPreset.inputsAceptados.includes('modelo');
    if (modelSelection === null && templateAcceptsModelo) {
      const m = lastState?.modelo;
      if (m?.mode === 'ia') {
        setModelSelection({ mode: 'auto' });
      } else if (m?.mode === 'personalizado') {
        setModelSelection({
          mode: 'custom',
          fields: {
            genero: m.genero,
            edad: m.edad,
            tipoCuerpo: m.tipoCuerpo,
            tonoPiel: m.tonoPiel,
            colorPelo: m.colorPelo,
            largoPelo: m.largoPelo,
            descripcion: m.descripcion,
          },
        });
      } else if (m?.mode === 'guardados' && m.guardadoId) {
        const found = userModels.find(
          (um) => String(um.id) === String(m.guardadoId),
        );
        if (found) {
          setModelSelection({
            mode: 'preset',
            id: String(found.id),
            name: found.nombre,
            imageUrl: found.thumbnailUrl,
          });
        }
        // Si userModels aún no cargó (length 0), no hacemos nada — el
        // effect se re-dispara cuando llega y vuelve a intentar.
      } else {
        // Fallback al legacy key V2 (solo si nada en storage compartido).
        try {
          const raw = localStorage.getItem('atlas-v2-last-model');
          if (raw) {
            const parsed = JSON.parse(raw) as ModelSelection;
            if (
              parsed &&
              (parsed.mode === 'auto' ||
                parsed.mode === 'preset' ||
                parsed.mode === 'custom' ||
                parsed.mode === 'upload')
            ) {
              setModelSelection(parsed);
            }
          }
        } catch {
          /* noop */
        }
      }
    }

    // ─── Fondo (fotos-producto) ────────────────────────────────────
    // Default FIJO en blanco, sin restaurar el de la última generación: el
    // modo existe para fotos de catálogo y ese es su punto de partida
    // esperado. Si el user quiere una escena, la describe en el picker.
    if (selectedPreset.kind === 'fotos-producto' && fondoSelection === null) {
      setFondoSelection({ mode: 'blanco' });
    }

    // ─── Fondo (solo design-modelo) ────────────────────────────────
    // Los modos que requieren lookup (elegido, guardado) esperan a que
    // el catálogo cargue antes de fallback-ear a auto — si seteamos
    // auto ahora y la lista llega después, perdemos la restauración.
    if (isDesignModelo && fondoSelection === null) {
      const bg = lastState?.background;
      if (bg?.mode === 'ia' || !bg?.mode) {
        setFondoSelection({ mode: 'auto' });
      } else if (bg.mode === 'blanco') {
        setFondoSelection({ mode: 'blanco' });
      } else if (bg.mode === 'descripcion' && bg.text) {
        setFondoSelection({ mode: 'descripcion', text: bg.text });
      } else if (bg.mode === 'elegido' && bg.id) {
        const found = bibliotecaFondos.find((f) => f.id === bg.id);
        if (found) {
          setFondoSelection({
            mode: 'biblioteca',
            id: found.id,
            name: found.nombre,
            imageUrl: found.imageUrl,
          });
        } else if (bibliotecaFondos.length > 0) {
          // Catálogo cargado y el id no existe → fallback.
          setFondoSelection({ mode: 'auto' });
        }
      } else if (bg.mode === 'guardado' && bg.fileName) {
        const found = fondosGuardados.find((f) => f.fileName === bg.fileName);
        if (found) {
          setFondoSelection({
            mode: 'guardado',
            fileName: found.fileName,
            name: found.nombre,
            imageUrl: found.imageUrl,
          });
        } else if (fondosGuardados.length > 0) {
          setFondoSelection({ mode: 'auto' });
        }
      } else {
        setFondoSelection({ mode: 'auto' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPreset, userModels, bibliotecaFondos, fondosGuardados]);

  // ── Modo 'fotos-producto': catálogo de vistas de los productos elegidos ──
  // Las vistas compatibles dependen del `model_view` de CADA producto (un
  // zapato no tiene "maniquí invisible"). Con varios productos se ofrece la
  // UNIÓN anotada con `productIds` — a qué productos aplica cada vista
  // (ADR-2026-08-30-fotos-producto-multi-producto-2): la generación produce
  // una imagen por cada par (producto, vista) aplicable y activo.
  const vistasProducto = useMemo(
    () =>
      selectedPreset?.kind === 'fotos-producto'
        ? vistasSoloProductoParaConjunto(
            selectedProducts.map((p) => ({
              id: p.id,
              modelView: p.modelView,
            })),
          )
        : [],
    [selectedPreset?.kind, selectedProducts],
  );
  // Si el user cambia de producto después de elegir vistas, las que ya no
  // aplican a la nueva categoría se caen solas. Sin esto quedaban en el state
  // (invisibles en la grilla) y viajaban igual al backend.
  useEffect(() => {
    if (selectedPreset?.kind !== 'fotos-producto') return;
    setVistaIds((prev) => {
      const validos = new Set(vistasProducto.map((v) => v.id));
      const next = prev.filter((id) => validos.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [vistasProducto, selectedPreset?.kind]);
  // Productos DESACTIVADOS por vista (checklist del badge de la grilla,
  // ADR-2026-08-30-fotos-producto-multi-producto-2). Default: todos activos.
  // Keyed por slug de vista; las entradas de vistas deseleccionadas se
  // conservan (re-elegir la vista recuerda la elección).
  const [vistaProductosExcluidos, setVistaProductosExcluidos] = useState<
    Record<string, string[]>
  >({});
  // Poda: un producto que sale de la selección global no puede quedar
  // excluido en ninguna vista (volvería "muerto" si se re-agrega).
  useEffect(() => {
    if (selectedPreset?.kind !== 'fotos-producto') return;
    const validos = new Set(selectedProducts.map((p) => p.id));
    setVistaProductosExcluidos((prev) => {
      let cambio = false;
      const next: Record<string, string[]> = {};
      for (const [vistaId, ids] of Object.entries(prev)) {
        const filtrados = ids.filter((id) => validos.has(id));
        if (filtrados.length !== ids.length) cambio = true;
        if (filtrados.length > 0) next[vistaId] = filtrados;
        else if (ids.length > 0) cambio = true;
      }
      return cambio ? next : prev;
    });
  }, [selectedProducts, selectedPreset?.kind]);
  // Activar/desactivar un producto para UNA vista. El último activo no se
  // puede desactivar: cada vista seleccionada genera al menos una imagen
  // (para no generar ninguna, se desmarca la vista).
  const handleToggleVistaProducto = (vistaId: string, productId: string) => {
    const vista = vistasProducto.find((v) => v.id === vistaId);
    if (!vista) return;
    setVistaProductosExcluidos((prev) => {
      const excluidos = prev[vistaId] ?? [];
      if (excluidos.includes(productId)) {
        const next = excluidos.filter((id) => id !== productId);
        return { ...prev, [vistaId]: next };
      }
      const activos = vista.productIds.filter(
        (id) => !excluidos.includes(id),
      );
      if (activos.length <= 1) return prev; // último activo: no se toca
      return { ...prev, [vistaId]: [...excluidos, productId] };
    });
  };
  // Total de imágenes de la tanda = Σ por vista elegida de sus productos
  // activos. Alimenta el pill del Generar, el guard de créditos (ADR-518) y
  // el contador del panel.
  const fotosProductoTotalImagenes = useMemo(() => {
    if (selectedPreset?.kind !== 'fotos-producto') return 0;
    const porId = new Map(vistasProducto.map((v) => [v.id, v]));
    return vistaIds.reduce((acc, vistaId) => {
      const vista = porId.get(vistaId);
      if (!vista) return acc;
      const excluidos = vistaProductosExcluidos[vistaId] ?? [];
      return (
        acc + vista.productIds.filter((id) => !excluidos.includes(id)).length
      );
    }, 0);
  }, [
    selectedPreset?.kind,
    vistasProducto,
    vistaIds,
    vistaProductosExcluidos,
  ]);

  // (La preselección de pose por defecto vive más abajo, después de
  //  `allPosesFiltered`, porque ahora toma la primera pose RECOMENDADA.)

  // Cantidad de modelos activos: en single mode siempre 1; en multi
  // mode = 1 (slot principal) + cantidad de extras.
  const expectedPeopleInPose = multiModelEnabled
    ? 1 + modelSelectionsExtra.length
    : 1;

  // Catálogo de poses filtrado por la cantidad de personas que
  // matchea la cantidad de modelos. Las poses sin `cantidad_personas`
  // explícito caen como 1 desde el mapper (ver posesApi).
  //
  // Además, ordenamos las poses RECOMENDADAS para el producto primero: si
  // elegiste una remera (model_view "torso"), van primero las poses que
  // cubren el torso. Misma lógica que PruebaNuevaInterfaz: una pose es
  // recomendada si sus `areasPrincipales` incluyen el/los model_view de los
  // productos ('cuerpo_completo' = torso + piernas). Dentro del conjunto
  // preferido, "Editorial de pie" es el default; y si el producto no tiene
  // model_view (o ninguna pose lo cubre) caemos a cuerpo completo — nunca a
  // un close-up arbitrario. (Ver detalle dentro del memo.)
  const allPosesFiltered = React.useMemo(() => {
    const base = allPoses.filter(
      (p) => p.cantidadPersonas === expectedPeopleInPose,
    );
    const modelViews = selectedProducts
      .map((p) => (p.modelView || '').trim())
      .filter((mv) => mv.length > 0);
    const expand = (mv: string): string[] =>
      mv === 'cuerpo_completo' ? ['torso', 'piernas'] : [mv];
    const isFullBody = (pose: PoseV2) =>
      pose.categoria === 'cuerpo_completo' ||
      (pose.areasPrincipales.includes('torso') &&
        pose.areasPrincipales.includes('piernas'));

    // Conjunto "preferido" que va primero en la lista → su primer ítem es
    // el default que selecciona el effect de abajo.
    //   • Con modelView → poses que cubren las áreas del/los producto(s)
    //     (misma lógica que PruebaNuevaInterfaz).
    //   • Sin modelView → poses de cuerpo completo.
    // Si el preferido queda vacío (producto con modelView que ninguna pose
    // cubre), caemos a cuerpo completo igual — así el default NUNCA arranca
    // en un close-up arbitrario (la primera por `created_at` es
    // "Close-up superior", que incluye 'torso' y se veía como "la del
    // cuello").
    const isRecommended = (pose: PoseV2) =>
      modelViews.every((mv) =>
        expand(mv).every((part) => pose.areasPrincipales.includes(part)),
      );
    let preferred =
      modelViews.length === 0 ? base.filter(isFullBody) : base.filter(isRecommended);
    if (preferred.length === 0) {
      preferred = base.filter(isFullBody);
      if (preferred.length === 0) return base;
    }

    // Dentro del conjunto preferido, "Editorial de pie" es el default
    // elegido (pose de pie, encuadre editorial) — la llevamos al frente si
    // está y es compatible. Si no, queda la primera del conjunto.
    const prefIdx = preferred.findIndex(
      (p) => p.nombre.trim().toLowerCase() === DEFAULT_FULLBODY_POSE_NAME,
    );
    const orderedPreferred =
      prefIdx > 0
        ? [preferred[prefIdx], ...preferred.filter((_, i) => i !== prefIdx)]
        : preferred;
    const prefIds = new Set(orderedPreferred.map((p) => p.id));
    const rest = base.filter((p) => !prefIds.has(p.id));
    return [...orderedPreferred, ...rest];
  }, [allPoses, expectedPeopleInPose, selectedProducts]);

  // Pose default (solo design-modelo): seleccionamos la PRIMERA pose
  // RECOMENDADA (primer ítem de `allPosesFiltered`, que ya viene ordenado
  // recomendadas-primero según el `modelView` del producto). Mismo espíritu
  // que PruebaNuevaInterfaz (arranca en el tab "recomendado").
  //
  // CLAVE: esperamos a que haya un producto elegido (`selectedProducts`).
  // Si aplicáramos el default con el catálogo cargado pero sin producto, el
  // orden no está recomendado todavía (queda por `created_at`) y se fijaría
  // una pose arbitraria (p.ej. un close-up de collar) que después ya no se
  // re-evalúa. El ref `poseAutoSelectRef` distingue auto vs. elección
  // manual / restore, y se resetea a 'pending' al confirmar/cambiar producto
  // para re-recomendar (ver `handleConfirmProducts` / `closePresetSheet`).
  useEffect(() => {
    if (!selectedPreset) return;
    if (selectedPreset.kind !== 'design-modelo') return;
    if (selectedProducts.length === 0) return;
    if (allPosesFiltered.length === 0) return;
    if (poseAutoSelectRef.current !== 'pending') return;
    setPoseIds([allPosesFiltered[0].id]);
    poseAutoSelectRef.current = 'applied';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPreset, allPosesFiltered, selectedProducts.length]);

  // Cambio de pose hecho por el user (inline picker o PosePickerSheet).
  // Marca la selección como manual para que el default recomendado no la
  // pise en re-renders ni en cambios async del catálogo.
  const handlePoseIdsChange = useCallback((next: string[]) => {
    // Mínimo 1 pose: nunca dejamos la selección vacía. Si quedara en 0 (al
    // Generar el payload de pose saldría vacío y rompería la generación), lo
    // ignoramos → la última pose seleccionada no se puede deseleccionar.
    if (next.length === 0) return;
    poseAutoSelectRef.current = 'manual';
    setPoseIds(next);
  }, []);

  // Toggle "Seleccionar múltiples" del menú de poses. Al apagarlo con
  // varias poses elegidas, colapsamos a la PRIMERA (mismo criterio que
  // PruebaNuevaInterfaz). El PosePickerSheet ya colapsa su draft antes
  // de commitear — esto cubre el path directo (y futuros callers).
  const handleMultiPoseEnabledChange = useCallback((enabled: boolean) => {
    setMultiPoseEnabled(enabled);
    if (!enabled) {
      setPoseIds((prev) => (prev.length > 1 ? [prev[0]] : prev));
    }
  }, []);

  // ── E-commerce: lock de cantidad de modelos ──────────────────────────
  // Las plantillas de "E-commerce" son poses con `cantidadPersonas`
  // explícito en la tabla `poses`. Cuando la pose tiene > 1 persona,
  // armamos multi-mode automáticamente con esa cantidad de slots y
  // deshabilitamos add/remove (`lockSlotCount`). Para poses de 1
  // persona, single-mode normal.
  const ecommercePoseMeta = React.useMemo(() => {
    if (!selectedPreset) return null;
    if (selectedPreset.kind !== 'design') return null;
    if (!selectedPreset.id.startsWith('pose-')) return null;
    const poseId = selectedPreset.id.slice('pose-'.length);
    const pose = allPoses.find((p) => p.id === poseId);
    if (!pose) return null;
    return pose;
  }, [selectedPreset, allPoses]);
  const lockSlotCount = !!(
    ecommercePoseMeta && ecommercePoseMeta.cantidadPersonas > 1
  );
  useEffect(() => {
    if (!ecommercePoseMeta) return;
    const count = ecommercePoseMeta.cantidadPersonas;
    if (count > 1) {
      setMultiModelEnabled(true);
      setModelSelectionsExtra((prev) => {
        const expected = count - 1;
        if (prev.length === expected) return prev;
        const next = [...prev];
        while (next.length < expected) next.push(null);
        while (next.length > expected) next.pop();
        return next;
      });
    } else {
      setMultiModelEnabled(false);
      setModelSelectionsExtra([]);
    }
    // Pre-poblamos poseIds con la pose embebida en la card que el user
    // tappeó, si todavía está vacío. Para 1-persona habilitamos
    // multi-pose (el user puede agregar más desde el PosePicker); para
    // 2+ personas queda fija — el array siempre tiene exactamente la
    // pose del card.
    setPoseIds((prev) => {
      if (count > 1) {
        return [ecommercePoseMeta.id];
      }
      if (prev.length === 0) {
        return [ecommercePoseMeta.id];
      }
      return prev;
    });
  }, [ecommercePoseMeta]);

  // Si cambia la cantidad de modelos (o llega el catálogo), descartamos las
  // poses ya seleccionadas que dejaron de ser compatibles — las globales Y
  // las propias ("Mis poses" cuentan por su cantidad_personas; la versión
  // anterior solo miraba `allPosesFiltered` y descartaba SIEMPRE las custom).
  // Si quedan 0 después del descarte, la primera global compatible — espejo
  // del default recomendado de arriba. Lógica pura en posesCompatibilidad.ts.
  //
  // OJO: el efecto NO corre en su primer pase. En el remount post-generar
  // corría viendo el render PRE-restore (multi todavía apagado ⇒
  // expectedPeopleInPose=1, con `allPoses` caliente del cache de
  // sessionStorage) mientras su updater recibía el poseIds YA restaurado:
  // pisaba la pose multi-persona del restore con la default de 1 persona y
  // dejaba modelo↔poses cruzados. El efecto existe para reaccionar a
  // CAMBIOS, no para validar el mount — el default inicial ya lo pone el
  // efecto de preselección. Ver ADR-2026-08-24-estado-multi-modelo-consistente.
  // Firma de deps en vez de un boolean: el doble-setup de StrictMode (dev)
  // re-invoca el efecto con el MISMO render — con un "ready" simple el
  // segundo pase corría igual que antes del fix, con los valores pre-restore.
  const poseCompatSeenRef = useRef<string | null>(null);
  useEffect(() => {
    const firma = `${expectedPeopleInPose}|${allPoses.length}`;
    if (poseCompatSeenRef.current === firma) return;
    const esPrimerPase = poseCompatSeenRef.current === null;
    poseCompatSeenRef.current = firma;
    if (esPrimerPase) return;
    if (allPoses.length === 0) return;
    const compatiblesGlobales = allPosesFiltered.map((p) => p.id);
    const compatiblesCustom = customPoses
      .filter((p) => (p.cantidadPersonas ?? 1) === expectedPeopleInPose)
      .map((p) => p.id);
    setPoseIds((prev) =>
      ajustarPosesPorCantidadDeModelos({
        prev,
        compatiblesGlobales,
        compatiblesCustom,
        expectedPeopleInPose,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expectedPeopleInPose, allPoses.length]);

  // ── Generación ─────────────────────────────────────────────────────
  // Loading + error state para la llamada al webhook. `showLoadingScreen`
  // overlay full-screen mientras genera; `genError` mensaje inline en el
  // panel si algo falla. `generatingLockRef` previene doble click.
  const [showLoadingScreen, setShowLoadingScreen] = useState<boolean>(false);
  const [loadingExiting, setLoadingExiting] = useState<boolean>(false);
  const [videoGenerating, setVideoGenerating] = useState<boolean>(false);
  // Marca si la generación en curso es de modo 'anuncio'. Se usa para
  // alargar la barra de carga (los anuncios tardan más que una imagen
  // normal). Se setea al arrancar cada generación, igual que videoGenerating.
  const [anuncioGenerating, setAnuncioGenerating] = useState<boolean>(false);
  const [completeProgressNow, setCompleteProgressNow] = useState<boolean>(false);
  const [genError, setGenError] = useState<string | null>(null);
  const generatingLockRef = useRef<boolean>(false);
  const pendingVideoIdRef = useRef<string | null>(null);
  const pendingVideoCompanyIdRef = useRef<string | null>(null);

  // B — Reanudar una generación de imagen pendiente tras RECARGA / descarte de
  // la página (el poll en memoria de la carrera muere ahí). Al montar, si hay
  // una generación reciente pendiente, retomamos el poll por su execution_id y
  // navegamos al resultado cuando aparece. Versión SILENCIOSA a propósito (sin
  // pantalla de carga) para no arriesgar un "loading colgado": si no aparece
  // dentro del presupuesto, se limpia y la imagen —si salió— queda en el
  // historial. NO corre al apagar/prender la pantalla (eso no remonta el
  // componente; ese caso lo cubre la carrera en memoria sin esperas).
  useEffect(() => {
    const pending = readPendingGeneration();
    if (!pending) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await pollGeneratedByExecutionId({
          executionId: pending.executionId,
          companyId: pending.companyId,
          expectedCount: pending.expectedCount,
          timeoutMs: 120_000,
        });
        if (cancelled) return;
        clearPendingGeneration();
        const ok = rows.filter((r) => !r.has_error);
        // No interrumpir si el user ya arrancó otra generación.
        if (ok.length > 0 && !generatingLockRef.current) {
          navigate(rutaResultados, {
            state: {
              imageIds: ok.map((r) => r.id),
              companyId: pending.companyId,
            },
          });
        }
      } catch {
        if (!cancelled) clearPendingGeneration();
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mapping de slug de formato → aspect-ratio que espera el webhook.
  // Mismo mapping que PruebaNuevaInterfaz.handleGenerate.
  const formatSlugToAspect = (slug: string | null): string => {
    switch (slug) {
      case 'story':
        return '9:16';
      case 'square':
        return '1:1';
      case 'portrait':
        return '2:3';
      case 'banner':
        return '16:9';
      case 'editorial':
        return '3:2';
      case 'billboard':
        return '21:9';
      case 'post':
      default:
        return '4:5';
    }
  };

  // ── Gating al generar: funciones por plan + créditos ────────────────────
  // Reusa los mecanismos del resto de la app (PruebaNuevaInterfaz / video):
  //   • Funciones bloqueadas para `gratis`: catálogo IA y agente.
  //   • Video (plantillas) bloqueado para `gratis` y `emprendimiento`.
  //   • Créditos insuficientes (`imagenes_restantes < cost`) → modal de límite
  //     (mismo `SubscriptionLimitModal` de toda la app).
  // El gating de función se chequea ANTES que los créditos. Devuelve true si
  // se puede generar. Fetchea fresco para no bloquear/dejar pasar de más.
  // `cost` = créditos que consumirá la acción (lo que muestra el pill del
  // botón Generar). Antes solo chequeábamos `<= 0` (disponibilidad), no
  // suficiencia: un user con 1 crédito disparaba producciones/catálogos/videos
  // de N créditos. Ver ADR-518.
  const checkGenerationGuards = async (
    feature?: 'catalogo' | 'video' | 'agente',
    cost?: number,
  ): Promise<boolean> => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return true;
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();
      const idCompany = (profile as any)?.id_company as string | undefined;
      if (!idCompany) return true;
      const { data: cp } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes')
        .eq('id_company', idCompany)
        .single();
      const planReal = ((cp as any)?.plan as string) ?? null;
      if (planReal) setUserPlanReal(planReal);
      // El plan que acaba de llegar de la base. Acá se aplicaba encima el simulado
      // del panel CONTEXTO DEV (ADR-2026-08-03-el-plan-simulado-tambien-gatea); el
      // panel se eliminó al mergear `principal` — ADR-2026-08-05-fuera-los-overrides-de-dev.
      const plan = planReal;
      // Gating por plan (antes que créditos). Backstop: los modos ya gatean al
      // entrar; acá usamos el MISMO helper con el plan fresco de Supabase para
      // que sea consistente.
      if (feature === 'catalogo') {
        // Pregunta por el `kind` REAL del preset y no por `'catalogo-ia'` hardcodeado,
        // que es como estaba. `handleGenerateCatalog` sirve a los TRES modos que pasan
        // por `CatalogoCardsFullscreen` —catálogo IA, catálogo de muebles e Infografía—
        // y los tres llegan acá con `feature: 'catalogo'`, así que el hardcode los
        // gateaba a todos con la regla de uno solo.
        //
        // HOY NO DISPARA: los tres quedaron sin piso de plan
        // (ADR-2026-08-20-infografia-tambien-se-abre-a-la-prueba-gratis), así que
        // `getModePlanLock` devuelve `null` para cualquiera de ellos y lo único que
        // corta más abajo son los créditos.
        //
        // Se deja igual en vez de borrarlo porque no es una regla escrita: es "aplicá
        // la regla que tenga este modo". Si mañana alguno vuelve a tener piso, funciona
        // sin que haya que acordarse de este archivo — que es exactamente lo que falló
        // con el hardcode.
        //
        // Sin `selectedPreset` (no debería pasar por este camino) queda el default de
        // `getModePlanLock`: sin `kind` no bloquea, igual que con el plan desconocido.
        const lock = getModePlanLock(selectedPreset?.kind, plan);
        if (lock) {
          setFeatureLock(lock);
          return false;
        }
      }
      if (feature === 'agente') {
        const lock = getModePlanLock('asistente', plan);
        if (lock) {
          setFeatureLock(lock);
          return false;
        }
      }
      // Video (plantillas / influencer): Pro y Ultra. `plan &&` evita bloquear
      // si el plan no se conoce (defensivo).
      if (feature === 'video' && plan && !isProOrUltra(plan)) {
        // Mismo modal "Función exclusiva" que el flujo de generación de
        // video (influencer / personalizado).
        setShowVideoPlanRestriction(true);
        return false;
      }
      // Créditos: bloqueamos si el saldo no alcanza para cubrir el costo de
      // la acción. `needed` cae a 1 si no llega un costo (nada es gratis).
      const needed = typeof cost === 'number' && cost > 0 ? Math.ceil(cost) : 1;
      const remaining = (cp as any)?.imagenes_restantes as
        | number
        | null
        | undefined;
      if (typeof remaining === 'number' && remaining < needed) {
        setSubscriptionLimitType(
          plan === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached',
        );
        // Reset explícito: este camino SÍ es "se quedó sin créditos". Sin esto, el
        // motivo que dejó el CTA de la barra quedaría pegado (ADR-2026-07-31-copy-catalogo-sigue-saldo-real).
        setMotivoPricing('sin');
        setShowSubscriptionLimitModal(true);
        return false;
      }
      return true;
    } catch (err) {
      console.error('[InterfazV2] checkGenerationGuards error', err);
      return true; // no bloquear por un fallo de chequeo
    }
  };

  const handleGenerateFinal = async (opts?: {
    autoCarrusel?: boolean;
    carruselCount?: number;
    // Créditos que consumirá esta generación (= pill del botón). Se pasa al
    // guard para chequear saldo suficiente. Ver ADR-518.
    cost?: number;
  }) => {
    if (generatingLockRef.current) return;
    if (!selectedPreset) return;
    // El modo "Video con influencer" SÍ genera: el branch dedicado vive
    // dentro del `try` de abajo (POST a `GENERATE_VIDEO_INFLUENCER_URL`).
    // Normalmente requiere al menos un producto (va en `variante_id`) — salvo
    // con "Sin producto" (ADR-673), donde `variante_id` directamente no viaja.
    if (selectedProducts.length === 0 && !sinProducto) return;
    generatingLockRef.current = true;
    // Historial de instrucciones reusables: se registra ACÁ, el único punto por
    // el que pasan todos los modos clásicos (los 5 lugares que arman
    // `instruccion_adicional` viven dentro de esta función). Es fire-and-forget:
    // no se espera ni puede fallar hacia afuera.
    recordPromptHistory(userCompanyId, 'instruccion', otraConfig);
    // "Generar producción": además de generar normal, al llegar al inline
    // de imágenes generadas dispara la generación de carrusel. Solo aplica
    // a la rama de plantillas (kind 'template', modo imagen).
    const wantsCarrusel = opts?.autoCarrusel === true;
    // Cuántas imágenes pedirle al carrusel cuando es producción. Viene del
    // stepper "x imágenes" como (cantidad elegida − 1). Si no llega, caemos
    // al default de 2 (mismo comportamiento que el carrusel manual).
    const carruselCount =
      typeof opts?.carruselCount === 'number' && opts.carruselCount > 0
        ? opts.carruselCount
        : 2;
    const isVideo =
      selectedPreset.kind !== 'design-modelo' &&
      selectedPreset.kind !== 'design' &&
      selectedPreset.outputType === 'video';
    // Video también cuando el toggle de plantilla está en 'video'.
    const isVideoOutput =
      isVideo ||
      (previewMediaTab === 'video' && selectedPreset.kind === 'template');
    // Guards (plan + créditos) ANTES de mostrar el loading.
    const guardsOk = await checkGenerationGuards(
      isVideoOutput ? 'video' : undefined,
      opts?.cost,
    );
    if (!guardsOk) {
      generatingLockRef.current = false;
      return;
    }
    setGenError(null);
    setLoadingExiting(false);
    setCompleteProgressNow(false);
    pendingVideoIdRef.current = null;
    pendingVideoCompanyIdRef.current = null;
    // Usamos `isVideoOutput` (no `isVideo`): así el loading dice "Generando
    // video..." también cuando es una PLANTILLA con el toggle en 'video'
    // (que genera imagen y después encadena el video). Antes usaba `isVideo`
    // —solo presets outputType:'video'— y la plantilla-video mostraba
    // "Generando imágenes...".
    setVideoGenerating(isVideoOutput);
    setAnuncioGenerating(selectedPreset.kind === 'anuncio');
    setShowLoadingScreen(true);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');
      if (!userCompanyId) throw new Error('No se encontró la empresa del usuario');

      const isDesign = selectedPreset.kind === 'design';
      const isDesignModelo = selectedPreset.kind === 'design-modelo';
      const isDesignAuto = selectedPreset.kind === 'design-auto';
      const isDesignProducto = selectedPreset.kind === 'design-producto';
      const isFotosProducto = selectedPreset.kind === 'fotos-producto';
      const isAnuncio = selectedPreset.kind === 'anuncio';
      const variantIds = selectedProducts.map((p) => p.id);
      const mode = variantIds.length > 1 ? 'multiple' : 'single';

      // `execution_id`: id de correlación que GENERA el frontend y manda al
      // webhook en TODOS los modos. El backend lo escribe en la fila de
      // `imagenes_generadas`; sirve para (a) el modo video (poll = fuente de
      // verdad) y (b) los modos de imagen (poll de respaldo en el `catch` si la
      // respuesta se pierde). Uno por generación.
      const executionId = crypto.randomUUID();

      // ── Modo 'video-influencer' (Video con influencer) ──
      // Manda producto(s) + modelo + duración al webhook
      // `video_influencer` (`GENERATE_VIDEO_INFLUENCER_URL`). El "id del
      // modelo" sale de `modelSelection`
      // cuando se eligió un modelo GUARDADO (mode 'preset'); si no se eligió
      // ninguno (o es automático/subido) va `modelo_mode: 'ia'` y el backend
      // decide. Las keys siguen el mismo dialecto del flujo de influencer
      // clásico (`variante_id`, `modelo_guardado_id`, `duracion`) — si el n8n
      // espera otros nombres, ajustar acá.
      if (selectedPreset.kind === 'video-influencer') {
        const modeloFields: Record<string, any> = {};
        if (modelSelection?.mode === 'preset') {
          modeloFields.modelo_mode = 'guardados';
          if (modelSelection.id) {
            modeloFields.modelo_guardado_id = String(modelSelection.id);
            if (modelSelection.name)
              modeloFields.modelo_guardado_nombre = modelSelection.name;
          }
        } else if (modelSelection?.mode === 'custom') {
          modeloFields.modelo_mode = 'personalizado';
          const f = modelSelection.fields;
          if (f.genero) modeloFields.genero = f.genero;
          if (f.edad) modeloFields.edad = f.edad;
          if (f.tipoCuerpo) modeloFields.tipo_cuerpo = f.tipoCuerpo;
          if (f.tonoPiel) modeloFields.tono_piel = f.tonoPiel;
          if (f.colorPelo) modeloFields.color_pelo = f.colorPelo;
          if (f.largoPelo) modeloFields.largo_pelo = f.largoPelo;
          if (f.descripcion) modeloFields.desc_modelo = f.descripcion;
        } else {
          // auto / sin selección / modelo subido → el backend decide.
          modeloFields.modelo_mode = 'ia';
        }

        // Duración efectiva (ADR-547): con guion escrito/grabado la marca el
        // GUION (duración del audio, o estimación de lectura del texto, tope
        // 14s) — es lo que viaja como `duracion` y lo que cobra el pill
        // (base 4 + duración). En Automático manda la barra de Duración.
        const influencerDuracion =
          getGuionDurationSec(videoVoiceGuion) ?? videoDuration;

        // Dirección de actuación saneada (ADR-559), solo con guion escrito.
        // Se computa una vez (sanitizeEstiloVoz es puro) y se incluye en el
        // payload solo si queda algo tras el saneo.
        const estiloVoz =
          videoVoiceGuion.guionMode === 'texto'
            ? sanitizeEstiloVoz(videoVoiceGuion.formaDeHablar)
            : '';

        // El modo video usa el `executionId` (de arriba) como FUENTE DE VERDAD:
        // dispara el webhook fire-and-forget y poolea hasta encontrar la fila.
        // (Los modos de imagen, más abajo, lo usan solo como respaldo.)
        const requestData: Record<string, any> = {
          user_id: user.id,
          // "Sin producto" (ADR-673): la key NO viaja y el backend saltea la
          // imagen de producto (Gemini y Enhancor reciben solo la influencer).
          ...(sinProducto ? {} : { variante_id: variantIds }),
          mode,
          ref_mode: 'ugc',
          // Etiqueta de modo del historial y del panel. ADR-560 la había sacado por
          // "redundante con ref_mode", pero el que lee `ref_mode` NO es el que escribe
          // la columna: sin esta key la fila de `imagenes_generadas` quedaba con
          // `modo_utilizado` NULL y la ejecución salía sin Modo en el panel (44 filas
          // en 30 días). SUPERSEDE ADR-560 para este webhook — ver
          // ADR-2026-08-26-modo-utilizado-en-toda-generacion. El backend igual pone
          // este mismo default en el enqueue; acá cubre además el camino n8n.
          modo_utilizado: 'video_influencer',
          duracion: influencerDuracion,
          aspect_ratio: formatSlugToAspect(formatoVal),
          execution_id: executionId,
          // Gancho elegido en la UI (`video_hooks.id`). La UI bloquea el
          // Generar sin elegir nada (ADR-551), pero desde ADR-671 "nada" y
          // "Sin gancho" son distintos: con `SIN_GANCHO_ID` la key NO viaja y
          // el backend saltea el clip de apertura y el concat. Con un gancho
          // real, lo usa como prompt de apertura (ADR-504).
          ...(isSinGancho(videoGanchoId)
            ? {}
            : { gancho_id: videoGanchoId }),
          // Voz y guion (ADR-525): mismos NOMBRES de campo que el modo
          // diálogo del Director de Estudio, pero desde ADR-550 el VALOR de
          // `voz_id` es el NOMBRE de la voz Gemini (tabla `voces_google`) —
          // n8n debe usar el TTS de Google acá, no ElevenLabs.
          // `dialogo_input_mode: 'auto'` = el backend genera guion Y voz
          // (voz_id: null). Con guion escrito/grabado, `voz_id` es
          // OBLIGATORIO: la UI bloquea el Generar sin voz (ADR-544/545).
          voz_id:
            videoVoiceGuion.guionMode === 'auto'
              ? null
              : videoVoiceGuion.vozId,
          // Dirección de actuación para el TTS (reemplaza a `velocidad_voz`,
          // ADR-558) — solo con guion escrito; en Automático el backend usa
          // su propio estilo (ver `estiloVoz` arriba).
          ...(estiloVoz ? { estilo_voz: estiloVoz } : {}),
          dialogo_input_mode: videoVoiceGuion.guionMode,
          ...(videoVoiceGuion.guionMode === 'texto'
            ? { dialogo: videoVoiceGuion.guionTexto.trim() }
            : {}),
          ...(videoVoiceGuion.guionMode === 'audio' && videoVoiceGuion.guionAudio
            ? {
                audio_url: videoVoiceGuion.guionAudio.url,
                audio_duration_sec: Math.round(
                  videoVoiceGuion.guionAudio.durationSec,
                ),
              }
            : {}),
          // Subtítulos (ADR-537): mismo dialecto que el flujo V1 — el
          // `caption_id` (estilo elegido) viaja SOLO si el user los activó.
          ...(videoVoiceGuion.subtitulos
            ? { caption_id: videoVoiceGuion.captionId }
            : {}),
          // "Instrucción adicional" — mismo campo que los modos de imagen.
          // El input se renderizaba desde el scaffold pero recién se manda
          // desde ADR-562; solo viaja si no está vacío.
          ...(otraConfig.trim()
            ? { instruccion_adicional: otraConfig.trim() }
            : {}),
          ...modeloFields,
        };
        console.log('[InterfazV2] video-influencer payload', requestData);

        // Disparamos el webhook "fire-and-forget": no esperamos su respuesta
        // para mostrar el resultado (lo hace el poll). Solo capturamos un error
        // temprano y DEFINITIVO (4xx / moderación) para cortar el poll antes.
        // Un timeout / conexión caída / 5xx del trigger NO es fatal: la
        // generación tarda varios minutos y el proxy (Render/Cloudflare) suele
        // cortar la respuesta larga con 502/504 alrededor de los ~100s con el
        // workflow TODAVÍA corriendo — el backend escribe la fila igual y el
        // poll la encuentra (ADR-540).
        let triggerError: string | null = null;
        void (async () => {
          try {
            // Ruteo atlas/n8n (ADR-588): mismo contrato fire-and-forget —
            // el poll por execution_id sigue siendo la fuente de verdad.
            // Atlas deriva el user del JWT (user_id no viaja).
            let res: Response;
            if (USE_ATLAS_BACKEND) {
              const { user_id: _uid, ...atlasBody } = requestData;
              void _uid;
              res = await atlasBackendFetch('/v1/videos/influencer', atlasBody, {
                signal: AbortSignal.timeout(VIDEO_INFLUENCER_POLL_TIMEOUT_MS),
              });
            } else {
              res = await fetch(GENERATE_VIDEO_INFLUENCER_URL, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: GENERATE_AUTH_TOKEN,
                },
                body: JSON.stringify(requestData),
                signal: AbortSignal.timeout(VIDEO_INFLUENCER_POLL_TIMEOUT_MS),
              });
            }
            const text = await res.text().catch(() => null);
            if (!res.ok) {
              if (res.status >= 400 && res.status < 500) {
                // Rechazo definitivo del backend (402 sin créditos, payload
                // inválido, auth…). Atlas manda JSON tipado pero el nombre del
                // campo varía por ruta (`error_message` / `error` / `message`)
                // — sin cubrir los tres, el toast mostraba el JSON crudo.
                let msg: string | null = null;
                try {
                  const j = text ? JSON.parse(text) : null;
                  msg = j?.error_message ?? j?.error ?? j?.message ?? null;
                } catch {
                  /* body no-JSON → cae al texto crudo */
                }
                triggerError = msg || text || `Error ${res.status}`;
              } else {
                console.warn(
                  '[InterfazV2] video-influencer: trigger HTTP',
                  res.status,
                  '— gateway/5xx no fatal, seguimos polleando',
                );
              }
              return;
            }
            try {
              const json = text ? JSON.parse(text) : null;
              if (json?.response === 'error_moderation') {
                triggerError =
                  'El contenido no pasó la moderación. Probá con otro producto o modelo.';
              }
            } catch {
              /* respuesta no-JSON: ok, manda el poll */
            }
          } catch (e) {
            // timeout / abort / conexión caída → seguimos polleando.
            console.warn(
              '[InterfazV2] video-influencer: trigger sin respuesta usable; seguimos polleando',
              e,
            );
          }
        })();

        // Poll por `execution_id` hasta el tope (VIDEO_INFLUENCER_POLL_
        // TIMEOUT_MS). Robustecido (ADR-541) para "puse a generar y volví
        // a los 20 min":
        //   • Deadline por RELOJ, no por cantidad de intentos: en background
        //     el browser throttlea los timers (sleeps de 5s pasan a minutos)
        //     y contar intentos distorsionaba el tope.
        //   • Cada consulta tolera errores de red (laptop suspendida,
        //     conexión caída al volver): warn + reintento, nunca aborta.
        //   • El chequeo va ANTES del corte por deadline: al despertar la
        //     pestaña tarde, la última iteración consulta igual — si el video
        //     ya está en la DB (el caso reportado), navega en vez de tirar
        //     timeout.
        const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
        const deadline = Date.now() + VIDEO_INFLUENCER_POLL_TIMEOUT_MS;
        // La fila puede ser el video O una fila has_error (el backend la escribe
        // en TODO fallo definitivo desde el 25/7 — censura, timeout, técnico):
        // sin mirar has_error navegábamos a la fila rota como si fuera el video.
        const checkRow = async (): Promise<{
          id: string;
          hasError: boolean;
          errorType: string | null;
        } | null> => {
          try {
            const { data: row } = await supabase
              .from('imagenes_generadas')
              .select('id, has_error, error_type')
              .eq('execution_id', executionId)
              .maybeSingle();
            return row?.id
              ? {
                  id: String(row.id),
                  hasError: row.has_error === true,
                  errorType: (row.error_type as string | null) ?? null,
                }
              : null;
          } catch (e) {
            console.warn(
              '[InterfazV2] video-influencer: poll con error de red — reintentamos',
              e,
            );
            return null;
          }
        };
        let videoRow: Awaited<ReturnType<typeof checkRow>> = null;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (triggerError) break;
          videoRow = await checkRow();
          if (videoRow) break;
          if (Date.now() >= deadline) break;
          await sleep(VIDEO_INFLUENCER_POLL_INTERVAL_MS);
        }
        if (videoRow?.hasError) {
          // Copy por tipo (censura ≠ timeout ≠ técnico), siempre aclarando que
          // no se consumieron créditos — antes: genérico o navegación rota.
          const t = (videoRow.errorType as GenerateAdErrorType) || 'technical';
          setGenError(mapErrorTypeToMessage(t));
          setShowLoadingScreen(false);
          setVideoGenerating(false);
          setAnuncioGenerating(false);
          generatingLockRef.current = false;
          return;
        }
        const videoId = videoRow?.id ?? null;
        if (!videoId) {
          // Terminales del modo manejados ACÁ y no con throw: el catch
          // genérico pisa el mensaje con "Se produjo un error…" (solo
          // GenerateAdError lo conserva) y perderíamos el detalle de
          // moderación/timeout (ADR-543).
          setGenError(
            triggerError ??
              'El video está tardando más de lo normal y puede seguir generándose en segundo plano. Revisá el historial en unos minutos; si no aparece, probá de nuevo.',
          );
          setShowLoadingScreen(false);
          setVideoGenerating(false);
          setAnuncioGenerating(false);
          generatingLockRef.current = false;
          return;
        }

        // Persistir "último utilizado" — mismo registro que los flujos de
        // imagen (`lastUsedProducts`), así el picker destaca este producto la
        // próxima vez. SOLO productos: el formato (story) y modelo del
        // influencer no deben pisar el auto-relleno de los modos de imagen.
        try {
          if (selectedProducts.length > 0) {
            localStorage.setItem(
              'atlas-v2-last-products',
              JSON.stringify(selectedProducts.map((p) => p.id)),
            );
          }
        } catch {
          /* noop */
        }
        // Última voz usada — se preselecciona en la próxima apertura del
        // modo (ADR-544). Solo si la voz REALMENTE se usó: en guion
        // Automático el backend elige la voz y el vozId local es solo el
        // preseleccionado (ADR-545).
        if (videoVoiceGuion.guionMode !== 'auto' && videoVoiceGuion.vozId) {
          saveLastUsedVoice(videoVoiceGuion.vozId, videoVoiceGuion.vozNombre);
        }

        try {
          new Audio('/images/clicksound.m4a').play();
        } catch {
          /* silent */
        }
        navigate(rutaResultados, {
          state: { imageIds: [videoId], companyId: userCompanyId },
        });
        return;
      }

      // ── Modo 'design-auto' (Generar imágenes automáticamente) ──
      // Atajo: armamos un payload mínimo equivalente al tab
      // "automatico" de PruebaNuevaInterfaz (modo 'rapido' + IA decide
      // todo). NO usa modelo / pose / fondo de la UI. Mandamos al
      // mismo endpoint V3 que el resto del flow.
      if (isDesignAuto) {
        const autoRequest: Record<string, any> = {
          user_id: user.id,
          variante_id: variantIds,
          mode,
          modo: 'rapido',
          modo_utilizado: 'imagen_automatica',
          // Cantidad de imágenes elegida con el control +/- arriba del
          // botón Generar. Clamped a [1, AUTO_IMAGE_COUNT_MAX].
          cantidad_imagenes: Math.max(
            1,
            Math.min(AUTO_IMAGE_COUNT_MAX, autoImageCount),
          ),
          Format: formatSlugToAspect(formatoVal),
          // Resolución elegida: el backend la traduce a imageConfig.imageSize y
          // multiplica los créditos (ADR-2026-08-25-resolucion-de-generacion-2k-4k).
          image_size: resolucionVal,
          composicion: 'producto',
          background_mode: 'ia',
          new_ui: true,
          modo_pro: true,
          execution_id: executionId,
          ...(otraConfig.trim()
            ? { instruccion_adicional: otraConfig.trim() }
            : {}),
        };
        // Carrera respuesta-vs-poll (A) + marca de pendiente para reanudar
        // tras recarga (B). El poll corre en paralelo: si el fetch se cuelga
        // (compu dormida), el resultado se recupera igual.
        markPendingGeneration({
          executionId,
          companyId: userCompanyId,
          expectedCount: autoRequest.cantidad_imagenes as number,
        });
        const autoResult = await awaitImageGeneration({
          fetchPromise: postGenerateAd(autoRequest, { url: GENERATE_AD_V3_URL }),
          executionId,
          companyId: userCompanyId,
          expectedCount: autoRequest.cantidad_imagenes as number,
        });
        clearPendingGeneration();
        const autoData: any = {
          response: 'success',
          imagenes_generadas_ids: autoResult.imageIds,
          id_company: autoResult.companyId,
        };
        if (
          Array.isArray(autoData?.imagenes_generadas_ids) &&
          autoData.imagenes_generadas_ids.length > 0
        ) {
          const imageIds = autoData.imagenes_generadas_ids as number[];
          const companyIdResult =
            (autoData.id_company as string) || userCompanyId;
          let preloadedImages: PreloadedImage[] = [];
          try {
            preloadedImages = await preloadGeneratedImages(
              imageIds,
              companyIdResult,
            );
          } catch (preloadErr) {
            console.warn(
              '[InterfazV2] preloadGeneratedImages (auto) falló',
              preloadErr,
            );
          }
          try {
            new Audio('/images/clicksound.m4a').play();
          } catch {}
          const { user_id: _u, ...lastPayload } = autoRequest;
          void _u;
          navigate(rutaResultados, {
            state: {
              imageIds,
              companyId: companyIdResult,
              lastPayload,
              preloadedImages,
            },
          });
          return;
        }
        throw new Error('No se recibieron imágenes generadas.');
      }

      // ── Modo 'design-producto' (vistas solo-producto de "Fondo blanco") ──
      // Atajo estilo 'design-auto': payload propio SIN modelo / pose. Es el
      // mismo shape que manda Catálogo IA para sus vistas solo-producto
      // (ver handleGenerateCatalog): composicion 'producto' + vista_producto
      // + fondo blanco fijo (background_id 5) + modo 'personalizado'.
      // `mode: 'single'` aunque haya varios productos: igual que el combo de
      // Catálogo IA, el backend compone todos los `variante_id` en UNA imagen.
      if (isDesignProducto) {
        // `vistaProductoId` es el slug real: las cards desdobladas
        // (`vista-frente` / `vista-frente-ropa`) mandan ambas 'frente'.
        // Fallback al id sin prefijo por si un preset viejo cacheado en
        // sessionStorage no trae el campo.
        const vistaSlug =
          selectedPreset.vistaProductoId ??
          (selectedPreset.id.startsWith(VISTA_PRESET_PREFIX)
            ? selectedPreset.id.slice(VISTA_PRESET_PREFIX.length)
            : selectedPreset.id);
        const productoRequest: Record<string, any> = {
          user_id: user.id,
          variante_id: variantIds,
          mode: 'single',
          modo: 'personalizado',
          // Trackeo (ADR-512): las vistas solo-producto cuentan como plantilla.
          modo_utilizado: 'plantilla',
          cantidad_imagenes: 1,
          Format: formatSlugToAspect(formatoVal),
          composicion: 'producto',
          vista_producto: vistaSlug,
          background_mode: 'blanco',
          background_id: 5,
          ...(otraConfig.trim()
            ? { instruccion_adicional: otraConfig.trim() }
            : {}),
          new_ui: true,
          modo_pro: true,
          execution_id: executionId,
        };
        // Carrera respuesta-vs-poll (A) + marca de pendiente (B) — mismo
        // esquema que design-auto.
        markPendingGeneration({
          executionId,
          companyId: userCompanyId,
          expectedCount: 1,
        });
        const productoResult = await awaitImageGeneration({
          fetchPromise: postGenerateAd(productoRequest, {
            url: GENERATE_AD_V3_URL,
          }),
          executionId,
          companyId: userCompanyId,
          expectedCount: 1,
        });
        clearPendingGeneration();
        if (productoResult.imageIds.length > 0) {
          const imageIds = productoResult.imageIds;
          const companyIdResult = productoResult.companyId || userCompanyId;
          let preloadedImages: PreloadedImage[] = [];
          try {
            preloadedImages = await preloadGeneratedImages(
              imageIds,
              companyIdResult,
            );
          } catch (preloadErr) {
            console.warn(
              '[InterfazV2] preloadGeneratedImages (design-producto) falló',
              preloadErr,
            );
          }
          try {
            new Audio('/images/clicksound.m4a').play();
          } catch {}
          const { user_id: _u, ...lastPayload } = productoRequest;
          void _u;
          navigate(rutaResultados, {
            state: {
              imageIds,
              companyId: companyIdResult,
              lastPayload,
              preloadedImages,
            },
          });
          return;
        }
        throw new Error('No se recibieron imágenes generadas.');
      }

      // ── Modo 'fotos-producto' (Fotos de producto) ────────────────
      // M productos, N vistas, fondo blanco o escena descrita.
      //
      // Se manda UN REQUEST POR (PRODUCTO × VISTA) en vez de mandar
      // `vista_producto: [id1, id2]` en una sola llamada, que es lo que hace
      // el Catálogo IA. Motivo: el backend solo resuelve el multi-vista para
      // marcas del rubro "Moda e Indumentaria"; para cualquier otro rubro,
      // `composicion: 'producto'` cae en la rama del catálogo de productos,
      // que ignora la vista y entrega UNA sola imagen — mientras el gate de
      // créditos igual cuenta N y el poll se quedaría esperando filas que no
      // van a llegar. Con un request por par el modo funciona igual para
      // todos los rubros. Ver ADR-2026-08-13-fotos-producto-fan-out-por-vista
      // y ADR-2026-08-30-fotos-producto-multi-producto (el fan-out por
      // producto). Cada request lleva UN producto en `variante_id`: mandar el
      // array entero caería en la composición multi-producto del backend
      // (outfit combinado), que no es este modo.
      if (isFotosProducto) {
        const vistasElegidas = [...vistaIds];
        if (vistasElegidas.length === 0) {
          throw new Error('Elegí al menos una vista para generar.');
        }
        // Fondo: el picker de este modo ofrece "Fondo liso" (con color) o
        // escena descrita. Una descripción vacía cae al liso (no mandamos
        // `background_text: ''`, que dejaría al prompt sin escena Y sin
        // fondo). Del liso, el BLANCO va por el flujo clásico
        // (background_id 5); beige/rosa/negro no existen en la tabla
        // `fondos`, así que viajan como descripción con el prompt fijo del
        // catálogo (liso, sin texturas, luz de estudio).
        const colorLisoMeta =
          fondoSelection?.mode === 'blanco'
            ? COLORES_FONDO_LISO.find(
                (c) => c.id === (fondoSelection.colorLiso ?? 'blanco'),
              )
            : null;
        const fondoPayload: Record<string, any> =
          fondoSelection?.mode === 'descripcion' && fondoSelection.text.trim()
            ? {
                background_mode: 'descripcion',
                background_text: fondoSelection.text.trim(),
              }
            : colorLisoMeta?.prompt
              ? {
                  background_mode: 'descripcion',
                  background_text: colorLisoMeta.prompt,
                }
              : // id 5 = fondo blanco en la tabla `fondos` (mismo mapping que
                // design-modelo, las poses E-commerce y PruebaNuevaInterfaz).
                { background_mode: 'blanco', background_id: 5 };
        // Un execution_id POR REQUEST (ADR-590): compartiéndolo, las
        // ejecuciones paralelas se ven las filas entre sí y disparan un
        // fan-out que genera imágenes de más Y LAS COBRA.
        //
        // Un job por par (vista, producto APLICABLE Y ACTIVO): la unión de
        // vistas anota a qué productos aplica cada una (`productIds`) y el
        // checklist del badge puede desactivar productos por vista
        // (`vistaProductosExcluidos`). Ver
        // ADR-2026-08-30-fotos-producto-multi-producto-2.
        const vistasPorId = new Map(vistasProducto.map((v) => [v.id, v]));
        const jobs = vistasElegidas.flatMap((vista) => {
          const catalogo = vistasPorId.get(vista);
          if (!catalogo) return [];
          const excluidos = vistaProductosExcluidos[vista] ?? [];
          return catalogo.productIds
            .filter((id) => !excluidos.includes(id))
            .map((productoId) => ({
              productoId,
              vista,
              executionId: crypto.randomUUID(),
            }));
        });
        if (jobs.length === 0) {
          throw new Error('Elegí al menos una vista para generar.');
        }
        // Esperado MUTABLE: cuando un POST falla a nivel HTTP le descontamos
        // su imagen — ese request nunca va a escribir su fila y sin esto el
        // poll la esperaría hasta el tope (ADR-133).
        let expectedTotal = jobs.length;
        const sendErrors: GenerateAdError[] = [];
        const sends: Promise<unknown>[] = [];
        // Tope de llamadas en paralelo, mismo motivo que en el catálogo: con
        // el flag de atlas apagado cada request sostiene la conexión hasta
        // que el workflow termina y las de más se pierden.
        const MAX_FOTOS_EN_PARALELO = 3;
        let fotosEnVuelo = 0;
        const esperarTurnoFotos = async () => {
          while (fotosEnVuelo >= MAX_FOTOS_EN_PARALELO) {
            await new Promise((r) => setTimeout(r, 200));
          }
        };
        // Marca de pendiente ANTES de disparar: si el usuario recarga en el
        // medio, la tanda se reanuda por estos ids.
        markPendingGeneration({
          executionId: jobs.map((j) => j.executionId),
          companyId: userCompanyId,
          expectedCount: jobs.length,
        });
        let primerPayload: Record<string, any> | null = null;
        for (const job of jobs) {
          const payload: Record<string, any> = {
            user_id: user.id,
            variante_id: [job.productoId],
            mode: 'single',
            modo: 'personalizado',
            // Trackeo (ADR-511/512): etiqueta propia para separar este modo
            // de la imagen personalizada y de las plantillas en el panel.
            modo_utilizado: 'fotos_producto',
            // Fire-and-forget: la completitud la define el poll por
            // execution_id, así que el atlas puede responder 202 al toque en
            // vez de sostener la conexión. n8n ignora el campo.
            wait: false,
            cantidad_imagenes: 1,
            Format: formatSlugToAspect(formatoVal),
            composicion: 'producto',
            vista_producto: job.vista,
            ...fondoPayload,
            ...(otraConfig.trim()
              ? { instruccion_adicional: otraConfig.trim() }
              : {}),
            new_ui: true,
            modo_pro: true,
            execution_id: job.executionId,
          };
          if (!primerPayload) primerPayload = payload;
          await esperarTurnoFotos();
          fotosEnVuelo += 1;
          // SIN REINTENTO, a propósito (mismo criterio que el catálogo): la
          // generación NO es idempotente y reintentar una llamada caída
          // después de que el backend la recibió le cobra dos veces al
          // usuario. Perder una imagen y avisarlo es menos grave.
          sends.push(
            postGenerateAd(payload, { url: GENERATE_AD_V3_URL })
              .catch((e) => {
                console.warn(
                  '[fotos-producto] una vista falló (seguimos polleando)',
                  e,
                );
                expectedTotal -= 1;
                if (e instanceof GenerateAdError) sendErrors.push(e);
              })
              .finally(() => {
                fotosEnVuelo -= 1;
              }),
          );
        }
        const rows = await pollGeneratedByExecutionId({
          executionId: jobs.map((j) => j.executionId),
          companyId: userCompanyId,
          // Getter: el esperado puede BAJAR mientras el poll corre. Si fallan
          // TODOS queda en 0 y corta al toque en vez de esperar 5 min.
          expectedCount: () => expectedTotal,
        });
        clearPendingGeneration();
        const okRows = rows.filter((r) => !r.has_error);
        if (okRows.length === 0) {
          // El poll pudo cortar con algún `.catch` sin correr: esperamos los
          // POST para elegir el mensaje con todos los errores a la vista
          // (barato con wait:false). Un error accionable (sin créditos /
          // rechazo del modelo) explica mucho mejor que el genérico.
          await Promise.allSettled(sends);
          const accionable = sendErrors.find((e) => e.isActionable);
          throw new Error(
            accionable?.message ??
              sendErrors[0]?.message ??
              'No se pudo generar ninguna foto. Probá de nuevo.',
          );
        }
        const imageIds = okRows.map((r) => r.id);
        let preloadedImages: PreloadedImage[] = [];
        try {
          preloadedImages = await preloadGeneratedImages(
            imageIds as unknown as number[],
            userCompanyId,
          );
        } catch (preloadErr) {
          console.warn(
            '[InterfazV2] preloadGeneratedImages (fotos-producto) falló',
            preloadErr,
          );
        }
        try {
          new Audio('/images/clicksound.m4a').play();
        } catch {
          /* silent */
        }
        // ── Tanda con VARIOS productos: resultados agrupados por producto,
        // igual que el catálogo (`state.catalogo` — GeneratedImagesInline
        // renderiza un grupo con header por producto). La agrupación sale del
        // `variante_id` de cada fila (= el producto del request, uno por
        // job). Igual que el catálogo, la vista agrupada NO lleva
        // `lastPayload` (sin "Generar más" global — no habría una única
        // configuración que repetir). Ver
        // ADR-2026-08-30-fotos-producto-multi-producto-2.
        const productosDeLaTanda = selectedProducts.filter((p) =>
          jobs.some((j) => j.productoId === p.id),
        );
        if (productosDeLaTanda.length > 1) {
          const rowsPorProducto = new Map<string, string[]>();
          for (const row of okRows) {
            const k = row.variante_id || 'otros';
            const lista = rowsPorProducto.get(k) ?? [];
            lista.push(row.id);
            rowsPorProducto.set(k, lista);
          }
          // Grupos en el ORDEN de la selección; filas con `variante_id`
          // desconocido (no debería pasar) caen en un bucket final, mismo
          // criterio que el catálogo.
          const productToImageIds: Record<string, string[]> = {};
          for (const p of productosDeLaTanda) {
            const ids = rowsPorProducto.get(p.id);
            if (ids && ids.length > 0) {
              productToImageIds[p.id] = ids;
              rowsPorProducto.delete(p.id);
            }
          }
          for (const [k, ids] of rowsPorProducto) {
            if (ids.length > 0) productToImageIds[k] = ids;
          }
          const productsMeta: Record<
            string,
            { id: string; name: string; imageUrl: string }
          > = {};
          for (const p of productosDeLaTanda) {
            productsMeta[p.id] = {
              id: p.id,
              name: p.name,
              imageUrl: p.imageUrl,
            };
          }
          navigate(rutaResultados, {
            state: {
              imageIds,
              companyId: userCompanyId,
              preloadedImages,
              catalogo: {
                productToImageIds,
                productsMeta,
                // Planeadas − llegadas OK: cubre fallos HTTP, filas con
                // error y las que nunca aparecieron dentro del tope.
                failedCount: Math.max(0, jobs.length - okRows.length),
              },
            },
          });
          return;
        }
        // ── UN producto: vista plana clásica. `lastPayload` = el de la
        // PRIMERA vista, sin `user_id` ni el execution_id de esta tanda:
        // "Generar más" en la pantalla de resultados arma su propia llamada
        // (genera un id nuevo) y pide 2 imágenes de esa misma vista.
        const { user_id: _u, execution_id: _e, wait: _w, ...lastPayload } =
          primerPayload ?? {};
        void _u;
        void _e;
        void _w;
        navigate(rutaResultados, {
          state: {
            imageIds,
            companyId: userCompanyId,
            lastPayload,
            preloadedImages,
          },
        });
        return;
      }

      // ── Modo 'anuncio' (Generar anuncio) ─────────────────────────
      // Atajo similar al de 'design-auto': payload propio sin modelo /
      // pose / fondo. Manda los 3 textos (título / descripción / CTA)
      // como objetos con `incluir` + `modo` ('auto' o 'custom') + el
      // `texto` ingresado por el user cuando es custom. Si el user
      // descartó el campo con el ojo, `incluir: false`.
      //
      // Los colores efectivos = `anuncioColors` si el user editó algo;
      // si no, los primeros 4 de la paleta del brand_profile (default).
      if (isAnuncio) {
        // Builder del objeto de cada campo textual.
        const buildTextoPayload = (
          field: typeof anuncioTitulo,
        ): Record<string, unknown> => {
          if (field.hidden) return { incluir: false };
          const trimmed = field.value.trim();
          const base: Record<string, unknown> = trimmed
            ? { incluir: true, modo: 'custom', texto: trimmed }
            : { incluir: true, modo: 'auto' };
          // Fuente elegida (las mismas del editor de video). Solo se manda
          // si el user eligió una; '' = Automática ⇒ la IA decide.
          if (field.font && field.font.trim()) {
            base.fuente = field.font.trim();
          }
          return base;
        };
        const coloresEfectivos =
          anuncioColors.length > 0
            ? anuncioColors
            : brandColors.slice(0, ANUNCIO_COLORS_MAX);
        const anuncioRequest: Record<string, any> = {
          user_id: user.id,
          // El sujeto del anuncio: la creación si se entró desde una
          // (ADR-2026-08-05-el-anuncio-desde-una-creacion-usa-el-modo), y si no los
          // productos de siempre. `modo` cambia con él porque el backend rutea por ahí:
          // con `anuncio_desde_imagen` usa la imagen como referencia en vez de componer
          // el producto.
          ...(imagenBase
            ? { imagen_generada_id: imagenBase.id, modo: 'anuncio_desde_imagen' }
            : { variante_id: variantIds, modo: 'anuncio' }),
          mode,
          modo_utilizado: 'anuncio',
          // Cantidad de imágenes a generar — mismo control que el de
          // design-auto, clamped a [1, AUTO_IMAGE_COUNT_MAX].
          cantidad_imagenes: Math.max(
            1,
            Math.min(AUTO_IMAGE_COUNT_MAX, autoImageCount),
          ),
          Format: formatSlugToAspect(formatoVal),
          textos: {
            titulo: buildTextoPayload(anuncioTitulo),
            descripcion: buildTextoPayload(anuncioDescripcion),
            cta: buildTextoPayload(anuncioCta),
          },
          // Solo mandamos colores si hay al menos uno (sea custom del
          // user o default del brand). Vacío = backend decide.
          ...(coloresEfectivos.length > 0
            ? { colores: coloresEfectivos }
            : {}),
          ...(otraConfig.trim()
            ? { instruccion_adicional: otraConfig.trim() }
            : {}),
          new_ui: true,
          modo_pro: true,
          execution_id: executionId,
        };
        // Carrera respuesta-vs-poll (A) + marca de pendiente (B).
        markPendingGeneration({
          executionId,
          companyId: userCompanyId,
          expectedCount: anuncioRequest.cantidad_imagenes as number,
        });
        const anuncioResult = await awaitImageGeneration({
          fetchPromise: postGenerateAd(anuncioRequest, {
            url: GENERATE_AD_V3_URL,
          }),
          executionId,
          companyId: userCompanyId,
          expectedCount: anuncioRequest.cantidad_imagenes as number,
          // gpt-5 en high tarda minutos POR imagen: sin estas ventanas el
          // poll cerraba la tanda con la primera fila y el resto caía
          // "después" al historial (ADR-578).
          pollTimeoutMs: ANUNCIO_POLL_TIMEOUT_MS,
          pollStableRounds: ANUNCIO_STABLE_ROUNDS,
        });
        clearPendingGeneration();
        const anuncioData: any = {
          response: 'success',
          imagenes_generadas_ids: anuncioResult.imageIds,
          id_company: anuncioResult.companyId,
        };
        if (
          Array.isArray(anuncioData?.imagenes_generadas_ids) &&
          anuncioData.imagenes_generadas_ids.length > 0
        ) {
          const imageIds = anuncioData.imagenes_generadas_ids as number[];
          const companyIdResult =
            (anuncioData.id_company as string) || userCompanyId;
          let preloadedImages: PreloadedImage[] = [];
          try {
            preloadedImages = await preloadGeneratedImages(
              imageIds,
              companyIdResult,
            );
          } catch (preloadErr) {
            console.warn(
              '[InterfazV2] preloadGeneratedImages (anuncio) falló',
              preloadErr,
            );
          }
          try {
            new Audio('/images/clicksound.m4a').play();
          } catch {}
          // Persistir última config para auto-rellenar próxima
          // generación (formato + productos). Los textos / colores
          // no los persistimos — son muy específicos del anuncio.
          try {
            if (formatoVal) {
              localStorage.setItem('atlas-v2-last-format', formatoVal);
            }
            if (selectedProducts.length > 0) {
              localStorage.setItem(
                'atlas-v2-last-products',
                JSON.stringify(selectedProducts.map((p) => p.id)),
              );
            }
          } catch {
            /* noop */
          }
          const { user_id: _u, ...lastPayload } = anuncioRequest;
          void _u;
          navigate(rutaResultados, {
            state: {
              imageIds,
              companyId: companyIdResult,
              lastPayload,
              preloadedImages,
            },
          });
          return;
        }
        throw new Error('No se recibieron imágenes generadas.');
      }

      // Modelo: el shape del payload depende del mode de la selección.
      //   • 'preset'  → guardados (mandamos modelo_guardado_id/nombre).
      //   • 'custom'  → personalizado + fields explícitos.
      //   • 'auto' / 'upload' / null → ia (default).
      // 'upload' hoy cae a 'ia' porque el avatar subido recientemente
      // queda como un modelo guardado normal — el user lo eligió desde
      // el flow, ya pasó por 'preset'. Si modelSelection sigue siendo
      // 'upload' raw, significa que el user no terminó el flow.
      //
      // Multi-modelo: cuando el user activó "varios modelos" en el
      // ModelPicker, mandamos `modelo_mode: 'multiple'` + `modelo_config`
      // (array) con la config de cada slot y los ids de productos que
      // lleva. Los campos custom/guardados del nivel raíz quedan vacíos.
      let modelo_mode: 'ia' | 'guardados' | 'personalizado' | 'multiple' = 'ia';
      const customFields: Record<string, string> = {};
      const guardadosFields: Record<string, any> = {};
      let modelo_config: Record<string, any>[] | null = null;

      // Builder de un item del array modelo_config — replica la lógica
      // single-mode pero localizado al slot, e incluye `variante_id`.
      const buildModeloConfigItem = (
        sel: ModelSelection | null,
        vIds: string[],
      ): Record<string, any> => {
        const item: Record<string, any> = { variante_id: vIds };
        if (sel?.mode === 'preset') {
          item.modelo_mode = 'guardados';
          if (sel.id) {
            item.modelo_guardado_id = String(sel.id);
            if (sel.name) item.modelo_guardado_nombre = sel.name;
          }
        } else if (sel?.mode === 'custom') {
          item.modelo_mode = 'personalizado';
          const f = sel.fields;
          if (f.genero) item.genero = f.genero;
          if (f.edad) item.edad = f.edad;
          if (f.tipoCuerpo) item.tipo_cuerpo = f.tipoCuerpo;
          if (f.tonoPiel) item.tono_piel = f.tonoPiel;
          if (f.colorPelo) item.color_pelo = f.colorPelo;
          if (f.largoPelo) item.largo_pelo = f.largoPelo;
          if (f.descripcion) item.desc_modelo = f.descripcion;
        } else {
          item.modelo_mode = 'ia';
        }
        return item;
      };

      if (multiModelEnabled) {
        modelo_mode = 'multiple';
        const allSlots = [modelSelection, ...modelSelectionsExtra];
        modelo_config = allSlots.map((s, idx) => {
          // Si el slot tiene productos propios asignados, usamos esos.
          // Si no, hereda los productos globales (selectedProducts).
          const slotProducts = multiSlotProducts[idx];
          const slotVariantIds = slotProducts
            ? slotProducts.map((p) => p.id)
            : variantIds;
          return buildModeloConfigItem(s, slotVariantIds);
        });
      } else if (modelSelection?.mode === 'preset') {
        modelo_mode = 'guardados';
        // El backend espera el id como string (igual que en las pantallas
        // viejas, donde viaja por URL params). Mandarlo como number lo
        // descarta del payload silenciosamente.
        if (modelSelection.id) {
          guardadosFields.modelo_guardado_id = String(modelSelection.id);
          if (modelSelection.name) {
            guardadosFields.modelo_guardado_nombre = modelSelection.name;
          }
        }
      } else if (modelSelection?.mode === 'custom') {
        modelo_mode = 'personalizado';
        const f = modelSelection.fields;
        if (f.genero) customFields.genero = f.genero;
        if (f.edad) customFields.edad = f.edad;
        if (f.tipoCuerpo) customFields.tipo_cuerpo = f.tipoCuerpo;
        if (f.tonoPiel) customFields.tono_piel = f.tonoPiel;
        if (f.colorPelo) customFields.color_pelo = f.colorPelo;
        if (f.largoPelo) customFields.largo_pelo = f.largoPelo;
        if (f.descripcion) customFields.desc_modelo = f.descripcion;
      }

      // Pose:
      //   • 'design'        (E-commerce): el state `poseIds` ya está
      //                     pre-cargado con la pose del card que el user
      //                     tappeó (vía el useEffect de ecommercePoseMeta).
      //                     Para poses 1-persona el user puede agregar
      //                     más; para 2+ queda fija.
      //   • 'design-modelo' (nuevo): array multi-select del state `poseIds`.
      const posePayload: string[] =
        isDesignModelo || isDesign
          ? poseIds.length > 0
            ? poseIds
            : selectedPreset.id.startsWith('pose-')
              ? [selectedPreset.id.slice('pose-'.length)]
              : []
          : [];

      // Separar poses globales de las CUSTOM. Las globales viajan por id en
      // `pose: []` (V3 las busca en la tabla `poses`, como hoy). Las custom
      // viajan en `poses_custom: [{id, filename}]` → V3 baja la imagen del
      // muñeco-en-pose y se la manda a Gemini como "Pose de referencia".
      const customPoseSet = new Set(customPoses.map((p) => p.id));
      const globalPosePayload = posePayload.filter(
        (id) => !customPoseSet.has(id),
      );
      const customPosesSelected = customPoses.filter((p) =>
        poseIds.includes(p.id),
      );
      // La pose custom viaja como REFERENCIA ({id, filename}). V3 baja la
      // imagen del muñeco-en-pose desde storage (bucket `modelos`,
      // `<id_company>/poses_custom/<filename>`) y se la pasa a Gemini como
      // "Pose de referencia". El front ya no baja ni manda la b64 — lo hace n8n.
      // NUNCA mandamos una pose custom sin filename: el workflow V3 muere en
      // "sign pose custom" sin escribir fila NI devolver error (200 con body
      // vacío) y el poll espera filas que no van a llegar (ADR-134). El atlas
      // las filtra de su lado, pero acá también, para que el conteo esperado
      // del poll no cuente una pose que nadie va a generar.
      const customPosesRef = customPosesSelected
        .filter((p) => {
          if (p.filename) return true;
          console.warn(
            '[InterfazV2] pose custom sin filename, se omite de la tanda',
            p.id,
          );
          return false;
        })
        .map((p) => ({
          id: p.id,
          filename: p.filename,
        }));

      // template_id: array porque el backend soporta generar múltiples
      // plantillas en una request. Si el user activó "Seleccionar varias"
      // en preview, multiPresetIds tiene N ids; sino, cae al id único del
      // selectedPreset. Solo aplica al kind 'template' — los del row
      // "Diseña tu imagen" no son templates.
      const template_id: string[] = !isDesign && !isDesignModelo
        ? multiPresetIds.length > 0
          ? multiPresetIds
          : [selectedPreset.id]
        : [];

      // Background del modo design-modelo: 5 shapes según el FondoSelection.
      // - auto         → background_mode: 'ia'
      // - blanco       → background_mode: 'blanco' + background_id: 5
      // - biblioteca   → 'elegido' + background_id (number)
      // - guardado     → 'guardado' + background_file_name
      // - descripcion  → 'descripcion' + background_text
      const buildDesignModeloBackground = (): Record<string, any> => {
        if (!fondoSelection || fondoSelection.mode === 'auto') {
          return { background_mode: 'ia' };
        }
        if (fondoSelection.mode === 'blanco') {
          // id 5 = fondo blanco en la tabla `fondos` (mismo mapping que
          // el flow 'design' regular y que PruebaNuevaInterfaz).
          // Tono y luz viajan SIEMPRE (con su default) cuando el usuario eligió
          // fondo blanco desde esta UI: el tono es el objetivo al que el backend
          // normaliza la imagen generada y la luz es un bloque del prompt. Que el
          // default viaje explícito es lo que hace que lo que muestran los chips
          // sea exactamente lo que se genera — ADR-2026-08-14-fondo-blanco-consistente.
          return {
            background_mode: 'blanco',
            background_id: 5,
            fondo_blanco_temperatura:
              fondoSelection.temperatura ?? TEMPERATURA_BLANCO_DEFAULT,
            fondo_blanco_iluminacion:
              fondoSelection.iluminacion ?? ILUMINACION_BLANCO_DEFAULT,
          };
        }
        if (fondoSelection.mode === 'biblioteca') {
          return {
            background_mode: 'elegido',
            background_id: fondoSelection.id,
          };
        }
        if (fondoSelection.mode === 'guardado') {
          return {
            background_mode: 'guardado',
            background_file_name: fondoSelection.fileName,
          };
        }
        return {
          background_mode: 'descripcion',
          background_text: fondoSelection.text,
        };
      };

      // El modo del payload usa 'personalizado' (mismo endpoint / flow que
      // PruebaNuevaInterfaz) para design-modelo Y para design (poses
      // E-commerce / "Fondo blanco"). 'plantilla' queda solo para los
      // templates reales (kind 'template').
      const requestModo: 'plantilla' | 'personalizado' =
        isDesignModelo || isDesign ? 'personalizado' : 'plantilla';

      // Trackeo: modo de la UI que originó la generación (ADR-511/512). Este
      // flujo lo comparten Imagen personalizada, las poses E-commerce y las
      // plantillas — el payload era indistinguible entre ellos. Las poses
      // E-commerce (kind 'design') cuentan como plantilla.
      const modoUtilizado = isDesignModelo
        ? 'imagen_personalizada'
        : selectedPreset.outputType === 'video'
          ? 'plantilla_video'
          : 'plantilla';

      // Imagen personalizada: con UNA sola pose (o pose Automática) el
      // contador de cantidad pide N variaciones del MISMO prompt — el
      // backend usa `cantidad_imagenes` como tamaño de la ola 1 mientras no
      // sea multi-pose. Con varias poses / varios modelos el helper devuelve
      // 1 y el comportamiento queda igual que antes (1 imagen por pose).
      // Ver ADR-2026-07-30-imagen-personalizada-cantidad.
      const designModeloCount = isDesignModelo
        ? resolveDesignModeloCount({
            rawCount: autoImageCount,
            poseCount: poseIds.length,
            multiModelEnabled,
            max: AUTO_IMAGE_COUNT_MAX,
          }).count
        : 1;

      const requestData: Record<string, any> = {
        user_id: user.id,
        variante_id: variantIds,
        mode,
        modo: requestModo,
        modo_utilizado: modoUtilizado,
        ...(template_id.length > 0 ? { template_id } : {}),
        cantidad_imagenes: designModeloCount,
        Format: formatSlugToAspect(formatoVal),
        // Sólo 'design-modelo' tiene selector de resolución; el resto de los kinds
        // que pasan por acá (plantillas E-commerce) mandan el default 1K, que es
        // exactamente lo que generaban antes.
        image_size: isDesignModelo ? resolucionVal : RESOLUCION_DEFAULT,
        composicion: 'modelo',
        modelo_mode,
        ...customFields,
        ...guardadosFields,
        ...(modelo_config ? { modelo_config } : {}),
        ...(globalPosePayload.length > 0 ? { pose: globalPosePayload } : {}),
        ...(customPosesRef.length > 0 ? { poses_custom: customPosesRef } : {}),
        // Las plantillas de "E-commerce" (kind: 'design' = poses) siempre
        // se generan con fondo blanco — pedido del producto para tener
        // consistencia entre photoshoots. `background_id: 5` es el id del
        // fondo blanco en la tabla `fondos` (mismo mapping que usa la
        // UI cuando el user elige "Liso blanco" en otros flows).
        ...(isDesign ? { background_mode: 'blanco', background_id: 5 } : {}),
        ...(isDesignModelo ? buildDesignModeloBackground() : {}),
        ...(otraConfig.trim()
          ? { instruccion_adicional: otraConfig.trim() }
          : {}),
        new_ui: true,
        modo_pro: true,
        execution_id: executionId,
      };

      // Para templates de video usamos un endpoint distinto — el pipeline
      // genera frames previos + video, vs el flow de imagen del endpoint
      // principal. design-modelo y design siempre van al endpoint de imagen
      // vía `postGenerateAd` (helper con error classification).
      const isVideoTemplate =
        selectedPreset.kind !== 'design-modelo' &&
        selectedPreset.kind !== 'design' &&
        selectedPreset.outputType === 'video';
      let data: any;
      if (isVideoTemplate) {
        // Video desde plantilla (Kling, 3-10 min) — helper con flag atlas/n8n.
        // Lo normal con atlas es 202: el helper cierra con el poll por
        // execution_id y devuelve el id REAL de la fila del video (con tipo
        // 'video', o has_error si falló) — ya no hace falta el lookup frágil
        // por created_at que había acá (ADR-581). Errores tipados (402
        // billing / policy) los muestra el catch de handleGenerateFinal.
        const videoId = await generateVideoFromTemplate(requestData, {
          companyId: userCompanyId,
        });
        pendingVideoIdRef.current = videoId;
        pendingVideoCompanyIdRef.current = userCompanyId;
        setCompleteProgressNow(true);
        return;
      } else {
        // Camino de IMAGEN (design-modelo / design / plantilla-imagen): carrera
        // respuesta-vs-poll (A) — si el fetch se cuelga (compu dormida), el poll
        // por execution_id recupera el resultado igual — + marca de pendiente
        // (B) para reanudar tras recarga. El fan-out de poses custom se arma
        // como UNA promesa (las N llamadas) y entra a la carrera como "fetch".
        // Con el atlas-backend, poses del catálogo y poses propias viajan en UN
        // request (`pose[]` + `poses_custom[]`): con más de una pose en total
        // recompone todas sobre la MISMA imagen base, así que la tanda sale con
        // el mismo modelo y el mismo fondo. Con el fan-out, en cambio, cada
        // request genera su propia base y el modelo cambiaba entre imágenes.
        // Mismo criterio que el Catálogo IA (ADR-607); el camino n8n conserva
        // el fan-out porque su workflow sólo lee `poses_custom[0]`.
        const tandaRuteaAtlas = shouldRouteToAtlas(
          requestData,
          GENERATE_AD_V3_URL,
        );
        const fetchPromise: Promise<any> =
          customPosesRef.length > 0 && !tandaRuteaAtlas
            ? (async () => {
                // n8n V3 no itera poses custom (solo globales): una generación
                // por pose custom + una para las globales. Juntamos los ids.
                const baseNoPoses: Record<string, any> = { ...requestData };
                delete baseNoPoses.pose;
                delete baseNoPoses.poses_custom;
                const reqs: Record<string, any>[] = [];
                if (globalPosePayload.length > 0) {
                  reqs.push({ ...baseNoPoses, pose: globalPosePayload });
                }
                for (const pc of customPosesRef) {
                  reqs.push({ ...baseNoPoses, poses_custom: [pc] });
                }
                const settled = await Promise.allSettled(
                  reqs.map((r) => postGenerateAd(r, { url: GENERATE_AD_V3_URL })),
                );
                const ok = settled
                  .filter((s) => s.status === 'fulfilled')
                  .map((s) => (s as PromiseFulfilledResult<any>).value);
                if (ok.length === 0) {
                  const firstErr = settled.find(
                    (s) => s.status === 'rejected',
                  ) as PromiseRejectedResult | undefined;
                  throw (
                    firstErr?.reason ?? new Error('No se pudo generar la imagen')
                  );
                }
                const mergedIds = ok.flatMap((d) =>
                  Array.isArray(d?.imagenes_generadas_ids)
                    ? d.imagenes_generadas_ids
                    : [],
                );
                return {
                  response: 'success',
                  imagenes_generadas_ids: mergedIds,
                  id_company: ok[0]?.id_company || userCompanyId,
                };
              })()
            : postGenerateAd(requestData, { url: GENERATE_AD_V3_URL });

        // `expectedCount`: el backend escribe una fila por imagen con gaps de
        // 30-60s, así que sin esto el poll cierra la tanda por estabilidad y se
        // pierden las que faltaban. Dos casos:
        //  • Varias poses en un request (atlas): la ola 2 escribe UNA FILA POR
        //    POSE — normales y propias por igual (`poseRows.map` + `customs.map`
        //    en generateAd.ts). Con el fan-out de n8n no aplica: ahí cada
        //    request trae lo suyo y la cuenta por execution_id no es directa.
        //  • Una sola pose: son variaciones, y la cuenta es `cantidad_imagenes`.
        const totalPosesTanda =
          globalPosePayload.length + customPosesRef.length;
        const modeloExpectedCount =
          tandaRuteaAtlas && totalPosesTanda > 1
            ? totalPosesTanda
            : designModeloCount > 1
              ? designModeloCount
              : undefined;
        markPendingGeneration({
          executionId,
          companyId: userCompanyId,
          expectedCount: modeloExpectedCount,
        });
        const modeloResult = await awaitImageGeneration({
          fetchPromise,
          executionId,
          companyId: userCompanyId,
          expectedCount: modeloExpectedCount,
        });
        clearPendingGeneration();
        data = {
          response: 'success',
          imagenes_generadas_ids: modeloResult.imageIds,
          id_company: modeloResult.companyId,
        };
      }

      // El caso video-plantilla ya retornó arriba (helper con poll por
      // execution_id) — acá solo llega el camino de IMAGEN.
      if (
        Array.isArray(data?.imagenes_generadas_ids) &&
        data.imagenes_generadas_ids.length > 0
      ) {
        const imageIds = data.imagenes_generadas_ids as number[];
        const companyIdResult =
          (data.id_company as string) || userCompanyId;

        // Persistir la última config exitosa para auto-rellenar en la
        // próxima generación (formato, modelo, productos). Si algo no
        // está seteado, no lo guardamos — para que un fallback más
        // antiguo siga vigente.
        try {
          if (formatoVal) {
            localStorage.setItem('atlas-v2-last-format', formatoVal);
          }
          if (modelSelection) {
            localStorage.setItem(
              'atlas-v2-last-model',
              JSON.stringify(modelSelection),
            );
          }
          if (selectedProducts.length > 0) {
            localStorage.setItem(
              'atlas-v2-last-products',
              JSON.stringify(selectedProducts.map((p) => p.id)),
            );
          }
        } catch {
          /* localStorage puede fallar en modo privado — non-fatal */
        }

        // Preload images antes de navegar — así la pantalla destino
        // arranca con las imágenes ya en cache.
        let preloadedImages: PreloadedImage[] = [];
        try {
          preloadedImages = await preloadGeneratedImages(
            imageIds,
            companyIdResult,
          );
        } catch (preloadErr) {
          console.warn(
            '[InterfazV2] preloadGeneratedImages falló, continúo igual',
            preloadErr,
          );
        }

        // En modo video el "listo" real es cuando terminan los videos,
        // así que diferimos el sonido hasta después del video chain
        // (sino el user escucha el listo con 30-60s de espera por delante).
        const isVideoModeNow =
          previewMediaTab === 'video' && selectedPreset.kind === 'template';
        if (!isVideoModeNow) {
          try {
            new Audio('/images/clicksound.m4a').play();
          } catch {
            /* silent — algunos browsers bloquean audio fuera de gesture */
          }
        }

        // Persistir el state completo de la UI a sessionStorage para
        // que al darle "Atrás" desde la pantalla de imágenes generadas
        // (navigate(-1)) la InterfazV2 vuelva a montar con TODAS las
        // opciones del último generate ya seleccionadas (preset
        // abierto en step configure, modelo, pose, fondo, formato,
        // productos, multi mode + slots extra + productos por slot,
        // instrucción adicional, multi-preset ids). Los lookups async
        // (preset + productos) los resuelven effects dedicados cuando
        // los catálogos llegan.
        try {
          const restoreBlob = {
            presetId: selectedPreset?.id ?? null,
            modelSelection,
            poseIds,
            fondoSelection,
            formatoVal,
            otraConfig,
            selectedProductIds: selectedProducts.map((p) => p.id),
            multiModelEnabled,
            modelSelectionsExtra,
            multiSlotProductIds: Object.fromEntries(
              Object.entries(multiSlotProducts).map(([k, list]) => [
                k,
                list.map((p) => p.id),
              ]),
            ),
            multiPresetIds,
          };
          sessionStorage.setItem(
            'atlas-v2-restore',
            JSON.stringify(restoreBlob),
          );
        } catch {
          /* sessionStorage puede fallar en modo privado — non-fatal */
        }

        const { user_id: _uid, ...lastPayload } = requestData;
        void _uid;

        // ── Modo video (toggle 'video' + kind 'template') ──────────────
        // Después de tener las imágenes, encadenamos para cada una:
        //   1) Pedir el prompt sugerido al webhook generar_prompt_video.
        //   2) Disparar la generación del video (cinema multi o single).
        //   3) Esperar a que el row del video aparezca en imagenes_generadas.
        // Después navegamos con los IDs de video. Si falla todo, navegamos
        // igual con las imágenes (mejor algo que nada).
        const isVideoMode =
          previewMediaTab === 'video' && selectedPreset.kind === 'template';
        if (isVideoMode) {
          const videoRequestTime = new Date().toISOString();

          const generateOne = async (
            imageId: number,
          ): Promise<string | null> => {
            // 1) Prompt sugerido (helper con flag atlas/n8n) — best-effort.
            let prompt = '';
            try {
              prompt = await fetchSuggestedVideoPrompt(imageId, user.id);
            } catch (err) {
              console.warn('[InterfazV2] prompt de video sugerido falló', err);
            }

            // 2) Trigger video generation
            const videoPayload = {
              user_id: user.id,
              tipo_video: 'cinematografico',
              prompt,
              duracion: videoDuration,
              Format: '9:16',
              style_prompt: '',
              multiples_escenas: videoMultiScene,
              origen: 'plantilla v2',
              modo_utilizado: 'plantilla_video',
              scenes: [
                {
                  index: 0,
                  prompt,
                  start_frame: imageId,
                  end_frame: null,
                  duration_seconds: videoDuration,
                },
              ],
            };
            // Animar imagen (ambas ramas del toggle multi-escena): helper
            // con flag atlas/n8n — 202 → poll de respaldo por execution_id
            // (normal con Kling multi-shot); 402 billing. Ver ADR-582.
            let webhookVideoId: string | null = null;
            try {
              webhookVideoId = await generateVideoFromImage(videoPayload, {
                companyId: companyIdResult,
              });
            } catch (err) {
              console.warn('[InterfazV2] animar imagen (plantilla) falló', err);
              return null;
            }

            // 3) Encontrar el row real del video. El webhook de n8n devuelve
            // un UUID del workflow, no el id de imagenes_generadas. Buscamos
            // el último video del company creado después del request.
            const sleep = (ms: number) =>
              new Promise((r) => setTimeout(r, ms));
            for (let attempt = 0; attempt < 20; attempt++) {
              const { data: row } = await supabase
                .from('imagenes_generadas')
                .select('id, file_name')
                .eq('id_company', companyIdResult)
                .in('tipo', ['video', 'video_pro'])
                .gte('created_at', videoRequestTime)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();
              if (row?.id) return String(row.id);
              await sleep(2000);
            }
            // Fallback al id devuelto por el backend si no encontramos el
            // row (en atlas ES el id real; en n8n es el UUID del workflow).
            return webhookVideoId;
          };

          const videoResults = await Promise.allSettled(
            imageIds.map((id) => generateOne(id)),
          );
          const videoIds = videoResults
            .map((r) =>
              r.status === 'fulfilled' && r.value ? r.value : null,
            )
            .filter((v): v is string => !!v);

          if (videoIds.length > 0) {
            // Sonido de "listo" — recién acá, cuando los videos
            // realmente están listos para navegar.
            try {
              new Audio('/images/clicksound.m4a').play();
            } catch {
              /* silent */
            }
            navigate(rutaResultados, {
              state: {
                imageIds: videoIds,
                companyId: companyIdResult,
                lastPayload,
                // No mandamos preloadedImages porque el preload helper
                // arma signed URLs para imágenes (full/opt). Los videos
                // los carga la página destino con su propio fetch que ya
                // sabe distinguir tipo='video'.
              },
            });
            return;
          }
          // Fallback: si NINGÚN video salió, navegamos con las imágenes
          // generadas para que el user al menos las tenga.
          console.warn(
            '[InterfazV2] todos los videos fallaron, navegando solo con imágenes',
          );
        }

        navigate(rutaResultados, {
          state: {
            imageIds,
            companyId: companyIdResult,
            lastPayload,
            preloadedImages,
            autoCarrusel: wantsCarrusel,
            // Cuántas imágenes pide el carrusel automático = (cantidad
            // elegida en el stepper − 1). El inline lo manda al webhook.
            autoCarruselCount: carruselCount,
          },
        });
      } else {
        throw new Error('No se recibieron imágenes generadas.');
      }
    } catch (err) {
      console.error('[InterfazV2] handleGenerateFinal error', err);
      // La generación falló de verdad → no hay nada que reanudar.
      clearPendingGeneration();
      if (err instanceof GenerateAdError && err.errorType === 'billing') {
        // Sin créditos según el backend nuevo (HTTP 402) — mismo UX que el
        // gate local de checkGenerationGuards (modal de límite, no genError).
        setSubscriptionLimitType(
          userPlan === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached',
        );
        setMotivoPricing('sin');
        setShowSubscriptionLimitModal(true);
      } else if (err instanceof GenerateAdError && err.isActionable) {
        setGenError(err.message);
      } else {
        setGenError(
          'Se produjo un error. No se consumieron imágenes. Por favor, volvé a intentarlo.',
        );
      }
      setShowLoadingScreen(false);
      setVideoGenerating(false);
      setAnuncioGenerating(false);
      generatingLockRef.current = false;
    }
  };

  // ── Modo 'catalogo-ia' ─────────────────────────────────────────────
  // Dispara N llamadas al endpoint V3 en paralelo, una por cada
  // combinación (vista × producto) confirmada por el user en el step
  // `configure`. Todas con fondo blanco. Loading screen se mantiene
  // hasta que todas las promesas se resuelvan.
  //
  // Payload por card:
  //   • con-modelo   → composicion: 'modelo' + modelo_mode: 'ia' + pose: [vistaId]
  //   • solo-producto → composicion: 'producto' + vista_producto: vistaId
  //
  // Resultado: navegamos a `/redes-sociales/imagenes-generadas` con el
  // listado completo de imageIds + meta `catalogo` para que la pantalla
  // de imágenes generadas pueda renderizar agrupado por producto
  // (implementado en la Fase 7 — ahora solo navega con la data).
  const handleGenerateCatalog = async (cards: CatalogoCard[]) => {
    // Este handler sirve a los DOS modos de catálogo. Se extendió en vez de
    // clonarlo: el 90% (guards, execution_id, agrupación, poll, recuperación,
    // navigate) es idéntico, y dos copias divergen en el primer bugfix que se
    // aplique a una sola. Lo específico de muebles son 3 cosas puntuales.
    // El modo Infografía (ADR-813) comparte TODO el comportamiento de muebles
    // (medidas, atributos, vistas muebles, ruteo al backend); solo cambia que su
    // única vista es infografía. Por eso entra acá como muebles.
    const esModoMuebles =
      selectedPreset?.kind === 'catalogo-muebles' ||
      selectedPreset?.kind === 'infografia';
    if (generatingLockRef.current) return;
    if (cards.length === 0) return;
    if (!userCompanyId) {
      setGenError('No se encontró la empresa del usuario');
      return;
    }
    generatingLockRef.current = true;
    // Guards: catálogo IA no está en el plan gratis + chequeo de créditos.
    // Costo = 1 crédito por card (cada card = 1 imagen final), igual que el
    // pill de CatalogoCardsFullscreen (`activeCards.length`). Ver ADR-518.
    // Costo = imágenes REALES, no cards: con el stepper de "foto en contexto"
    // una card puede valer hasta 3 (la base + 2 del carrusel). Contar cards
    // subestimaría el chequeo de créditos y dejaría pasar una generación que
    // el usuario no puede pagar.
    const totalImagenesAGenerar = cards.reduce(
      (acc, c) => acc + Math.max(1, c.cantidad ?? 1),
      0,
    );
    const guardsOk = await checkGenerationGuards(
      'catalogo',
      totalImagenesAGenerar,
    );
    if (!guardsOk) {
      generatingLockRef.current = false;
      return;
    }
    setGenError(null);
    setLoadingExiting(false);
    setCompleteProgressNow(false);
    setVideoGenerating(false);
    setAnuncioGenerating(false);
    setShowLoadingScreen(true);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      // NOTA: la marca de "generación pendiente" (para reanudar tras recarga) se
      // hace MÁS ABAJO, una vez armados los grupos: desde ADR-590 cada llamada
      // lleva su propio execution_id y hay que guardar el conjunto, no uno solo.

      // Agrupamos las cards por (producto, kind de vista) y mandamos una
      // sola llamada por grupo. El backend ya soporta multi-pose
      // (`pose: [id1, id2]`) en composición 'modelo' y multi-vista
      // (`vista_producto: [id1, id2]`) en composición 'producto' siempre
      // que haya una sola variante por request — que es nuestro caso
      // (1 producto por grupo).
      //
      // Antes: N requests para un producto con N vistas. Ahora: hasta 2
      // requests por producto (1 con-modelo + 1 solo-producto).
      type CatalogoGroup = {
        productId: string;
        product: typeof cards[number]['product'];
        kind: typeof cards[number]['vista']['kind'];
        vistaIds: string[];
        // Combo de productos (principal primero) que se combinan en la
        // imagen. Viaja entero en `variante_id`, igual que multi-producto
        // del modo modelo.
        comboIds: string[];
        /** Medidas del producto (modo muebles). Iguales para todo el grupo:
         *  los grupos son por producto. */
        medidas?: Medidas | null;
        /** Id propio de ESTA llamada. Ver el comentario en el payload. */
        executionId: string;
        /** Config global de la infografía (modo muebles). */
        infografiaConfig?: {
          estilo: string;
          color: string;
          mostrarMedidas: boolean;
          mostrarAtributos: boolean;
        };
        /** Características curadas del producto para la infografía (máx 3).
         *  Iguales para todo el grupo (los grupos son por producto). */
        atributosInfografia?: string[];
        // Solo en grupos FUSIONADOS para atlas-backend: referencias de poses
        // custom que viajan en `poses_custom` junto con las normales de
        // `vistaIds` → `pose`. El backend recompone todas sobre la misma
        // imagen base (mismo modelo/fondo en la tanda). Ver ADR-576.
        posesCustomRefs?: { id: string; filename: string }[];
      };
      // Mapa id de pose custom → filename del muñeco (para armar
      // `poses_custom:[{id,filename}]` en la request). Fuente de verdad:
      // `customPoses` (viene fresco de la DB) — igual que el modo
      // personalizado, que arma la referencia desde ahí y nunca falla.
      // Fallback: el `filename` acarreado en la vista (picker → cards).
      // NUNCA mandamos filename vacío: el workflow V3 muere en
      // "sign pose custom" sin escribir fila NI devolver error (responde
      // 200 con body vacío) y el poll se queda esperando filas que no van
      // a llegar — caso Clohé, ADR-134.
      const customPoseFilenameById = new Map<string, string>();
      for (const p of customPoses) {
        if (p.filename) customPoseFilenameById.set(p.id, p.filename);
      }
      for (const card of cards) {
        if (
          card.vista.kind === 'mis-pose' &&
          card.vista.filename &&
          !customPoseFilenameById.has(card.vista.id)
        ) {
          customPoseFilenameById.set(card.vista.id, card.vista.filename);
        }
      }
      // ── Tope de llamadas en paralelo ──────────────────────────────────
      // Cada request mantiene la conexión abierta hasta que el workflow
      // termina (V3 usa `responseMode: responseNode`), así que "disparar todas
      // juntas" abre N conexiones largas simultáneas. Con 3 ya se perdía una
      // (fetch muerto sin respuesta, "Provisional headers are shown"); con un
      // catálogo de 5 productos × 3 vistas serían 15 y se perderían muchas.
      //
      // El semáforo NO serializa: deja hasta MAX en vuelo y va largando la
      // siguiente a medida que alguna termina. Un catálogo grande tarda un poco
      // más, pero llegan todas — que es lo que importa cuando cada imagen que
      // se pierde es un crédito que el usuario pagó.
      //
      // MUEBLES VA DE A 1 (curita temporal). El modo muebles NO agrupa (cada
      // vista es su propio fondo → una ejecución por vista), así que para 3
      // imágenes abre 3× más conexiones a Supabase que el catálogo, que sí
      // agrupa. Con el pool de Postgres crónicamente al ~68% del techo (90),
      // esa ráfaga tira `EAUTHTIMEOUT` en `get user`. Serializando muebles el
      // pico baja a 1 ejecución por vez y deja de caerse. Es más lento, pero
      // llegan todas. ES UN PARCHE: el fix de fondo es migrar la credencial de
      // Postgres de n8n al POOLER de Supabase (Supavisor 6543, transaction);
      // cuando esté, volvé esto a 3. Ver docs/ y el análisis del 2026-07-22.
      const MAX_LLAMADAS_EN_PARALELO = esModoMuebles ? 1 : 3;
      let llamadasEnVuelo = 0;
      const esperarTurno = async () => {
        while (llamadasEnVuelo >= MAX_LLAMADAS_EN_PARALELO) {
          await new Promise((r) => setTimeout(r, 200));
        }
      };

      // producto → ids que devolvió el carrusel (se llena más abajo, después
      // del poll; se declara acá para que lo vean los dos bloques).
      const carruselIdsPorProducto = new Map<string, string[]>();
      const groupsMap = new Map<string, CatalogoGroup>();
      for (const card of cards) {
        const comboIds =
          card.comboProducts && card.comboProducts.length > 0
            ? card.comboProducts.map((p) => p.id)
            : [card.product.id];
        // El group-key incluye el combo ORDENADO para NO mezclar en una
        // sola request combinaciones distintas — comparten `variante_id`.
        // Las poses custom (`mis-pose`) NO se agrupan entre sí acá: el
        // workflow de n8n solo procesa `poses_custom[0]`, así que cada pose
        // custom arranca como su propio grupo (incluimos el id de la vista
        // en la key). Si la tanda rutea al atlas-backend, más abajo estos
        // grupos se FUSIONAN con el con-modelo del mismo combo (ADR-576).
        // Las vistas de MUEBLES tampoco se agrupan entre sí (igual que las
        // poses custom, y por una razón parecida): las tres son
        // `solo-producto`, así que sin esto caerían en una sola request con
        // `vista_producto: [packshot, infografia, contexto]` y un único
        // `background_mode`. Y justamente cada una necesita el suyo — el
        // packshot va sobre blanco y la foto en contexto sobre un ambiente.
        const groupKey =
          card.vista.kind === 'mis-pose'
            ? `${card.product.id}::mis-pose::${card.vista.id}::${comboIds.join(',')}`
            : esModoMuebles
              ? `${card.product.id}::mueble::${card.vista.id}::${comboIds.join(',')}`
              : `${card.product.id}::${card.vista.kind}::${comboIds.join(',')}`;
        const existing = groupsMap.get(groupKey);
        if (existing) {
          existing.vistaIds.push(card.vista.id);
        } else {
          groupsMap.set(groupKey, {
            productId: card.product.id,
            product: card.product,
            kind: card.vista.kind,
            medidas: card.medidas ?? null,
            infografiaConfig: card.infografiaConfig,
            atributosInfografia: card.atributosInfografia,
            executionId: crypto.randomUUID(),
            vistaIds: [card.vista.id],
            comboIds,
          });
        }
      }
      const groups = Array.from(groupsMap.values());

      // Fondo y modelo elegidos para TODA la tanda desde los botones del
      // review. Default: blanco + IA (como venía el catálogo). Misma forma
      // de payload que design-modelo.
      const buildCatalogoBackground = (): Record<string, any> => {
        const sel = fondoSelection;
        if (!sel || sel.mode === 'blanco') {
          // Tono y luz viajan con su default aunque el user no los toque: el
          // catálogo es JUSTO donde la deriva de blanco se ve peor (una tanda
          // entera de fotos del mismo producto, una al lado de la otra en la
          // tienda). Ver ADR-2026-08-14-fondo-blanco-consistente.
          return {
            background_mode: 'blanco',
            background_id: 5,
            fondo_blanco_temperatura:
              sel?.temperatura ?? TEMPERATURA_BLANCO_DEFAULT,
            fondo_blanco_iluminacion:
              sel?.iluminacion ?? ILUMINACION_BLANCO_DEFAULT,
          };
        }
        if (sel.mode === 'auto') return { background_mode: 'ia' };
        if (sel.mode === 'biblioteca') {
          return { background_mode: 'elegido', background_id: sel.id };
        }
        if (sel.mode === 'guardado') {
          return { background_mode: 'guardado', background_file_name: sel.fileName };
        }
        return { background_mode: 'descripcion', background_text: sel.text };
      };
      const buildCatalogoModelo = (): Record<string, any> => {
        const sel = modelSelection;
        if (sel?.mode === 'preset') {
          const o: Record<string, any> = {
            modelo_mode: 'guardados',
            modelo_guardado_id: String(sel.id),
          };
          if (sel.name) o.modelo_guardado_nombre = sel.name;
          return o;
        }
        if (sel?.mode === 'custom') {
          const f = sel.fields;
          const o: Record<string, any> = { modelo_mode: 'personalizado' };
          if (f.genero) o.genero = f.genero;
          if (f.edad) o.edad = f.edad;
          if (f.tipoCuerpo) o.tipo_cuerpo = f.tipoCuerpo;
          if (f.tonoPiel) o.tono_piel = f.tonoPiel;
          if (f.colorPelo) o.color_pelo = f.colorPelo;
          if (f.largoPelo) o.largo_pelo = f.largoPelo;
          if (f.descripcion) o.desc_modelo = f.descripcion;
          return o;
        }
        return { modelo_mode: 'ia' };
      };
      const catalogoBackground = buildCatalogoBackground();
      const catalogoModelo = buildCatalogoModelo();

      // Total esperado de filas en la DB: una imagen FINAL por card. Es
      // MUTABLE a propósito: cuando un request falla a nivel HTTP le
      // descontamos sus cards — ese grupo nunca va a escribir filas, y si no
      // lo descontáramos el poll se quedaría esperándolas hasta el tope
      // (ADR-133). El poll lo lee vía getter en cada ronda.
      let expectedTotal = cards.length;

      // Errores HTTP de los requests de la tanda. El `.catch` de abajo los
      // descuenta de `expectedTotal` para no colgar el poll, pero ANTES los
      // tiraba a un console.warn: un 402 (sin créditos) o un 400 (validación)
      // no crean filas, así que el chequeo final los veía como "cero filas" y
      // mostraba "No se generó ninguna imagen del catálogo" — un mensaje mudo
      // que además no deja rastro server-side (el rechazo es previo a
      // `createExecution`). Los guardamos para que el error real llegue al
      // usuario. Ver ADR-677.
      const sendErrors: GenerateAdError[] = [];
      // Las promesas de los POST: con `wait:false` resuelven en ms, así que
      // esperarlas en la rama de fallo es barato y evita la carrera contra el
      // poll (podríamos decidir el mensaje con `sendErrors` todavía vacío).
      const sends: Promise<unknown>[] = [];

      // ¿La tanda rutea al atlas-backend? Toda la tanda comparte modo y
      // modelo_mode, así que la decisión es UNA sola. Con atlas, los grupos
      // `mis-pose` se FUSIONAN con el `con-modelo` del mismo combo en un
      // único request (`pose: [...]` + `poses_custom: [...]`): el backend
      // recompone todas las poses sobre la misma imagen base → mismo modelo
      // y mismo fondo en toda la tanda (antes, tandas mixtas podían salir
      // con modelos distintos). Con n8n queda el fan-out de siempre (su
      // workflow solo lee `poses_custom[0]`). Ver ADR-576.
      const tandaRuteaAtlas = shouldRouteToAtlas(
        { modo: 'personalizado', ...catalogoModelo },
        GENERATE_AD_V3_URL,
      );
      let sendGroups = groups;
      if (tandaRuteaAtlas) {
        const mergedByCombo = new Map<string, CatalogoGroup>();
        const resto: CatalogoGroup[] = [];
        for (const g of groups) {
          if (g.kind !== 'con-modelo' && g.kind !== 'mis-pose') {
            resto.push(g); // solo-producto: igual que siempre
            continue;
          }
          const key = `${g.productId}::${g.comboIds.join(',')}`;
          let target = mergedByCombo.get(key);
          if (!target) {
            target = {
              productId: g.productId,
              product: g.product,
              kind: 'con-modelo',
              vistaIds: [],
              comboIds: g.comboIds,
              posesCustomRefs: [],
              // Un execution_id PROPIO también para el grupo fusionado
              // (ADR-590): cada request lleva su id para que el backend no
              // mezcle sus filas con las de otros pedidos y no cobre de más.
              executionId: crypto.randomUUID(),
            };
            mergedByCombo.set(key, target);
          }
          if (g.kind === 'mis-pose') {
            // Guard de filename (ADR-134) ACÁ: el grupo sin filename se
            // descuenta y NO entra al request fusionado.
            const fn = customPoseFilenameById.get(g.vistaIds[0]);
            if (!fn) {
              console.warn(
                '[catalogo-ia] pose custom sin filename — se omite del request fusionado',
                g.vistaIds[0],
              );
              expectedTotal -= g.vistaIds.length;
              continue;
            }
            target.posesCustomRefs!.push({ id: g.vistaIds[0], filename: fn });
          } else {
            target.vistaIds.push(...g.vistaIds);
          }
        }
        sendGroups = [
          ...resto,
          // Un grupo fusionado puede quedar vacío si TODAS sus poses custom
          // venían sin filename y no había poses normales — no se manda.
          ...[...mergedByCombo.values()].filter(
            (g) =>
              g.vistaIds.length > 0 || (g.posesCustomRefs?.length ?? 0) > 0,
          ),
        ];
      }

      // Marca de "generación pendiente" para reanudar si se recarga/descarta la
      // página. Va ACÁ —después de armar sendGroups, antes de disparar— y NO
      // después del loop: muebles serializa (1 request en vuelo) y cada uno
      // mantiene la conexión abierta hasta que el workflow termina, así que el
      // loop puede tardar minutos. Si el user recarga en el medio, las llamadas
      // YA disparadas (y cobradas) se quedarían sin recuperación si la marca se
      // escribiera al final.
      //
      // Usa los ids de `sendGroups` (los grupos REALES que se mandan, ya
      // fusionados si va a atlas): un execution_id por request (ADR-590).
      // expectedCount = `cards.length` (imágenes BASE), NO el total con extras
      // del carrusel: ese corre DESPUÉS del poll con OTROS ids que no están en
      // esta marca, así que reanudar por estos execution_id solo produce las base.
      markPendingGeneration({
        executionId: sendGroups.map((g) => g.executionId),
        companyId: userCompanyId,
        expectedCount: cards.length,
      });

      // Disparamos las generaciones fire-and-forget: NO usamos sus respuestas
      // para saber cuándo terminó. Con multi-pose una respuesta puede volver
      // antes de que estén TODAS las imágenes, así que la COMPLETITUD la
      // define el poll por `execution_id`: el backend escribe una fila por
      // imagen —tanto las OK como las que fallan (`has_error`)— y esperamos a
      // que aparezcan las `expectedTotal` filas (o venza el tope) y recién
      // ahí mostramos.
      for (const g of sendGroups) {
        const isMisPose = g.kind === 'mis-pose';
        const isConModelo = g.kind === 'con-modelo';
        // Cards que cubre este grupo (para descontar del esperado si el
        // request falla): poses normales + poses custom fusionadas.
        const groupCardCount =
          g.vistaIds.length + (g.posesCustomRefs?.length ?? 0);
        // Guard mis-pose (solo camino n8n — con atlas los mis-pose ya se
        // fusionaron arriba): sin filename NO se manda el request — ese
        // payload mata el workflow V3 sin dejar rastro (ver comentario del
        // mapa) y el grupo quedaría colgando el poll. Lo descontamos como
        // fallo conocido; entra en `failedCount` y se avisa en la pantalla
        // de generadas.
        const misPoseFilename = isMisPose
          ? customPoseFilenameById.get(g.vistaIds[0])
          : undefined;
        if (isMisPose && !misPoseFilename) {
          console.warn(
            '[catalogo-ia] pose custom sin filename — grupo omitido',
            g.vistaIds[0],
          );
          expectedTotal -= g.vistaIds.length;
          continue;
        }
        // Para solo-producto, replicamos el shape de PruebaNuevaInterfaz:
        // string cuando hay una sola vista, array cuando hay varias.
        const vistaProductoPayload =
          g.vistaIds.length === 1 ? g.vistaIds[0] : g.vistaIds;
        const payload: Record<string, any> = {
          user_id: user.id,
          // Combo completo (principal + combinados). El backend los compone
          // en una sola imagen, igual que el multi-producto del modo modelo.
          variante_id: g.comboIds,
          mode: 'single',
          modo: 'personalizado',
          // Trackeo (ADR-503). Los dos modos de catálogo se distinguen acá.
          modo_utilizado: esModoMuebles ? 'catalogo-muebles' : 'catalogo',
          // Rubro de la MARCA activa — SOLO en modo muebles, a propósito.
          //
          // n8n rutea los prompts por el rubro de la EMPRESA (`get company`),
          // que en cuentas multi-marca es el dato equivocado. Con este campo,
          // `If es moda1` puede leer `body.rubro` y caer al de la empresa si no
          // viene.
          //
          // NO se manda en el Catálogo IA ni en los demás modos ADREDE: si lo
          // hiciéramos, toda cuenta multi-marca cuyo rubro de marca difiera del
          // de la empresa cambiaría de prompt de un día para el otro, sin
          // pedirlo. Acá el modo es nuevo y no hay nada que romper.
          ...(esModoMuebles && brandRubro ? { rubro: brandRubro } : {}),
          // Estas llamadas son fire-and-forget (la completitud la define el
          // poll por execution_id): `wait: false` hace que el atlas-backend
          // responda 202 al toque en vez de sostener la conexión hasta 150s.
          // Los 400/402 sincrónicos (que descuentan expectedTotal en el
          // .catch) llegan igual — la validación y el chequeo de créditos
          // corren antes de encolar. n8n ignora el campo.
          wait: false,
          cantidad_imagenes: 1,
          Format: formatSlugToAspect(formatoVal),
          new_ui: true,
          modo_pro: true,
          // Un id POR LLAMADA, no uno compartido por toda la tanda.
          //
          // Compartirlo era un bug caro: las ejecuciones paralelas de V3 buscan
          // sus filas con `get gen images ... where execution_id = X`, así que
          // cada una veía también las filas de las otras, y eso disparaba un
          // fan-out que generaba imágenes de más Y LAS COBRABA. Medido: 3
          // vistas → 12 imágenes, 9 basura, 1 crédito cada una. Con UNA sola
          // vista salía 1 imagen. Ver ADR-590.
          //
          // El poll ahora busca por el conjunto de ids (acepta array), así que
          // no se pierde la recuperación de toda la tanda con una query.
          execution_id: g.executionId,
          // `mis-pose` también es "con modelo": el muñeco define la POSE y el
          // modelo lo pone la selección del catálogo.
          composicion: isMisPose || isConModelo ? 'modelo' : 'producto',
          // En muebles el fondo se DERIVA de la vista (no hay selector global:
          // se sacó a pedido, porque cada vista ya define el suyo). Es lo que
          // manda a cada vista a su rama del árbol de prompts de V3.
          ...(esModoMuebles
            ? backgroundParaVistaMueble(g.vistaIds[0])
            : catalogoBackground),
          // Guardrail de escala (ADR-584). Va en las TRES vistas, no solo en la
          // infografía: la slide 9 del deck de Sodimac muestra que la foto en
          // contexto también se rompe por escala (un producto mal proporcionado
          // dentro de una habitación). `proporcion` es la relación normalizada,
          // que un modelo de imagen respeta mejor que los números absolutos.
          // Si el producto no tiene medidas cargadas no se manda nada, para no
          // ensuciar el payload con un objeto vacío.
          ...(medidasParaPayload(g.medidas) ?? {}),
          // Config de la infografía — SOLO en esa vista. Son dos flags que el
          // prompt de n8n usa para armar o no armar el bloque de cotas y el de
          // características. No filtran nada acá: la decisión de qué dibujar es
          // del prompt, igual que con `instruccion_adicional`.
          ...(esModoMuebles &&
          g.vistaIds[0] === VISTA_MUEBLE_INFOGRAFIA &&
          g.infografiaConfig
            ? {
                estilo_infografia: g.infografiaConfig.estilo,
                color_infografia: g.infografiaConfig.color,
                mostrar_medidas: g.infografiaConfig.mostrarMedidas,
                mostrar_atributos: g.infografiaConfig.mostrarAtributos,
                // Etiquetas que el usuario curó (máx 3). Antes se juntaban en la
                // UI y se descartaban acá: solo viajaba el flag de arriba. n8n
                // las dibuja cuando `mostrar_atributos` es true. Ver ADR-808.
                atributos_infografia: g.atributosInfografia ?? [],
              }
            : {}),
          ...(isMisPose
            ? {
                // Camino n8n: 1 sola pose custom por request (V3 lee
                // `poses_custom[0]`). SIN `pose`: el muñeco se baja por la
                // cadena de poses_custom (ADR-030/036) y se aplica como la
                // pose de la imagen.
                poses_custom: [
                  // `misPoseFilename` está garantizado acá por el guard de
                  // arriba (grupo sin filename no llega a este punto).
                  { id: g.vistaIds[0], filename: misPoseFilename },
                ],
                ...catalogoModelo,
              }
            : isConModelo
              ? {
                  // Grupo con-modelo (fusionado en atlas): poses normales en
                  // `pose` + custom en `poses_custom` — el backend escribe
                  // una fila por pose de ambas listas, recompuestas sobre la
                  // misma imagen base (ADR-576).
                  ...(g.vistaIds.length > 0 ? { pose: g.vistaIds } : {}),
                  ...(g.posesCustomRefs && g.posesCustomRefs.length > 0
                    ? { poses_custom: g.posesCustomRefs }
                    : {}),
                  ...catalogoModelo,
                }
              : { vista_producto: vistaProductoPayload }),
        };
        // SIN REINTENTO, a propósito.
        //
        // Se probó reintentar la llamada caída y se sacó: la generación NO es
        // idempotente. Si la conexión se cae DESPUÉS de que n8n recibió el
        // pedido —que es exactamente el síntoma observado, un fetch que muere
        // sin respuesta— el reintento encola una segunda generación y le cobra
        // dos veces al usuario. Perder una imagen y avisarlo es mucho menos
        // grave que cobrar de más.
        //
        // La pérdida ocasional se cubre avisando ("no se pudo generar") en vez
        // de reintentando a ciegas. Si hace falta reintentar de verdad, primero
        // hay que hacer el endpoint idempotente (una clave por llamada que n8n
        // respete). Ver ADR-590.
        // Espera un lugar libre antes de disparar (ver el semáforo arriba).
        await esperarTurno();
        llamadasEnVuelo += 1;
        // El promise va a `sends`: si la tanda termina vacía, se espera a
        // TODOS los POST para explicar el porqué con el error real (ADR-677).
        sends.push(
          postGenerateAd(payload, { url: GENERATE_AD_V3_URL })
            .catch((e) => {
              console.warn(
                '[catalogo] una llamada falló (seguimos polleando)',
                e,
              );
              // Este grupo no va a escribir sus filas: lo sacamos del total
              // esperado así el poll no lo espera hasta el tope (cards del grupo
              // = poses normales + custom fusionadas).
              expectedTotal -= groupCardCount;
              // Guardamos el motivo: si la tanda termina sin ninguna imagen, es
              // lo único que explica POR QUÉ (el backend no escribió filas).
              if (e instanceof GenerateAdError) sendErrors.push(e);
            })
            .finally(() => {
              llamadasEnVuelo -= 1;
            }),
        );
      }

      const rows = await pollGeneratedByExecutionId({
        // Todos los ids de la tanda (uno por request REAL, ya fusionados si
        // la tanda va a atlas). Antes era uno solo compartido — ver ADR-590.
        executionId: sendGroups.map((g) => g.executionId),
        companyId: userCompanyId,
        // Getter: el esperado puede BAJAR mientras el poll corre (fallos
        // HTTP de arriba). Si TODOS fallan queda en 0 y el poll corta al
        // toque en vez de colgarse 5 min.
        expectedCount: () => expectedTotal,
      });

      const okRows = rows.filter((r) => !r.has_error);
      const erroredCount = rows.length - okRows.length;
      if (okRows.length === 0) {
        // El poll pudo cortar apenas `expectedTotal` llegó a 0, con algún
        // `.catch` todavía sin correr: esperamos los POST para decidir el
        // mensaje con TODOS los errores a la vista (baratísimo con wait:false).
        await Promise.allSettled(sends);
        // Un error accionable (sin créditos / rechazo del modelo) explica la
        // tanda mucho mejor que el genérico, y le dice al usuario qué hacer.
        // Si no hay ninguno, igual preferimos el técnico concreto al mudo.
        const accionable = sendErrors.find((e) => e.isActionable);
        throw new Error(
          accionable?.message ??
            sendErrors[0]?.message ??
            (erroredCount > 0
              ? 'Todas las imágenes del catálogo fallaron. Probá de nuevo.'
              : 'No se generó ninguna imagen del catálogo.'),
        );
      }

      const allImageIds = okRows.map((r) => r.id);

      // ── Carrusel: tomas alternativas de "Foto en contexto" ──────────────
      // El stepper de la card pide N imágenes; la generación de arriba produjo
      // 1 (la base). Las N-1 restantes salen del carrusel, que parte de esa
      // imagen base y varía ángulo manteniendo mueble y ambiente.
      //
      // Se hace ACÁ y no en la pantalla de resultados porque el webhook
      // devuelve los ids de lo que generó: así las imágenes nuevas entran en el
      // mismo `imageIds` que se le pasa a la pantalla y el user las ve en la
      // misma tanda, sin tener que refrescar.
      const cardsConExtras = esModoMuebles
        ? cards.filter((c) => (c.cantidad ?? 1) > 1)
        : [];
      if (cardsConExtras.length > 0) {
        try {
          // El poll solo trae `id, variante_id, has_error`, así que no alcanza
          // para saber qué vista es cada fila. Se consulta `datos_generacion`
          // solo de estas filas, en vez de engordar el select del poll, que es
          // compartido con el Catálogo IA.
          const { data: filas } = await supabase
            .from('imagenes_generadas')
            .select('id, variante_id, datos_generacion')
            .in('id', allImageIds);

          // producto → id de su imagen de "foto en contexto"
          const imagenContextoPorProducto = new Map<string, string>();
          for (const fila of (filas ?? []) as any[]) {
            let datos = fila.datos_generacion;
            // n8n lo guarda con JSON.stringify, pero la columna puede venir ya
            // parseada según el driver — se contemplan los dos casos.
            if (typeof datos === 'string') {
              try {
                datos = JSON.parse(datos);
              } catch {
                continue;
              }
            }
            const vista = datos?.vista_producto;
            const esContexto = Array.isArray(vista)
              ? vista.includes(VISTA_MUEBLE_CONTEXTO)
              : vista === VISTA_MUEBLE_CONTEXTO;
            if (esContexto && fila.variante_id) {
              imagenContextoPorProducto.set(fila.variante_id, String(fila.id));
            }
          }

          const pedidos = cardsConExtras
            .map((card) => {
              const imageId = imagenContextoPorProducto.get(card.product.id);
              if (!imageId) return null;
              const extra = (card.cantidad ?? 1) - 1;
              const medidasPayload = medidasParaPayload(card.medidas);
              return pedirNuevosAngulos({
                userId: user.id,
                imageId,
                cantidad: extra,
                medidas: medidasPayload?.medidas ?? null,
                proporcion: medidasPayload?.proporcion ?? null,
              }).then((ids) => ({ productId: card.product.id, ids }));
            })
            .filter(Boolean) as Promise<{
            productId: string;
            ids: string[];
          }>[];

          // allSettled: si un carrusel falla, el resto (y las imágenes base)
          // siguen su curso. El usuario ve lo que sí salió.
          const resultados = await Promise.allSettled(pedidos);
          for (const r of resultados) {
            if (r.status !== 'fulfilled' || r.value.ids.length === 0) continue;
            allImageIds.push(...r.value.ids);
            carruselIdsPorProducto.set(r.value.productId, r.value.ids);
          }
        } catch (e) {
          console.warn('[catalogo-muebles] carrusel falló, seguimos', e);
        }
      }
      // Agrupación producto → imágenes desde el `variante_id` de cada fila
      // (= product.id en este flujo). Los combos quedan bajo el producto que el
      // backend haya escrito como `variante_id`.
      const productToImageIds: Record<string, string[]> = {};
      for (const row of okRows) {
        const k = row.variante_id || 'otros';
        (productToImageIds[k] ??= []).push(row.id);
      }
      // Las del carrusel van bajo el mismo producto que su imagen de origen,
      // así la pantalla de resultados las agrupa donde corresponde.
      for (const [productId, ids] of carruselIdsPorProducto) {
        (productToImageIds[productId] ??= []).push(...ids);
      }
      // Meta de productos desde las cards locales (id → name/imageUrl).
      const productsMeta: Record<
        string,
        { id: string; name: string; imageUrl: string }
      > = {};
      for (const card of cards) {
        if (!productsMeta[card.product.id]) {
          productsMeta[card.product.id] = {
            id: card.product.id,
            name: card.product.name,
            imageUrl: card.product.imageUrl,
          };
        }
      }
      // Cuántas NO salieron OK = fallidas (has_error) + fallos HTTP (ya
      // descontados de `expectedTotal`, por eso usamos `cards.length`) + las
      // que nunca aparecieron dentro del tope.
      // Cuenta IMÁGENES, no cards: con el stepper de "foto en contexto" una
      // card puede valer hasta 3. `totalImagenesAGenerar` ya lo contempla.
      const failedCount = Math.max(0, totalImagenesAGenerar - okRows.length);
      clearPendingGeneration(); // ya tenemos el resultado

      // Preload (instant display) antes de navegar.
      let preloadedImages: PreloadedImage[] = [];
      try {
        preloadedImages = await preloadGeneratedImages(
          allImageIds as unknown as number[],
          userCompanyId,
        );
      } catch (preloadErr) {
        console.warn(
          '[InterfazV2] preloadGeneratedImages (catalogo) falló',
          preloadErr,
        );
      }

      try {
        new Audio('/images/clicksound.m4a').play();
      } catch {
        /* noop */
      }

      navigate(rutaResultados, {
        state: {
          imageIds: allImageIds,
          companyId: userCompanyId,
          preloadedImages,
          // Marca de catálogo + agrupación. La pantalla de
          // GeneratedImagesInline detecta `state.catalogo` y cambia el
          // render (Fase 7).
          catalogo: {
            productToImageIds,
            productsMeta,
            failedCount,
          },
        },
      });
    } catch (err) {
      console.error('[catalogo-ia] generación falló', err);
      clearPendingGeneration();
      setShowLoadingScreen(false);
      setGenError(
        err instanceof GenerateAdError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Falló la generación del catálogo',
      );
    } finally {
      generatingLockRef.current = false;
    }
  };

  // Origen del picker de productos: 'choose' (flujo inicial, viene de la
  // pantalla de elegir producto) o 'configure' ("Añadir producto al outfit"
  // desde la configuración). Define a dónde vuelve el back/cancel del picker.
  const pickOriginRef = useRef<'choose' | 'configure'>('choose');
  // Handler para "Ver mis productos" — el grid de productos aparece
  // dentro del MISMO panel en vez de un sheet separado.
  const handleShowProducts = () => {
    pickOriginRef.current = 'choose';
    setPanelStep('pick-product');
  };
  // "Añadir producto al outfit" desde configure — abre el mismo picker pero
  // vuelve a configure al confirmar/cancelar. La selección actual va
  // premarcada (ver initialSelectedIds) para que el user SUME productos al
  // outfit en vez de reemplazarlos.
  const handleAddProducts = () => {
    pickOriginRef.current = 'configure';
    setPanelStep('pick-product');
  };
  // Multi-selección: el grid toggle-ea cada producto. El user confirma con
  // un botón cuando termina, y ahí avanzamos al configure (o a
  // 'select-vistas' si el preset es 'catalogo-ia' — ese flow tiene un
  // step intermedio para elegir las vistas que se generan).
  // ── Diálogo autogenerado del influencer (ADR-674) ────────────────────
  // Dispara al entrar a `configure` en el modo influencer CON producto. No
  // corre con "Sin producto" (ADR-673): el guion sale de la foto del producto,
  // sin foto no hay nada que generar y el textarea arranca vacío.
  //
  // El resultado PISA el textarea a propósito: es el reemplazo del viejo modo
  // "Automático". Solo se pide una vez por variante (`dialogoForRef`), así que
  // volver a esta pantalla no borra lo que el usuario haya editado; cambiar de
  // producto sí regenera, porque el guion viejo hablaba de otra cosa.
  useEffect(() => {
    if (panelStep !== 'configure') return;
    if (selectedPreset?.kind !== 'video-influencer') return;
    if (sinProducto) return;
    const varianteId = selectedProducts[0]?.id;
    if (!varianteId) return;
    if (dialogoForRef.current === varianteId) return;
    dialogoForRef.current = varianteId;

    let alive = true;
    setDialogoLoading(true);
    setDialogoError(null);
    void fetchInfluencerDialogo(varianteId)
      .then((texto) => {
        if (!alive) return;
        if (texto) {
          setVideoVoiceGuion((v) => ({ ...v, guionTexto: texto }));
        }
      })
      .catch((e) => {
        if (!alive) return;
        console.warn('[InterfazV2] no se pudo autogenerar el diálogo', e);
        setDialogoError(e instanceof Error ? e.message : 'error');
        // Permitir reintentar si el usuario vuelve a entrar a la pantalla.
        dialogoForRef.current = null;
      })
      .finally(() => {
        if (alive) setDialogoLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [panelStep, selectedPreset?.kind, sinProducto, selectedProducts]);

  const handleConfirmProducts = (products: MockProductV2[]) => {
    setSelectedProducts(products);
    setSinProducto(false);
    // Producto confirmado/cambiado → re-recomendar la pose por defecto para
    // este producto (salvo que después el user elija una a mano).
    poseAutoSelectRef.current = 'pending';
    // Modo Infografía (ADR-813): NO tiene picker de vistas —su única vista es
    // infografía—. Se presetea esa vista y va DIRECTO al review (la misma pantalla
    // del catálogo de productos, pero solo con la infografía).
    if (selectedPreset?.kind === 'infografia') {
      const infoVista = VISTAS_MUEBLES.find(
        (v) => v.id === VISTA_MUEBLE_INFOGRAFIA,
      );
      setCatalogoVistas(infoVista ? [infoVista] : []);
      setPanelStep('configure');
      return;
    }
    setPanelStep(
      esModoCatalogo(selectedPreset?.kind) ? 'select-vistas' : 'configure',
    );
  };

  /** "Sin producto" (ADR-673, solo video-influencer): saltea la elección y va
   *  directo a configurar. Fuerza el guion a "Escribir" porque el modo
   *  Automático depende del producto — es Gemini mirando la foto para decidir
   *  qué decir; sin producto no tiene de qué hablar (el backend además lo
   *  rechaza con 400). */
  const handleSkipProduct = () => {
    setSelectedProducts([]);
      // La creación base se va con el panel: si no, el próximo anuncio abierto desde un
      // producto seguiría mandando `imagen_generada_id`.
      setImagenBase(null);
    setSinProducto(true);
    setVideoVoiceGuion((v) =>
      v.guionMode === 'texto' ? v : { ...v, guionMode: 'texto' },
    );
    setPanelStep('configure');
  };
  const handleBackToChoose = () =>
    setPanelStep(
      pickOriginRef.current === 'configure' ? 'configure' : 'choose-product',
    );

  // ── Upload de producto nuevo ────────────────────────────────────────
  // Paso del GRID donde aterrizar tras subir, según viewport: en DESKTOP es
  // `choose-product` (grid fusionado, que conserva el botón "Subir nuevo
  // producto" arriba); en MOBILE el grid vive en `pick-product` (full-screen).
  // Así, tras subir, el user cae viendo sus productos tildados y —en desktop—
  // con el botón para seguir subiendo.
  const gridStepForViewport = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(min-width: 768px)').matches
      ? ('choose-product' as const)
      : ('pick-product' as const);
  // Subida en bloque para CATÁLOGO con 2+ archivos: cada archivo es un
  // PRODUCTO distinto (no variantes del mismo). Los subimos uno por uno
  // —secuencial, para no romper el conteo del nombre placeholder "Producto
  // N"; el backend los renombra con IA—, refrescamos, los dejamos todos
  // seleccionados y caemos en el grid (`pick-product`) listos para
  // Confirmar. No pasa por la pantalla "Nuevo producto" (que agruparía
  // todo en un solo producto).
  const handleBulkUploadCatalog = async (files: File[]) => {
    if (!userCompanyId) {
      setUploadError('No se encontró la empresa del usuario.');
      return;
    }
    setBulkUploadingProducts(true);
    setUploadError(null);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');
      const created: MockProductV2[] = [];
      const failures: string[] = [];
      for (const file of files) {
        try {
          const { variantId, finalName } = await uploadNewProduct({
            companyId: userCompanyId,
            userId: user.id,
            name: '', // vacío → el backend le pone nombre con IA
            files: [file],
            brandId: currentBrandId,
          });
          created.push({ id: variantId, name: finalName, imageUrl: '' });
        } catch (err) {
          console.error(
            '[InterfazV2] bulk upload — archivo falló',
            file.name,
            err,
          );
          failures.push(file.name);
        }
      }
      if (created.length === 0) {
        // Falló todo: caemos a la pantalla "Nuevo producto" con el error
        // para que el user lo vea y pueda reintentar (es el único lugar
        // del flujo que renderiza `uploadError`).
        setPendingProductFiles(files);
        setPendingProductName('');
        setUploadError('No pudimos subir los productos. Probá de nuevo.');
        setPanelStep('upload-product');
        return;
      }
      // Refrescar para tener las filas reales (con URL/thumbnail). Si alguna
      // todavía no aparece (replicación), usamos el stub que ya armamos.
      const refreshed = await fetchUserProducts(userCompanyId, currentBrandId);
      persistUserProducts(refreshed);
      const byId = new Map(refreshed.map((p) => [p.id, p]));
      const selected = created.map((c) => byId.get(c.id) || c);
      setSelectedProducts(selected);
      // Productos nuevos recién subidos → re-recomendar pose por defecto.
      poseAutoSelectRef.current = 'pending';
      if (failures.length > 0) {
        console.warn(
          `[InterfazV2] bulk upload: ${failures.length} archivo(s) fallaron`,
          failures,
        );
      }
      setPanelStep(gridStepForViewport());
    } catch (err) {
      console.error('[InterfazV2] handleBulkUploadCatalog error', err);
      setPendingProductFiles(files);
      setPendingProductName('');
      setUploadError(
        err instanceof Error
          ? err.message
          : 'No pudimos subir los productos. Probá de nuevo.',
      );
      setPanelStep('upload-product');
    } finally {
      setBulkUploadingProducts(false);
    }
  };
  // ── "Subir varios productos" (ADR-684, ajustado por ADR-688) ──────────
  // Antes era un file picker directo y SOLO en Catálogo. Ahora abre el MISMO
  // popup que Mi Marca (AgregarVariosModal: drag & drop, nombres editables,
  // agrupar por nombre de archivo, Tiendanube, tope por plan) y está en todos
  // los modos — pero vive en UNA sola pantalla: el estado vacío de
  // `upload-product` ("Nuevo producto"). Las pantallas de ELEGIR producto
  // quedan con dos opciones y nada más (ADR-688).
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const handleOpenBulkUpload = () => {
    // El grid al que caemos después hereda su "volver" de este ref. El popup
    // solo se abre desde el flujo de elegir producto, así que el origen es
    // siempre `choose`; lo fijamos explícito para que un back no termine en
    // `configure` si el ref venía sucio de una vuelta anterior.
    pickOriginRef.current = 'choose';
    setBulkModalOpen(true);
  };
  // El popup ya creó los productos (y se cierra solo). Refrescamos el catálogo
  // y caemos en el grid: los recién creados vienen ordenados por created_at
  // desc → quedan primeros, SIN tildar (el user elige cuál usar). Qué paso es
  // "el grid" depende del viewport (gridStepForViewport): en desktop es el
  // panel fusionado y en mobile la pantalla full-screen. Navegar importa sobre
  // todo en mobile: ahí la pantalla de origen son dos botones, así que sin
  // movernos no se vería nada de lo recién subido.
  const handleBulkModalSaved = async () => {
    if (!userCompanyId) return;
    const refreshed = await fetchUserProducts(userCompanyId, currentBrandId);
    persistUserProducts(refreshed);
    setPanelStep(gridStepForViewport());
  };
  // Disparado desde "Subir nuevo producto" en ChooseProductPanel /
  // PickProductPanel. Recibe los archivos del file picker.
  const handleStartUpload = (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    // Catálogo + 2 o más archivos → subida en bloque (cada uno su propio
    // producto, sin pantalla intermedia). Con 1 solo archivo —o en los
    // demás modos— sí mostramos "Nuevo producto" (para sumar ángulos del
    // mismo producto y/o ponerle nombre).
    if (esModoCatalogo(selectedPreset?.kind) && arr.length >= 2) {
      void handleBulkUploadCatalog(arr);
      return;
    }
    setPendingProductFiles(arr);
    setPendingProductName('');
    setUploadError(null);
    setPanelStep('upload-product');
  };
  // "Subir nuevo producto" desde el ChooseProductPanel: en vez de abrir el
  // file picker directo, entra al paso `upload-product` SIN archivos. Ese
  // panel, en estado vacío, muestra la pantalla explicativa (cómo subir el
  // producto: ejemplos + tips) y desde ahí se elige la foto. La pantalla es
  // full-screen en mobile (portal) y panel lateral en desktop (igual que el
  // resto del flujo de upload).
  const handleStartNewProduct = () => {
    setPendingProductFiles([]);
    setPendingProductName('');
    setUploadError(null);
    setPanelStep('upload-product');
  };
  // Agregar más imágenes desde la pantalla intermedia.
  const handleAddMoreFiles = (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    setPendingProductFiles((prev) => [...prev, ...arr]);
  };
  const handleRemovePendingFile = (idx: number) => {
    setPendingProductFiles((prev) => prev.filter((_, i) => i !== idx));
  };
  // Cancelar el upload — volver a choose-product y resetear pending state.
  const handleCancelUpload = () => {
    setPendingProductFiles([]);
    setPendingProductName('');
    setUploadError(null);
    setPanelStep('choose-product');
  };
  // Confirmar upload: subir todo a Supabase, refrescar productos, y
  // auto-seleccionar la variante creada para que el user pueda generar.
  const handleConfirmUpload = async () => {
    if (uploadingProduct) return;
    if (pendingProductFiles.length === 0) return;
    if (!userCompanyId) {
      setUploadError('No se encontró la empresa del usuario.');
      return;
    }
    setUploadingProduct(true);
    setUploadError(null);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');
      const { variantId, finalName } = await uploadNewProduct({
        companyId: userCompanyId,
        userId: user.id,
        name: pendingProductName,
        files: pendingProductFiles,
        brandId: currentBrandId,
      });
      // Refrescar la lista de productos para que el chip + futuros
      // pickers reflejen la nueva variante.
      const refreshed = await fetchUserProducts(userCompanyId, currentBrandId);
      persistUserProducts(refreshed);
      // Buscar la variante recién creada y auto-seleccionarla. Fallback:
      // armar un MockProductV2 stub si por alguna razón no aparece en el
      // refresh (carrera con replicación, etc.).
      const created = refreshed.find((p) => p.id === variantId) || {
        id: variantId,
        name: finalName,
        imageUrl: '',
      };
      setSelectedProducts([created]);
      // Producto nuevo recién subido → re-recomendar pose por defecto.
      poseAutoSelectRef.current = 'pending';
      setPendingProductFiles([]);
      setPendingProductName('');
      // Catálogo: tras subir (1 producto, con sus ángulos), volvemos al grid
      // con el producto ya tildado (desktop = grid fusionado con botón
      // "Subir"; mobile = grid full-screen), para que el user pueda sumar más
      // o Confirmar (→ elegir vistas). En los demás modos (diseño) vamos
      // directo a configurar, como antes.
      setPanelStep(
        esModoCatalogo(selectedPreset?.kind)
          ? gridStepForViewport()
          : 'configure',
      );
    } catch (err) {
      console.error('[InterfazV2] handleConfirmUpload error', err);
      setUploadError(
        err instanceof Error
          ? err.message
          : 'No pudimos subir el producto. Probá de nuevo.',
      );
    } finally {
      setUploadingProduct(false);
    }
  };

  // ── Datos para la home nueva (ADR-2026-07-31-inicio-se-funde-interfaz-v2) ──────
  //
  // `HomeMinimal` no decide nada: acá se traduce el estado de esta pantalla a lo que
  // necesita dibujar. Las tres derivaciones viven al final del componente, después
  // de todos los `useState`/`useRef` que consumen, y no junto a `displayCollections`:
  // ahí arriba `selectedPreset`, `previewList` y `homeScrollBeforeSheetRef` todavía
  // no existen y `abrirPreset` quedaría en la zona muerta del `const`.

  /**
   * Abrir un preset. Es el MISMO `presetClick` que estaba cableado en el JSX de la
   * home vieja, movido acá sin cambios de comportamiento: gating por plan primero,
   * después el guardado del scroll, y recién ahí la bifurcación entre modo con URL
   * propia (navega) y plantilla (sheet en estado).
   *
   * Lo usan la tira de modos, las plantillas de la home y —sin tocar— las filas de
   * mobile.
   */
  const abrirPreset = (collection: CollectionV2) => (p: PresetV2) => {
    // Card bloqueada → visor de ejemplos (nota del plan + "Mejorar plan"); sin
    // ejemplos, el modal clásico.
    const modeLock = getModePlanLock(p.kind, userPlan);
    if (modeLock) {
      if (!openLockedModeExamples(p, modeLock)) {
        setFeatureLock(modeLock);
      }
      return;
    }
    // Para restaurar el scroll de la home al cerrar el sheet (el contenedor pasa a
    // `hidden` y el scroll colapsa a 0).
    homeScrollBeforeSheetRef.current = getAppScrollY();
    // Modos con PANTALLA propia (Animar imagen, Carrusel, Shorts Studio): no hay
    // sheet que abrir, se va derecho a su ruta. Va ANTES de `MODE_KIND_TO_PATH`
    // porque ese mapa se deriva de `MODES` y por lo tanto también los contiene:
    // sin este corte caerían en `rutaModo(...)`, que abre el sheet de un modo que
    // no tiene panel — ver ADR-2026-08-14-modo-animar-imagen y -modo-carrusel.
    const externalRoute = p.kind ? MODE_KIND_TO_ROUTE[p.kind] : null;
    if (externalRoute) {
      navigate(externalRoute);
      return;
    }
    // Modos URL-routed: navegar; el useEffect de sync abre el sheet. Browser back
    // queda funcional sin tocar el state machine.
    const modePath = p.kind ? MODE_KIND_TO_PATH[p.kind] : null;
    if (modePath) {
      navigate(rutaModo(modePath));
      return;
    }
    // Templates / E-commerce: legacy in-state path.
    setSelectedPreset(p);
    setPreviewList(collection.presets);
    setPanelStep('preview');
    setPresetSheetAnimated(false);
  };

  /**
   * Los modos de la tira. Salen de `design-row`, o sea de la MISMA lista que pintaba
   * `CollectionRow`: respeta los feature flags, el rubro y el orden (incluido el
   * `promoteModeloFirst`). En desktop se muestran TODOS —los que el plan no incluye
   * salen con candado—; el filtrado por prueba gratis es sólo de mobile.
   */
  const modosHome = React.useMemo(() => {
    const designRow = displayCollections.find((c) => c.id === 'design-row');
    if (!designRow) return [];
    const cards = designRow.presets
      .map((p) => {
        const path = p.kind ? MODE_KIND_TO_PATH[p.kind] : null;
        // Un preset sin path de modo no es un modo (no tendría a dónde ir); se cae
        // de la tira en vez de renderizar una card muerta.
        if (!path) return null;
        return {
          path,
          titulo: p.title,
          imagen: p.poster || p.imageUrl,
          bloqueado: !!getModePlanLock(p.kind, userPlan),
          onAbrir: () => abrirPreset(designRow)(p),
        };
      })
      .filter((m): m is NonNullable<typeof m> => m !== null);
    // Anuncio tercero, la misma posición que ocupa en la sidebar del shell
    // (ADR-2026-08-03-anuncio-tercero-tira-y-sidebar). Va acá y no en `design-row`
    // para no mover las filas de mobile, que consumen la misma colección.
    return anuncioTercero(cards, (m) => m.path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayCollections, userPlan]);

  /* La lista de modos y las ediciones rápidas del sheet de "Generar" se mudaron a
     `useModosGenerar` (ADR-2026-08-04-la-barra-inferior-vive-en-el-shell). Salían de
     `design-row` —una colección que esta pantalla arma en su render— y por eso el
     sheet sólo podía existir acá; ahora se rearman desde `MODES`, el mismo catálogo
     que consumen `/modos` y el sheet de Mi marca, y el shell las monta en todas las
     pantallas. */

  /**
   * Las filas de plantillas: todas las colecciones menos `design-row`, que son los
   * modos. Es el mismo `restCollections` que alimentaba a `HomeTemplatesSection`.
   */
  const todasLasFilasPlantillas = React.useMemo(
    () =>
      // Sin plantillas para los rubros que no son Moda
      // (ADR-2026-08-03-plantillas-solo-para-moda). Se corta acá, en la fuente de las
      // filas, y no en la home: así se apagan de una vez la sección de desktop, las
      // filas de mobile y el "Ver todo", que las tres consumen esta lista. El rubro
      // es el de la MARCA activa, con fallback al de la empresa.
      // Mismo gate que `mostrarPlantillas` — `rubroMarcaActiva`, no `brandRubro`:
      // si acá no coincide con el guard de `/plantillas`, el "Ver más" de estas
      // filas rebota (ADR-2026-08-21-plantillas-rebotaban-por-dos-marcas-activas).
      !rubroVePlantillas(rubroMarcaActiva ?? userRubro)
        ? []
        : displayCollections
        .filter((c) => c.id !== 'design-row')
        .map((collection) => ({
          id: collection.id,
          titulo: collection.title,
          bajada: collection.subtitle,
          items: collection.presets.map((p) => ({
            id: p.id,
            src: p.imageUrl,
            titulo: p.title,
            onAbrir: () => abrirPreset(collection)(p),
          })),
          onVerMas: () => {
            saveHomeScroll();
            navigate(rutaPlantillasCategoria(collection.id));
          },
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayCollections, userPlan, rubroMarcaActiva, userRubro],
  );

  /**
   * De todas las colecciones, la home muestra 3 — y otras 3 en cada entrada o
   * refresh (ADR-2026-07-31-plantillas-home-rotan-tres). Hay 10 categorías activas:
   * listarlas todas hace de la home un scroll interminable, y mostrar siempre las
   * mismas esconde el resto del catálogo para siempre.
   *
   * Se rota acá y no dentro de `HomeMinimal` porque es una decisión sobre QUÉ datos
   * se muestran, no sobre cómo se dibujan: la home sigue recibiendo una lista de
   * filas y pintándolas todas.
   *
   * "Ver todo" sigue llevando a `/interfaz-v2/plantillas`, que las muestra todas —
   * es lo que hace que esconder 7 no sea esconderlas.
   */
  const filasPlantillasHome = useRotacionPlantillas(todasLasFilasPlantillas, 3);

  /**
   * Cuántas plantillas tiene el catálogo COMPLETO — lo dice el cierre de la sección
   * de plantillas de la home.
   *
   * Se cuenta acá y no en la home porque la home sólo recibe las 3 filas sorteadas:
   * desde ahí el total es invisible. Sale de la misma lista que alimenta el "Ver
   * todo", así que el número y el destino no pueden divergir (y sigue el filtro por
   * rubro: sin plantillas, la sección entera no se dibuja).
   */
  const totalPlantillas = React.useMemo(
    () => todasLasFilasPlantillas.reduce((n, f) => n + f.items.length, 0),
    [todasLasFilasPlantillas],
  );

  /**
   * Breadcrumb del sheet — ADR-2026-08-03-breadcrumbs-pantallas-de-modo.
   *
   * Se arma acá y no en `PresetDetailSheet` porque las tres cosas que decide son de
   * esta pantalla: si lo que está abierto es un MODO o el detalle de una plantilla
   * (`MODE_KIND_TO_PATH` mapea justamente los kinds de las cards sintéticas), cómo
   * se llama el paso actual, y qué significa "volver" (`closePresetSheet`, que ya
   * sabe si tiene que navegar —cuando se entró por URL— o cerrar con animación).
   *
   * `null` para plantillas y poses: ahí el header sigue con su botón "Volver".
   *
   * Sólo el primer nivel es link. El del medio es el modo, que es donde ya estás
   * parado —el paso es un momento DENTRO de esa pantalla, no otra pantalla—, así que
   * no hay a dónde llevar.
   */
  const migasModo = React.useMemo(() => {
    const kind = selectedPreset?.kind;
    const path = kind ? MODE_KIND_TO_PATH[kind] : null;
    if (!path) return undefined;

    const migas: { label: string; onClick?: () => void }[] = [
      { label: 'Inicio', onClick: closePresetSheet },
      // El nombre sale de `nombreCortoModo` —o sea de `COPY_TIRA`— y NO del
      // `title` del preset (ADR-2026-08-03-un-solo-nombre-por-modo): ésa es la
      // misma tabla que usan el carrusel de la home, la sidebar y el sheet de
      // Generar en mobile. Con `p.title` el breadcrumb decía "Video con
      // influencer" mientras el carrusel y la sidebar decían "Video".
      //
      // El modo es la RAÍZ de su propio flujo, así que su link vuelve al primer
      // paso. `setPanelStep` directo y no `handleBackToChoose`: ése respeta
      // `pickOriginRef` y puede devolverte a `configure` —correcto para su botón
      // "volver", pero acá el usuario está clickeando el nombre del modo y espera
      // el principio, no el paso del que salió.
      {
        label: nombreCortoModo(path, selectedPreset!.title),
        onClick: () => setPanelStep('choose-product'),
      },
    ];

    // Elegir producto NO es un nivel: es el estado de arranque del modo, así que
    // ahí la hoja es el modo mismo. `pick-product` es la misma pantalla en mobile
    // (grilla full-screen) y cuenta igual.
    if (panelStep === 'choose-product' || panelStep === 'pick-product') return migas;

    // Los modos de catálogo eligen vistas ANTES de configurar, así que ese paso va
    // en el camino. `infografia` se saltea el picker (usa una vista fija), por eso
    // se excluye — el mismo criterio que usa su botón de volver.
    const pasaPorVistas = esModoCatalogo(kind) && kind !== 'infografia';
    if (panelStep === 'configure' && pasaPorVistas) {
      migas.push({
        label: ETIQUETA_PASO['select-vistas'],
        onClick: () => setPanelStep('select-vistas'),
      });
    }

    const etiqueta = ETIQUETA_PASO[panelStep];
    // Sin etiqueta —un paso nuevo que nadie agregó al mapa— el breadcrumb se queda
    // en los niveles que sí sabe nombrar en vez de mostrar un nivel vacío.
    if (etiqueta) migas.push({ label: etiqueta });
    return migas;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPreset, panelStep, closePresetSheet]);

  // ── Render ────────────────────────────────────────────────────────────
  // NOTA: NO aplicar `paddingLeft: sidebarWidth` acá — `DashboardLayout`
  // ya lo hace correctamente (con check de mobile vs desktop). Si lo
  // duplicamos, en mobile el contenido se squisha al borde derecho y en
  // desktop queda asimétricamente offseteado a la derecha.
  return (
    <ProductsContext.Provider value={userProducts}>
     <ProductsLoadedContext.Provider value={userProductsLoaded}>
     <ProductEditContext.Provider value={productEditContextValue}>
     <ModelsContext.Provider value={userModels}>
     <AgentEnvContext.Provider
       value={{
         userId: userAuthId,
         companyId: userCompanyId,
         shortName,
         companyName,
         refreshModels: refreshUserModels,
       }}
     >
      {/* Overlay blanco fixed para tapar el gap gris que `DashboardLayout`
          deja entre la top-bar mobile y el padding-top del main (donde se
          ve el `bg-[#EFF0F5]` del wrapper exterior). Mismo patrón que usa
          SeleccionarProductosAnuncio. En desktop se desactiva
          (`sm:hidden`) para que la página se integre con el fondo gris
          del shell. */}
      <div
        className="fixed inset-0 bg-white pointer-events-none sm:hidden"
        aria-hidden="true"
      />
      {/* Skeleton overlay: tapa la home mientras restauramos scroll.
          Sin esto el user ve la home llenarse en fases y un salto al
          final. Se levanta cuando `isRestoringHomeScroll === false`. */}
      {isRestoringHomeScroll && (
        <div className="fixed inset-0 z-[60] bg-surface-mobile sm:bg-surface-content overflow-hidden">
          <HomeSkeleton />
        </div>
      )}
      {/* Acá vivía el panel CONTEXTO DEV, que se fue de las pantallas de la app —
          ADR-2026-08-03-fuera-panel-contexto-dev-de-las-pantallas. Con él se va la
          guarda `!dentroDelShell` que evitaba que se duplicara con el del shell. */}
      {/* Fondo de la página. Dentro del shell nuevo va TRANSPARENTE
          (ADR-2026-07-31-fondo-pagina-cede-shell): `surface-content` es el casi-blanco
          `#FDFDFE` del panel de `DashboardLayout`, y pintado acá adentro tapaba el
          gris `#F9F9F9` del shell — que es el que separa las tarjetas del lienzo y
          hace que la home se lea como bloques y no como una hoja en blanco.

          Fuera del shell (mobile) no cambia nada: el fondo lo tiene que poner esta
          pantalla, porque `DashboardLayout` no pinta el suyo abajo de `md:`. */}
      <div
        className={`relative min-h-[100dvh] md:min-h-full ${
          dentroDelShell ? '' : 'bg-surface-mobile sm:bg-surface-content'
        }`}
      >
      {/* MOBILE HERO — edge-to-edge, top-to-edge. Vive FUERA del
          contenedor centrado y usa margin-top negativo para anular el
          `mainTopPadding` que aplica `DashboardLayout` al `<main>`
          (env(safe-area-inset-top) + 3.5rem + 0.75rem). Así la
          imagen sube hasta el borde físico superior y el chrome
          flotante (back/menu pills + counter de créditos) queda por
          encima. El gradient inferior del hero fade-to-white se
          continúa visualmente con el bg de la página. */}
      {(() => {
        const designRow = displayCollections.find((c) => c.id === 'design-row');
        if (!designRow) return null;
        // Con un preset/panel abierto, la home queda DETRÁS del panel. No la
        // renderizamos: sus videos + imágenes seguirían consumiendo memoria de
        // GPU (y en la práctica se "colaban" por el panel cuando la GPU se
        // saturaba). Ver también el gate del contenedor de contenido más abajo.
        if (selectedPreset) return null;
        return (
          <div
            className="sm:hidden"
            style={{
              marginTop:
                'calc(0px - env(safe-area-inset-top, 0px) - 3.5rem - 0.75rem)',
            }}
          >
            <MobileModeHero
              cards={
                // Prueba gratis: en la home mobile mostramos una selección corta de
                // modos; el resto sigue accesible desde la página Modos, que no se
                // filtra. CUÁLES depende del rubro —
                // ADR-2026-08-20-la-home-gratis-de-hogar-muestra-automatico-y-fotos.
                //
                // La lista estaba escrita acá a mano (automático + personalizado +
                // "Crear desde cero"), una sola para todos y pensada para una marca de
                // ropa: a Hogar y Decoración le quedaban DOS de los tres —
                // `design-modelo` tiene `ocultoEnHogarDeco`— y uno de esos dos era el
                // chat.
                //
                // OJO: este filtro es APARTE del gating por plan. Abrir un modo en
                // `getModePlanLock` NO alcanza para que aparezca acá — ver la nota de
                // `kindsHomeGratis`, que es donde vive ahora la lista.
                isFreePlan
                  ? designRow.presets.filter(
                      (p) => !!p.kind && kindsGratisHome.has(p.kind),
                    )
                  : designRow.presets
              }
              isLocked={(preset) => !!getModePlanLock(preset.kind, userPlan)}
              rubro={brandRubro ?? userRubro}
              onCreate={(preset) => {
                // Gating por plan: card bloqueada → visor de ejemplos (nota
                // del plan + "Mejorar plan"); sin ejemplos, el modal clásico.
                const modeLock = getModePlanLock(preset.kind, userPlan);
                if (modeLock) {
                  if (!openLockedModeExamples(preset, modeLock)) {
                    setFeatureLock(modeLock);
                  }
                  return;
                }
                // Para restaurar el scroll de la home al cerrar el sheet.
                homeScrollBeforeSheetRef.current = getAppScrollY();
                // Modos con pantalla propia (Animar imagen, Shorts Studio):
                // no hay sheet que abrir, se va derecho a su ruta.
                const externalRoute = preset.kind
                  ? MODE_KIND_TO_ROUTE[preset.kind]
                  : null;
                if (externalRoute) {
                  navigate(externalRoute);
                  return;
                }
                // Modos URL-routed (auto/modelo/catalogo/asistente):
                // navegamos a su URL y el useEffect de sync abre el sheet.
                // Browser back queda funcional sin tocar el state machine.
                const modePath = preset.kind
                  ? MODE_KIND_TO_PATH[preset.kind]
                  : null;
                if (modePath) {
                  navigate(rutaModo(modePath));
                  return;
                }
                // Templates / E-commerce: legacy in-state path.
                setSelectedPreset(preset);
                setPreviewList(designRow.presets);
                setPanelStep('preview');
                setPresetSheetAnimated(false);
              }}
            />
          </div>
        );
      })()}
      {/* ── Barra de "sin créditos" (ADR-2026-07-30-barra-superior-creditos-home-ancho) ──
          Va DESPUÉS del hero mobile — ADR-2026-08-04-la-barra-de-creditos-no-va-arriba-del-hero.
          Era el primer hijo del root, y en mobile eso rompía dos cosas a la vez: el
          hero cancela el padding superior del `<main>` con un margen negativo que
          asume estar PRIMERO, así que con la barra delante ese margen se comía la
          barra en vez del padding —quedaba una franja blanca arriba y la barra
          cortada al medio—. Corrida abajo del hero, el margen negativo vuelve a
          hacer lo suyo y la barra se ve entera.
          Sigue fuera del `max-w-content` para ocupar el 100% del panel (edge-to-edge,
          sin la sidebar) y en flujo, no `fixed`: así no hay que compensar
          `sidebarWidth` ni pelearse con el scroll container. */}
      {mostrarTopBar && (
        // El `fixed` y el ref viven en un wrapper y no en la barra: `CreditsTopBar` es
        // una función sin `forwardRef`, y envolverla sale más barato que cambiarle la
        // firma a un componente que montan tres pantallas.
        <div
          ref={refBarraCreditos}
          className="fixed inset-x-0 top-0 z-[45] sm:static sm:z-auto"
        >
        <CreditsTopBar
          /* En PWA standalone (status bar translúcida, `viewport-fit=cover`) el
             wrapper fixed arranca DEBAJO de la hora y la batería: el padding mete
             el inset adentro de la barra —el degradé pinta el padding-box, así la
             franja del notch queda azul— y el texto baja a zona visible. El efecto
             que publica `--barra-creditos-mobile` mide el wrapper, así que el
             header se corre solo. `sm:pt-0` porque de 640px para arriba la barra
             vuelve al flujo, debajo del header del shell que ya compensa. */
          className="pt-[env(safe-area-inset-top,0px)] sm:pt-0"
          estado={estadoTopBar}
          restantes={homeBanners.freeImagesRemaining ?? undefined}
          discountPercent={homeBanners.discountPercent}
          onDismiss={descartarTopBar}
          /* En MOBILE la barra queda fija arriba de todo —
             ADR-2026-08-04-en-mobile-la-barra-de-creditos-queda-fija. Un aviso de saldo
             que se va con el scroll es un aviso que se pierde: en el teléfono la home
             es larga y a los dos gestos ya no está.
             El header mobile —donde vive la pastilla de créditos— se corre abajo de la
             barra leyendo `--barra-creditos-mobile`, que publica el efecto de más
             arriba midiéndola (ADR-2026-08-04-la-pastilla-de-creditos-va-abajo-del-cartel).
             `sm:static` la devuelve al flujo de 640px para arriba, que es donde ya
             estaba bien (y donde el shell de desktop monta la suya aparte). */
          // El CTA abre el catálogo COMO OVERLAY sobre esta pantalla en vez de
          // navegar a `/subscription` (ADR-2026-07-31-fuera-cartel-30-imagenes-gratis). Se reusa el `SubscriptionLimitModal`
          // que esta pantalla ya monta: desde ADR-2026-07-30-copy-catalogo-segun-contexto-abrio los tipos de crédito los
          // renderiza `CreditsPricingSheet`, que es exactamente el overlay que se
          // quiere. Levantar otro sheet acá duplicaría las ramas de cobro.
          onUpgrade={() => {
            // El copy sigue al estado que la barra acaba de anunciar, así el
            // catálogo no contradice al cartel que lo abrió (ADR-2026-07-31-copy-catalogo-sigue-saldo-real). Va por
            // `motivo` y NO por `type`: los dos tipos distinguen POR QUÉ se
            // acabaron los créditos, no SI quedan.
            setMotivoPricing(estadoTopBar);
            setSubscriptionLimitType('plan-limit-reached');
            setShowSubscriptionLimitModal(true);
          }}
        />
        </div>
      )}
      {/* Outer container con max-w-7xl para que las colecciones aprovechen
          el espacio horizontal en desktop. El greeting + prompt bar viven
          dentro de un sub-container más angosto centrado, para que esos
          elementos no se vean estirados en pantallas anchas. */}
      {/* `lg:px-0 lg:pt-0`: dentro del shell nuevo la sangría de 32px la pone el
          wrapper (`.shell-seccion`, ADR-2026-08-03-sangria-32-la-pone-el-shell). Este
          contenedor es NIETO del wrapper, así que el reset de padding —que alcanza
          sólo al hijo directo— nunca lo tocaba: la home terminaba 32px a la derecha
          y 32px más abajo que las secciones, que sí lo perdían. Fuera del shell
          (mobile / `DashboardLayout`) el padding sigue siendo de esta pantalla. */}
      <div
        className={`max-w-content mx-auto px-5 sm:px-8 pt-4 sm:pt-8 pb-24 md:pb-8 lg:px-0 lg:pt-0 ${
          selectedPreset ? 'hidden' : ''
        }`}
      >
        {/* ── HOME DESKTOP: el diseño de `/inicio` con la lógica de acá ──────
            ADR-2026-07-31-inicio-se-funde-interfaz-v2.

            Acá vivían el saludo chico, `CollectionRow` con los modos y
            `HomeTemplatesSection` (chips + masonry). Los reemplaza `HomeMinimal`,
            que es la misma pantalla que servía `/inicio`: saludo grande con los
            accesos de cuenta, tira de modos, creaciones recientes, plantillas y
            ediciones rápidas.

            `HomeMinimal` es PRESENTACIONAL: no decide nada. Todo lo que sigue —qué
            modos existen, el gating por plan, el guardado del scroll, abrir el sheet
            o navegar— se calcula acá y entra por props, que es exactamente la lógica
            que antes estaba cableada en el JSX de esta sección.

            `hidden sm:block`: en mobile sigue todo como estaba (`MobileModeHero`
            arriba y los `CollectionRow` de abajo), porque `HomeMinimal` asume el
            shell de desktop —de él saca el estado de cuenta del encabezado— y en
            teléfono ese contexto no existe. */}
        <div className="hidden sm:block">
          <HomeMinimal
            banners={
              /* Los mismos banners de siempre, en el lugar que les toca en el
                 layout nuevo. `bannerLateral` sigue mandando: cuando la barra
                 superior ya dice lo mismo, el upsell no se repite
                 (ADR-2026-07-31-banners-desktop-solo-upsell-cede). */
              bannerLateral ? (
                /* `mt-12` y no `mt-6`: es el mismo respiro que la tira de modos
                   deja por debajo (`section.mt-12`), así saludo → banner → tira
                   quedan a la misma distancia. Con `mt-6` el banner se leía
                   pegado al saludo y suelto del resto. */
                <HomeBannersView state={homeBanners} className="mt-12" />
              ) : null
            }
            /* Hasta que `useHomeBanners` no sepa qué cartel va, la home entera va en
               esqueleto: `kind: null` significa a la vez "ninguno" y "todavía no sé",
               así que sin esto la home se dibujaba sin cartel y lo metía después,
               empujando la tira de modos hacia abajo
               (ADR-2026-08-03-home-aparece-entera-o-skeleton). Se nota sobre todo al
               cambiar de marca, que rehace la consulta. */
            cargando={!homeBanners.resuelto}
            modos={modosHome}
            plantillas={filasPlantillasHome}
            totalPlantillas={totalPlantillas}
            onVerTodasPlantillas={() => {
              saveHomeScroll();
              navigate('/interfaz-v2/plantillas');
            }}
          />
        </div>

        {/* Banner en mobile: debajo del hero de modos. */}
        <HomeBannersView state={homeBanners} className="sm:hidden" showStatus />

        {/* ── Cuerpo de la home — SÓLO MOBILE ──────────────────────────
            ADR-2026-07-31-home-mobile-hero-nav-sheet-generar.

            Acá había una fila (`CollectionRow`) por colección, con scroll
            horizontal. Ahora mobile muestra LAS MISMAS secciones que desktop
            —creaciones recientes, plantillas por colección y ediciones rápidas—,
            que es el mismo componente con las mismas props: las grillas ya traían
            `grid-cols-2` para teléfono.

            Lo de arriba sigue siendo distinto en cada uno: desktop abre con el
            saludo y la tira de modos; mobile, con el hero a sangre y su barra
            inferior. */}
        <div className="mt-4 sm:hidden">
          <SeccionesHome
            idCompany={userCompanyId}
            // Sin filtro por marca en mobile: el filtro de "Creaciones recientes"
            // (ADR-2026-08-03-creaciones-recientes-por-marca) se apoya en el selector
            // de marca de la sidebar del shell nuevo, que acá no existe. `null` es lo
            // que la sección ya recibía antes de que ese prop existiera.
            idMarca={null}
            plantillas={filasPlantillasHome}
            totalPlantillas={totalPlantillas}
            onVerTodasPlantillas={() => {
              saveHomeScroll();
              navigate('/interfaz-v2/plantillas');
            }}
          />
        </div>
      </div>

      {/* La barra inferior y el sheet de "Generar" se mudaron al shell
          (ADR-2026-08-04-la-barra-inferior-vive-en-el-shell): son navegación de la
          app, no de esta pantalla, y montados acá existían sólo en el inicio. Lo que
          esta pantalla sigue decidiendo es cuándo se aparta — ver `barraSeAparta`. */}

      {/* ── Preset detail sheet ─────────────────────────────────── */}
      {selectedPreset && selectedPreset.kind === 'asistente' && (
        <AsistenteMarketingSheet
          animated={presetSheetAnimated}
          sidebarWidth={sidebarWidth}
          onClose={closePresetSheet}
          chat={chat}
        />
      )}
      {selectedPreset && selectedPreset.kind !== 'asistente' && (
        <PresetDetailSheet
          preset={selectedPreset}
          animated={presetSheetAnimated}
          migas={migasModo}
          step={panelStep}
          previewList={previewList}
          rubro={userRubro}
          rubroMarca={brandRubro ?? userRubro}
          lockSlotCount={lockSlotCount}
          catalogoVistas={catalogoVistas}
          onCatalogoVistasChange={setCatalogoVistas}
          onConfirmCatalogoVistas={() => setPanelStep('configure')}
          onBackFromCatalogoConfigure={() =>
            // Infografía salteó el picker de vistas → el "back" vuelve a elegir
            // productos, no a 'select-vistas' (que en ese modo no existe).
            selectedPreset?.kind === 'infografia'
              ? handleBackToChoose()
              : setPanelStep('select-vistas')
          }
          onGenerateCatalog={handleGenerateCatalog}
          brandId={currentBrandId}
          onProductsRefreshed={async () => {
            if (!userCompanyId) return;
            const refreshed = await fetchUserProducts(userCompanyId, currentBrandId);
            persistUserProducts(refreshed);
          }}
          selectedProducts={selectedProducts}
          imagenBase={imagenBase}
          modelSelection={modelSelection}
          fondoVal={fondoVal}
          formatoVal={formatoVal}
          resolucion={resolucionVal}
          otraConfig={otraConfig}
          sidebarWidth={sidebarWidth}
          onClose={closePresetSheet}
          onUtilizarMultiple={handleUtilizarMultiplePresets}
          multiPresetIds={multiPresetIds}
          onUtilizarPreset={handleUtilizarPreset}
          onShowProducts={handleShowProducts}
          onAddProducts={handleAddProducts}
          onConfirmProducts={handleConfirmProducts}
          onBackToChoose={handleBackToChoose}
          onStartUpload={handleStartUpload}
          onStartNewProduct={handleStartNewProduct}
          onSkipProduct={handleSkipProduct}
          sinProducto={sinProducto}
          dialogoLoading={dialogoLoading}
          dialogoError={dialogoError}
          onBulkUpload={handleOpenBulkUpload}
          userPlan={userPlan}
          onAddMoreFiles={handleAddMoreFiles}
          onRemovePendingFile={handleRemovePendingFile}
          onCancelUpload={handleCancelUpload}
          onConfirmUpload={handleConfirmUpload}
          pendingProductFiles={pendingProductFiles}
          pendingProductName={pendingProductName}
          onPendingNameChange={setPendingProductName}
          uploadingProduct={uploadingProduct}
          uploadError={uploadError}
          bulkUploadingProducts={bulkUploadingProducts}
          onClearProducts={() => {
            setSelectedProducts([]);
      // La creación base se va con el panel: si no, el próximo anuncio abierto desde un
      // producto seguiría mandando `imagen_generada_id`.
      setImagenBase(null);
            // Volver a la pantalla de producto limpia también "Sin producto":
            // si no, la card seguiría diciendo "Sin producto" mientras el user
            // está justamente eligiendo uno (ADR-673).
            setSinProducto(false);
            // Al volver a elegir producto, re-recomendar la pose por defecto.
            poseAutoSelectRef.current = 'pending';
            setPanelStep('choose-product');
          }}
          // Quitar UN producto (fotos-producto multi-producto, ADR-2026-08-30).
          // Sin productos restantes cae al mismo destino que onClearProducts —
          // defensivo: el panel manda el último X por onClearProducts.
          onRemoveProduct={(id) => {
            const next = selectedProducts.filter((p) => p.id !== id);
            setSelectedProducts(next);
            if (next.length === 0) {
              setImagenBase(null);
              setSinProducto(false);
              poseAutoSelectRef.current = 'pending';
              setPanelStep('choose-product');
            }
          }}
          onModelChange={setModelSelection}
          modelSelectionsExtra={modelSelectionsExtra}
          onModelChangeExtras={setModelSelectionsExtra}
          multiModelEnabled={multiModelEnabled}
          onMultiModelEnabledChange={setMultiModelEnabled}
          onOpenModelPicker={() => {
            setModelPickerInitialView('list');
            setModelPickerOpen(true);
          }}
          onOpenModelSheet={(initialView) => {
            setModelPickerInitialView(initialView);
            setModelPickerInitialEditingIdx(null);
            setModelPickerOpen(true);
          }}
          onOpenModelSheetForMultiSlot={(slotIdx, initialView) => {
            setModelPickerInitialView(initialView);
            setModelPickerInitialEditingIdx(slotIdx);
            setModelPickerOpen(true);
          }}
          multiProductsPerSlot={multiSlotProducts}
          onTapSlotProducts={(slotIdx) => {
            setProductPickerForSlot(slotIdx);
            setProductPickerOpen(true);
          }}
          onOpenFondoSheet={(initialView) => {
            setFondoPickerInitialView(initialView);
            setFondoPickerInitialCategory(null);
            setFondoPickerOpen(true);
          }}
          onFondoChange={setFondoVal}
          onFormatoChange={setFormatoVal}
          onResolucionChange={setResolucionVal}
          onOpenFormatPicker={() => setFormatPickerOpen(true)}
          onOtraChange={setOtraConfig}
          ganchoId={videoGanchoId}
          onGanchoIdChange={handleGanchoIdChange}
          voiceGuion={videoVoiceGuion}
          onVoiceGuionChange={setVideoVoiceGuion}
          onVideoDurationChange={setVideoDuration}
          onGenerate={(cost) => handleGenerateFinal({ cost })}
          onGenerateProduction={(carruselCount, cost) =>
            handleGenerateFinal({ autoCarrusel: true, carruselCount, cost })
          }
          autoImageCount={autoImageCount}
          onAutoImageCountChange={setAutoImageCount}
          autoImageCountMax={AUTO_IMAGE_COUNT_MAX}
          userModels={userModels}
          userAuthId={userAuthId}
          userCompanyId={userCompanyId}
          onModelCreated={refreshUserModels}
          allPoses={allPosesFiltered}
          poseIds={poseIds}
          onPoseIdsChange={handlePoseIdsChange}
          vistasProducto={vistasProducto}
          vistaIds={vistaIds}
          onVistaIdsChange={setVistaIds}
          vistaProductosExcluidos={vistaProductosExcluidos}
          onToggleVistaProducto={handleToggleVistaProducto}
          fotosProductoTotalImagenes={fotosProductoTotalImagenes}
          multiPoseEnabled={multiPoseEnabled}
          onOpenPosePicker={() => setPosePickerOpen(true)}
          customPoses={customPoses.filter(
            // Mismo filtro dinámico que el PosePickerSheet: con "varios
            // modelos" las propias de 2-3 personas TIENEN que estar acá —
            // el panel resuelve las poses seleccionadas contra esta lista
            // (DesignModeloPanel.selectedPoses) y con el filtro fijo a 1
            // la custom elegida no aparecía como seleccionada en la preview.
            (p) => (p.cantidadPersonas ?? 1) === expectedPeopleInPose,
          )}
          onUploadCustomPose={handleCatalogUploadPose}
          uploadingCustomPose={uploadingPose}
          onDeleteCustomPose={handleDeletePose}
          bibliotecaFondos={bibliotecaFondos}
          fondosGuardados={fondosGuardados}
          fondoSelection={fondoSelection}
          onFondoSelectionChange={setFondoSelection}
          onOpenFondoPicker={() => {
            // Resetear a 'list' (la vista raíz: en mobile el sheet lo
            // mapea a 'all' — menú de categorías estilo
            // PruebaNuevaInterfaz), sino el sheet podría reabrirse en
            // describe/upload por un onOpenSheet previo.
            setFondoPickerInitialView('list');
            setFondoPickerInitialCategory(null);
            setFondoPickerOpen(true);
          }}
          fondoView={fondoView}
          onFondoViewChange={setFondoView}
          onFondoSaved={refreshFondosGuardados}
          previewMediaTab={previewMediaTab}
          onPreviewMediaTabChange={setPreviewMediaTab}
          videoDuration={videoDuration}
          videoMultiScene={videoMultiScene}
          videoCreditCostPerImage={videoCreditCostPerImage}
          onOpenVideoConfig={() => setShowVideoConfigSheet(true)}
          anuncioTitulo={anuncioTitulo}
          onAnuncioTituloChange={setAnuncioTitulo}
          anuncioDescripcion={anuncioDescripcion}
          onAnuncioDescripcionChange={setAnuncioDescripcion}
          anuncioCta={anuncioCta}
          onAnuncioCtaChange={setAnuncioCta}
          anuncioColors={anuncioColors}
          onAnuncioColorsChange={setAnuncioColors}
          anuncioBrandColors={brandColors}
        />
      )}
      {/* Sheet/modal de configuración de video — se abre desde el botón
          "Configurar video" del ConfigurePanel cuando el toggle del
          header está en 'video' y el preset es 'template'. */}
      <VideoConfigSheet
        open={showVideoConfigSheet}
        duration={videoDuration}
        onDurationChange={setVideoDuration}
        multiScene={videoMultiScene}
        onMultiSceneChange={setVideoMultiScene}
        onClose={() => setShowVideoConfigSheet(false)}
        // El modo "Video con influencer" usa 8-18s (14 sin gancho, ADR-671) y
        // sin escenas múltiples. El resto, 4-12s con escenas múltiples. Desde
        // ADR-671 ningún caso tiene costo fijo: el influencer cobra 1 crédito
        // por segundo de video final y nada más (antes sumaba +1 por el
        // frame, ver ADR-099).
        minDuration={
          selectedPreset?.kind === 'video-influencer'
            ? VIDEO_INFLUENCER_DURATION_MIN
            : undefined
        }
        maxDuration={
          selectedPreset?.kind !== 'video-influencer'
            ? undefined
            : isSinGancho(videoGanchoId)
              ? VIDEO_INFLUENCER_DURATION_MAX_SIN_GANCHO
              : VIDEO_INFLUENCER_DURATION_MAX
        }
        showMultiScene={selectedPreset?.kind !== 'video-influencer'}
        baseCost={0}
      />
      </div>
      <ChatPanelV2 chat={chat} open={chatOpen} onClose={closeChat} />
      {/* Picker de modelo para mobile — vive a nivel root para que se vea
          encima de todo (sheets, top bars, etc.) via portal. En desktop
          el picker está inline en el ConfigurePanel, este componente no
          se renderiza en esa breakpoint (md+) gracias al check de width. */}
      <ModelPickerSheet
        open={modelPickerOpen}
        selection={modelSelection}
        onChange={setModelSelection}
        // Influencer: el modelo se elige sí o sí (sin tile "Automático").
        hideAuto={selectedPreset?.kind === 'video-influencer'}
        onClose={() => {
          setModelPickerOpen(false);
          // Resetear el slot pre-set para que la próxima apertura
          // (sin pre-config) caiga en list/multi como corresponda.
          setModelPickerInitialEditingIdx(null);
        }}
        userModels={userModels}
        userId={userAuthId}
        companyId={userCompanyId}
        onModelCreated={refreshUserModels}
        initialView={modelPickerInitialView}
        initialEditingMultiIdx={modelPickerInitialEditingIdx}
        selectionsExtra={modelSelectionsExtra}
        // Multi-modelo se ofrece en 'design-modelo' (opt-in) y en
        // 'design' (E-commerce, forzado por la pose). Plantillas
        // tradicionales (kind 'template') NO soportan varios modelos.
        onChangeExtras={
          selectedPreset?.kind === 'design-modelo' ||
          selectedPreset?.kind === 'design'
            ? setModelSelectionsExtra
            : undefined
        }
        multiEnabled={
          (selectedPreset?.kind === 'design-modelo' ||
            selectedPreset?.kind === 'design') && multiModelEnabled
        }
        // El toggle del multi solo se muestra en design-modelo. En
        // E-commerce (design) la cantidad la fija la pose y no debería
        // ser togglable — por eso pasamos undefined y `lockSlotCount`.
        onMultiEnabledChange={
          selectedPreset?.kind === 'design-modelo'
            ? setMultiModelEnabled
            : undefined
        }
        lockSlotCount={lockSlotCount}
        multiProducts={selectedProducts.map((p) => ({
          id: p.id,
          name: p.name,
          imageUrl: p.imageUrl,
        }))}
        multiProductsPerSlot={Object.fromEntries(
          Object.entries(multiSlotProducts).map(([idx, list]) => [
            idx,
            list.map((p) => ({
              id: p.id,
              name: p.name,
              imageUrl: p.imageUrl,
            })),
          ]),
        )}
        onTapSlotProducts={(slotIdx) => {
          setProductPickerForSlot(slotIdx);
          setProductPickerOpen(true);
        }}
      />
      {/* Popup "Subir varios productos" — EL MISMO de Mi Marca (ADR-684). Se
          abre desde la pantalla "Nuevo producto" (estado vacío de
          `upload-product`) en todos los modos. Vive a nivel root y portalea a
          body; el zIndex 220 lo deja por encima del PresetDetailSheet
          (z-100/101) y de la pantalla full-screen mobile (z-35), que si no lo
          taparían. */}
      <AgregarVariosModal
        open={bulkModalOpen}
        companyId={userCompanyId}
        brandId={currentBrandId}
        userPlan={userPlan ?? ''}
        zIndex={220}
        onClose={() => setBulkModalOpen(false)}
        onSaved={handleBulkModalSaved}
      />
      {/* Picker de formato — vive a nivel root vía portal. Mobile = bottom
          sheet, desktop = side panel. Se abre desde el botón Formato del
          ConfigurePanel (en cualquier breakpoint). */}
      {/* Picker de productos para un slot específico del modo
          varios-modelos. Mismas opciones (Choose / Pick / Upload) que
          el flow principal, pero la confirmación va al slot, no al
          state global `selectedProducts`. */}
      <MultiSlotProductPickerSheet
        open={productPickerOpen}
        onClose={() => {
          setProductPickerOpen(false);
          setProductPickerForSlot(null);
        }}
        currentSelection={
          productPickerForSlot !== null
            ? multiSlotProducts[productPickerForSlot] ?? selectedProducts
            : []
        }
        onConfirm={(products) => {
          if (productPickerForSlot !== null) {
            setMultiSlotProducts((prev) => ({
              ...prev,
              [productPickerForSlot]: products,
            }));
          }
          setProductPickerOpen(false);
          setProductPickerForSlot(null);
        }}
        userCompanyId={userCompanyId}
        brandId={currentBrandId}
        rubro={userRubro}
        userPlan={userPlan}
        tiposProductoSugeridos={
          selectedPreset?.tiposProductoSugeridos ?? null
        }
        onProductsRefreshed={async () => {
          if (!userCompanyId) return;
          const refreshed = await fetchUserProducts(userCompanyId, currentBrandId);
          persistUserProducts(refreshed);
        }}
      />
      <FormatPicker
        open={formatPickerOpen}
        selectedId={formatoVal}
        onChange={setFormatoVal}
        onClose={() => setFormatPickerOpen(false)}
      />
      {/* Pickers del modo 'design-modelo' — siempre montados (portal a
          body). Cubren el caso mobile y el "Ver más" desde el desktop. */}
      <PosePickerSheet
        singleSelect={expectedPeopleInPose > 1}
        open={posePickerOpen}
        poses={allPosesFiltered}
        selectedIds={poseIds}
        onChange={handlePoseIdsChange}
        multiPoseEnabled={multiPoseEnabled}
        onMultiPoseEnabledChange={handleMultiPoseEnabledChange}
        onClose={() => setPosePickerOpen(false)}
        customPoses={customPoses.filter(
          (p) => p.cantidadPersonas === expectedPeopleInPose,
        )}
        onUploadPose={handleUploadPose}
        onDeletePose={handleDeletePose}
        uploading={uploadingPose}
      />
      <FondoPickerSheet
        open={fondoPickerOpen}
        selection={fondoSelection}
        onChange={setFondoSelection}
        onClose={() => setFondoPickerOpen(false)}
        biblioteca={bibliotecaFondos}
        guardados={fondosGuardados}
        companyId={userCompanyId}
        onFondoSaved={refreshFondosGuardados}
        initialView={fondoPickerInitialView}
        initialCategory={fondoPickerInitialCategory}
        // Catálogo IA (el de ropa) y SOLO ese: la segunda acción del picker es
        // "Agregar fondo" (subir uno propio) en vez de describir el fondo. NO
        // se usa `esModoCatalogo`, que además de este incluye muebles e
        // infografía — esos dos ni siquiera tienen selector global de fondo
        // (cada vista deriva el suyo).
        accionSecundaria={
          selectedPreset?.kind === 'catalogo-ia' ? 'anadir' : 'describir'
        }
        subtitulo={
          selectedPreset?.kind === 'catalogo-ia'
            ? 'Elegí el fondo o escenario para todas las imágenes del catálogo.'
            : undefined
        }
        // Los ajustes de tono/luz viven DENTRO del picker en el catálogo: es el
        // único lugar donde se define el fondo de toda la tanda. En Imagen
        // personalizada no van acá — ahí ya están en el panel de configuración,
        // y duplicarlos sería el mismo estado en dos controles de la pantalla.
        mostrarAjustesBlanco={selectedPreset?.kind === 'catalogo-ia'}
        selectedProductNames={selectedProducts.map((p) => p.name)}
        rubro={userRubro}
      />
      {/* Restore overlay — bloque blanco que cubre el "home" del V2
          mientras el async restore del preset (post-back desde Imágenes
          Generadas) abre el sheet. Se levanta cuando el sheet ya está
          completamente animado. */}
      {restoringPreset && (
        <div
          className="fixed inset-0 z-[209] bg-white pointer-events-none"
          aria-hidden
        />
      )}
      {/* Loading overlay full-screen mientras se genera. Mismo componente
          que usa PruebaNuevaInterfaz para que la experiencia sea idéntica. */}
      {showLoadingScreen && (
        <LoadingAnimation
          key={videoGenerating ? 'video' : 'image'}
          message={videoGenerating ? 'Generando video...' : 'Generando imágenes...'}
          // El influencer declara su demora REAL (10-20 min): "unos minutos"
          // prometía de menos justo en el modo más largo y la espera se leía
          // como colgada. El resto de los videos sigue con el genérico — no
          // tienen una medición propia que declarar.
          subMessage={
            videoGenerating && selectedPreset?.kind === 'video-influencer'
              ? 'La generación puede tardar entre 10 y 20 minutos'
              : videoGenerating || multiModelEnabled
                ? 'Esto puede tardar unos minutos'
                : 'Esto puede tardar unos segundos'
          }
          durationMs={
            videoGenerating
              ? // El modo influencer tarda bastante más que la animación de
                // plantilla-video: barra paceada a ~11 min (ADR-539).
                selectedPreset?.kind === 'video-influencer'
                ? VIDEO_INFLUENCER_LOADING_BAR_MS
                : 360_000
              : anuncioGenerating
                ? 210_000
                : 80_000
          }
          completeNow={completeProgressNow}
          onCompleted={() => {
            setShowLoadingScreen(false);
            setVideoGenerating(false);
            setAnuncioGenerating(false);
            setCompleteProgressNow(false);
            generatingLockRef.current = false;
            const vid = pendingVideoIdRef.current;
            const vcId = pendingVideoCompanyIdRef.current;
            pendingVideoIdRef.current = null;
            pendingVideoCompanyIdRef.current = null;
            if (vid) {
              console.log('[InterfazV2] onCompleted — navigating to results, videoId:', vid, 'companyId:', vcId || userCompanyId);
              navigate(rutaResultados, {
                state: { imageIds: [vid], companyId: vcId || userCompanyId },
              });
            }
          }}
          exiting={loadingExiting}
          onExitComplete={() => setShowLoadingScreen(false)}
        />
      )}
      {/* Error de generación — toast fijo. `genError` se seteaba en el catch
          pero NUNCA se renderizaba: la pantalla de carga desaparecía sin
          feedback (bug reportado por el usuario, ADR-541). Colores en hex
          arbitrario: las clases brand/error con opacidad no compilan (ADR-531). */}
      {genError && (
        <div className="fixed inset-x-4 bottom-4 md:inset-x-auto md:right-6 md:bottom-6 md:max-w-[420px] z-[300]">
          <div className="flex items-start gap-3 rounded-2xl bg-white shadow-2xl ring-1 ring-black/[0.08] p-4">
            <div className="w-8 h-8 rounded-full bg-[#DC2626]/10 flex items-center justify-center shrink-0">
              <AlertCircle className="w-4 h-4 text-[#DC2626]" />
            </div>
            {/* min-w-0 + break-words: un token largo sin espacios (ej. un JSON
                de error) definía el ancho mínimo del flex item, estiraba la
                card más allá del viewport y dejaba la X de cerrar fuera de
                pantalla — el toast quedaba imposible de cerrar. */}
            <p className="flex-1 min-w-0 break-words text-[13.5px] text-brand-black leading-snug pt-1">
              {genError}
            </p>
            <button
              type="button"
              onClick={() => setGenError(null)}
              className="w-8 h-8 inline-flex items-center justify-center rounded-full text-[#3C3C43]/40 hover:bg-black/[0.06] transition-colors shrink-0"
              aria-label="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {/* Créditos agotados — mismo modal que el resto de la app. */}
      <SubscriptionLimitModal
        isOpen={showSubscriptionLimitModal}
        onClose={() => setShowSubscriptionLimitModal(false)}
        type={subscriptionLimitType}
        motivo={motivoPricing}
      />
      {/* Función bloqueada por plan (catálogo / agente). */}
      <FeatureLockedModal
        open={featureLock !== null}
        onClose={() => setFeatureLock(null)}
        title={featureLock?.title ?? ''}
        message={featureLock?.message ?? ''}
      />
      {/* Visor de ejemplos al tocar un modo bloqueado (nota del plan + CTA
          "Mejorar plan"). Mismo visor que la pantalla Modos. */}
      <ModeExamplesModal
        open={lockedModeExamples !== null}
        onClose={() => setLockedModeExamples(null)}
        title={lockedModeExamples?.title ?? ''}
        description={lockedModeExamples?.description}
        examples={lockedModeExamples?.examples ?? []}
        actionLabel="Mejorar plan"
        onAction={() => navigate('/subscription')}
        locked
        planNote={lockedModeExamples?.planNote}
      />
      {/* Video bloqueado por plan — mismo modal que el flujo de video. */}
      <VideoPlanRestrictionModal
        open={showVideoPlanRestriction}
        onClose={() => setShowVideoPlanRestriction(false)}
        userPlan={userPlan}
      />

      {/* Prompt "Instalar Atlas como app" / "Abrir en el navegador" — mismo
          componente y comportamiento que la pantalla clásica de seleccionar
          productos. Sólo en la home V2 (sin preset/panel abierto): así no
          interrumpe mientras el user está configurando un anuncio. El
          componente se auto-gatea por dispositivo, si ya está instalado
          (standalone) y por las reglas de descarte persistidas, y se portaléa
          a document.body por su cuenta. */}
      {!selectedPreset && (
        <AddToHomescreenPrompt
          appName="Atlas"
          appIconUrl="/favicon.png"
          delayMs={2000}
          origen="home"
        />
      )}
     </AgentEnvContext.Provider>
     </ModelsContext.Provider>
     </ProductEditContext.Provider>
     </ProductsLoadedContext.Provider>
    </ProductsContext.Provider>
  );
};

export default InterfazV2;



